/**
 * useProjectPersistence — the PROJECT PERSISTENCE seam (save / load / reset).
 *
 * TS-060 T5 slice 7 (DEC-COUNCIL-015) — the FINAL App.jsx-split slice. The
 * six bodies moved VERBATIM from App.jsx v5.46.76 base 72848712 (pre-move
 * tree lines in parentheses): `resetForm` (5266-5417), the save-queue
 * machinery `mergeProjectSaveOverrides` / `queueProjectSave` /
 * `settleQueuedProjectSaves` / `scheduleQueuedProjectSaveDrain` /
 * `isProjectWorthSaving` / `_crmShouldLog` / `promptDuplicateProjectResolution`
 * (5434-5543), `saveCurrentProjectImpl` (5546-6613), `loadProject`
 * (6629-6702), `loadProjectUnchecked` (6760-6836) and `completeProjectLoad`
 * (6839-7652). THIS SEAM CARRIES THE v5.46.72-74 CROSS-PROJECT MEDIA-BLEED
 * GUARD FAMILY (photo/video blob ownership, load-generation token, owner-
 * gated repaints, stale-save repaint guard) — every guard moved byte-exact
 * and is pinned by src/__tests__/use-project-persistence.test.js and the
 * repointed photo-load-stale-guard.test.js.
 *
 * The hook is 100% STATELESS: zero useState/useRef/useEffect/useCallback/
 * useMemo. EVERY ref the bodies touch stays App-owned and threads in via
 * deps. Refs that were DECLARED inside or below the moved region had their
 * `useRef` declarations RELOCATED VERBATIM within App.jsx to just above the
 * instantiation (a pure within-App move): the five queue refs
 * (pendingSaveRef / pendingSaveOverridesRef / pendingSaveDeferredsRef /
 * isSavingProjectRef / saveDrainTimerRef), _crmLogDedupeRef, batchActiveRef,
 * pendingOpenQuoteRef and autoOpenLoadedQuoteProjectRef. App KEEPS the
 * render-time `saveCurrentProjectRef.current = saveCurrentProjectImpl;`
 * assignment immediately after the instantiation destructure.
 *
 * Identity contract: all returned closures are re-created every render (NO
 * memoisation) exactly like the originals; cross-references BETWEEN the
 * moved functions (impl → queue helpers, loadProject → loadProjectUnchecked
 * → completeProjectLoad, drain → saveCurrentProjectRef.current) resolve
 * hook-locally with the same same-render closure semantics they had as
 * App-local siblings. `scheduleQueuedProjectSaveDrain` deliberately calls
 * through `saveCurrentProjectRef.current` (NOT the local impl) and
 * loadProject's Save-&-switch calls the threaded STABLE WRAPPER
 * `saveCurrentProject` — both preserved verbatim per call-site.
 *
 * All App deps are declared ABOVE the instantiation point in App.jsx (the
 * spot where resetForm previously sat), so every dep threads DIRECTLY — this
 * slice needs no lazy wrappers.
 *
 * Receiver preservation: PipelineMonitor.setProjectId,
 * SyncFolderService.isConfigured/syncProject/recoverMissingFiles/getFolderName,
 * DataService.companies/crmContacts/properties/propertyContacts.*,
 * ActivityLogService.log, AssessorNamesService.getLastUsed, editor.reset/peek,
 * db.projects.get (dynamic import), BusinessPricingService.getDataReadOnly (dynamic)
 * and every platform global (crypto/URL/sessionStorage/console/Math/Date/
 * Promise/Number/Array/Object) keep their property-access callee form —
 * no detached aliases introduced.
 *
 * Annotation-only deltas from the raw App.jsx bytes (for the byte-diff
 * proof): (1) the nine moved function signatures gained parameter/return
 * type annotations; (2) 12 dynamic `import('./…')` specifiers became
 * `import('../…')` (the hook lives one directory deeper); (3) 17 caught-
 * error member reads gained the house `(x as { message?: string })?.message`
 * casts (incl. one `status` read); (4) body-internal arrow/callback params
 * gained `: any` (or structural) types where strict-mode inference needed
 * them, and three object-literal declarations (projectRecord / newCompany /
 * newProperty) gained `: LooseRecord`; (5) four dynamic-import call sites
 * gained parenthesized `as any` casts (db ×2, data-service ×1,
 * pricing-match-applier ×1 — the modules' JSDoc types would otherwise force
 * body edits); (6) the `_crmLogDedupeRef` useRef declaration line was
 * REMOVED here (relocated to App.jsx). No other bytes changed — resetForm
 * moved byte-identical.
 */

import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { RETIRED_STAGE_IDS } from '../config/brand';
import type { PendingProjectOpen } from './use-app-routing';
import {
    confirmedProjectMediaRetirementReceipts,
    type ProjectMediaRetirementReceipt,
} from '../services/project-media-retirement';
import {
    mergeProjectSaveResultForDexieOwned,
    stripProjectMediaRetirementTransientFields,
} from '../services/project-media-cache-merge';
import { seenPhotoIdsForLoadedProject as _seenPhotoIdsForLoadedProject } from '../utils/photo-reopen-seed';
import { ACTIONS as PROC_ACTIONS } from '../pages/stages/stage1/processingReducer';
import { PipelineMonitor } from '../services/pipeline-monitor';
import { PROJECT_WORKFLOW_MODES, normalizeProjectWorkflowMode } from '../services/project-workflow';
import { createAssistantDraftSessionKey, reportAssistantDraftBecameProject } from '../utils/assistant-draft-session';
import { saveProject as _saveProject, getProject as _getProject } from '../services/database';
import { broadcastProjectsChanged } from '../services/project-changes-channel';
import {
    createServerUnavailableError,
    ensureBackendAnswering,
    isBackendReachable,
    isServerUnavailableError,
} from '../services/network-resilience';
import {
    NEW_PROJECT_SLOT,
    clearUnsavedProjectEdits,
    hasKeptUnsavedEdits,
    keepUnsavedProjectEdits,
    noteRestoredUnsavedEdits,
    overlayKeptProjectEdits,
    readUnsavedProjectEdits,
    releaseNewProjectSlot,
} from '../services/unsaved-project-edits';
import { pairQuoteRowsForSave } from '../utils/stage4-stage5-mirror';
import {
    createProjectHydrationFingerprintReceipt,
    rebaseProjectFingerprintWithSaveOverrides,
    serverProjectContentSignature,
    type ProjectHydrationFingerprintReceipt,
} from '../utils/project-fingerprint';
import { buildDuplicateMatchKey as _buildDuplicateMatchKey, findDuplicateProjectCandidates as _findDuplicateProjectCandidates, rememberDuplicateSaveDecision as _rememberDuplicateSaveDecision, recallDuplicateSaveDecision as _recallDuplicateSaveDecision } from '../services/project-duplicate-guard';
import { SyncFolderService as _SyncFolderService } from '../services/sync-folder';
import {
    migrateProjectPhotos as _migrateProjectPhotos, storePhotoBlob as _storePhotoBlob,
    buildPhotoCatalog as _buildPhotoCatalog, findCatalogPhoto as _findCatalogPhoto,
    makePhotoArrayPortableForPersistence as _makePhotoArrayPortableForPersistence,
    makeQuoteDataPortableForPersistence as _makeQuoteDataPortableForPersistence,
    makeScopeImagesPortableForPersistence as _makeScopeImagesPortableForPersistence,
    mergePortablePhotoFields as _mergePortablePhotoFields,
} from '../services/photo.js';
import {
    loadCanonicalBackendProjectForBatch as _loadCanonicalBackendProjectForBatch,
} from '../services/data-service';
import { AssessorNamesService } from '../services/assessor-names';
import { devLog } from '../utils/dev-log';
import { afterNextPaintOrTimeout } from '../utils/after-paint';
import { tabProjectWriteLock } from '../utils/project-write-lock';
import { ActivityLogService as _ActivityLogService } from '../services/activity-log';
import { ensureItemIds } from '../utils/item-id';
import { syncScopeFromActivities } from '../utils/scope-activity-sync';
import { bindWmsToScopeProjection } from '../utils/stage3-work-method';
import { titleSourceKey } from '../utils/initials';
import { adoptCanonicalQuoteTitle, isContactNameQuoteTitle, isImportedFolderTitle, isJobDescriptionQuoteTitle } from '../utils/quote-title';
import { dedupeDocsByIdentity } from '../utils/merge-docs-by-identity';
import { makeDocumentArrayPortableForPersistence as _makeDocumentArrayPortableForPersistence, documentNeedsBackendUpload as _documentNeedsBackendUpload, normalizeDocumentsForProjectLoad as _normalizeDocumentsForProjectLoad } from '../services/document-persistence';
import {
    migrateProjectDocuments as _migrateProjectDocuments,
    quarantineInvalidDocumentBlobReferences as _quarantineInvalidDocumentBlobReferences,
    rehydrateProjectDocuments as _rehydrateProjectDocuments,
    revokeProjectDocumentUrls as _revokeProjectDocumentUrls,
} from '../services/document-blob-store';
import { revokeVideoObjectUrl as _revokeVideoObjectUrl } from '../services/video-blob-store';
import { DataService as _DataService } from '../services/data-service';
import { safeWriteFF } from '../services/safe-write';
import { calculateComprehensiveKPI as _calculateComprehensiveKPI } from '../utils/helpers';
import { healLegacyCamelCasedBlob, normalizeToSnake } from '../utils/jsonb-normalize';
import {
    inferCompletedStages as _inferCompletedStages,
    normalizeCompletedStages as _normalizeCompletedStages,
    projectHasCompletedQuote as _projectHasCompletedQuote,
    projectHasStage5Presentation as _projectHasStage5Presentation,
    quoteRowsCurrentForScope,
} from '../utils/project-stage';
import { pdfNeedsLocalRecovery, photoNeedsLocalRecovery } from '../utils/sync-recovery-detection';
import {
    ensureOriginalGeneratedSnapshots as _ensureOriginalGeneratedSnapshots,
    getOriginalGeneratedSnapshots as _getOriginalGeneratedSnapshots,
    getOriginalSnapshotPersistenceContext as _getOriginalSnapshotPersistenceContext,
    stripOriginalScopePhotoLocators as _stripOriginalScopePhotoLocators,
} from '../utils/ai-original-snapshots';
import { withQuoteArtifactsInAiContext } from '../services/production-quote-artifacts';
import { normaliseDiagnosisAnchoredStrategyLabel } from '../utils/project-strategy-label';
import {
    captureProjectRunContext,
    createStaleProjectRunError,
    isProjectRunContextCurrent,
    type ProjectRunContext,
} from '../utils/project-run-context';
import { classifyProjectSaveRetry } from '../utils/project-save-retry-policy';
import { prepareQuoteHeroForProjectSave as _prepareQuoteHeroForProjectSave } from '../services/quote-hero-persistence';
import {
    findCanonicalProjectOwner,
    isCanonicalProjectUuid,
    projectValueHasDeviceLocalMedia,
} from '../services/project-media-write-boundary';
import {
    hasReviewableManualPriceAuthority,
    reconcileGeneratedAmountsToPricingFloor,
} from '../utils/quote-pricing-readiness';
import { healDocumentDerivedPhotoCoverage } from '../utils/project-photo-contract';
import { assertProjectCloudSnapshotCompatible } from '../services/project-intake-persistence';
import {
    QUOTE_RUN_ALREADY_STOPPED_MESSAGE,
    QUOTE_RUN_JOB_SHOWN,
    QUOTE_RUN_STOPPING_MESSAGE,
    QUOTE_RUN_STOP_REASON,
    createQuoteRunActiveError,
    ignoreQuoteRunActive,
    showQuoteRunActiveNotice,
} from '../services/quote-run-open-guard';
import type { QuoteRunRefusedChange } from '../services/quote-run-open-guard';
import { noteLoadedProjectPipeline, pipelineVersionForSave } from '../utils/quote-brain-project';

// ── Loose views over JSDoc-typed JS modules ─────────────────────────────────
// The moved bodies were authored in App.jsx under `checkJs: false`; checking
// them against these modules' JSDoc-inferred types would force non-annotation
// body edits (return-shape unions, `never[]` catch fallbacks, narrow param
// types), which this slice forbids. Each import is therefore consumed through
// a loosely-typed view. RUNTIME BINDINGS ARE UNCHANGED — the views alias the
// exact same objects/functions, and every `obj.method(...)` call site keeps
// its property-access receiver form.
type AnyFn = (...args: any[]) => any;
type AnyAsyncListFn = (...args: any[]) => Promise<LooseRecord[]>;
const seenPhotoIdsForLoadedProject: AnyFn = _seenPhotoIdsForLoadedProject;
const saveProject: AnyFn = _saveProject;
const getProject: AnyFn = _getProject;
const SyncFolderService: any = _SyncFolderService;
const migrateProjectPhotos: AnyFn = _migrateProjectPhotos;
const storePhotoBlob: AnyFn = _storePhotoBlob;
const buildPhotoCatalog: AnyFn = _buildPhotoCatalog;
const findCatalogPhoto: AnyFn = _findCatalogPhoto;
const makePhotoArrayPortableForPersistence: (...args: any[]) => Promise<LooseRecord[]> = _makePhotoArrayPortableForPersistence;
const makeQuoteDataPortableForPersistence: AnyFn = _makeQuoteDataPortableForPersistence;
const makeScopeImagesPortableForPersistence: (...args: any[]) => Promise<LooseRecord[]> = _makeScopeImagesPortableForPersistence;
const mergePortablePhotoFields: AnyFn = _mergePortablePhotoFields;
const prepareQuoteHeroForProjectSave: AnyFn = _prepareQuoteHeroForProjectSave;
const makeDocumentArrayPortableForPersistence: (...args: any[]) => Promise<LooseRecord[]> = _makeDocumentArrayPortableForPersistence;
const documentNeedsBackendUpload: AnyFn = _documentNeedsBackendUpload;
const normalizeDocumentsForProjectLoad: (...args: any[]) => LooseRecord[] = _normalizeDocumentsForProjectLoad;
const migrateProjectDocuments: AnyFn = _migrateProjectDocuments;
const quarantineInvalidDocumentBlobReferences: AnyFn = _quarantineInvalidDocumentBlobReferences;
const rehydrateProjectDocuments: AnyFn = _rehydrateProjectDocuments;
const revokeProjectDocumentUrls: AnyFn = _revokeProjectDocumentUrls;
const revokeVideoObjectUrl: AnyFn = _revokeVideoObjectUrl;
const calculateComprehensiveKPI: AnyFn = _calculateComprehensiveKPI;
const inferCompletedStages: AnyFn = _inferCompletedStages;
const normalizeCompletedStages: AnyFn = _normalizeCompletedStages;
const projectHasCompletedQuote: AnyFn = _projectHasCompletedQuote;
const projectHasStage5Presentation: AnyFn = _projectHasStage5Presentation;
const ensureOriginalGeneratedSnapshots: AnyFn = _ensureOriginalGeneratedSnapshots;
const getOriginalGeneratedSnapshots: (...args: any[]) => LooseRecord[] = _getOriginalGeneratedSnapshots;
const getOriginalSnapshotPersistenceContext: AnyFn = _getOriginalSnapshotPersistenceContext;
const stripOriginalScopePhotoLocators: AnyFn = _stripOriginalScopePhotoLocators;
const buildDuplicateMatchKey: AnyFn = _buildDuplicateMatchKey;
const ActivityLogService: any = _ActivityLogService;
const findDuplicateProjectCandidates: (...args: any[]) => any[] = _findDuplicateProjectCandidates;
const rememberDuplicateSaveDecision: AnyFn = _rememberDuplicateSaveDecision;
const recallDuplicateSaveDecision: AnyFn = _recallDuplicateSaveDecision;
const DataService: {
    projects: { getAllSummaries: AnyAsyncListFn };
    companies: { search: AnyAsyncListFn; save: AnyFn };
    crmContacts: { getAll: AnyAsyncListFn; getByCompany: AnyAsyncListFn; save: AnyFn; search: AnyAsyncListFn };
    properties: { getByStrataplan: AnyAsyncListFn; findByAddress: AnyAsyncListFn; getByCompany: AnyAsyncListFn; save: AnyFn };
    propertyContacts: { save: AnyFn };
} = _DataService as any;
const loadCanonicalBackendProjectForBatch: AnyFn = _loadCanonicalBackendProjectForBatch;

/**
 * JSONB-shaped / dynamically-keyed record. The persistence seam reads and
 * writes project envelopes, quoteData blobs, CRM rows and photo/document
 * entries whose inner shapes are AI-prompt- or migration-contracted (see the
 * OPAQUE_JSONB_KEYS rule in CLAUDE.md) — hard-typing them here would force
 * non-annotation body edits, which this slice forbids. `any`-valued index
 * access keeps the moved bodies byte-exact.
 */
type LooseRecord = Record<string, any>;

/**
 * Edits a save kept on this device while QuoteMaker's server was not
 * answering, painted back by a project open (outage lane, 2026-09-29).
 */
interface RestoredUnsavedEdits {
    keptAt: string;
    /** The server row changed after the edits were made. */
    serverMoved: boolean;
    /** The server content the edits were based on (for the stale-write self-heal). */
    baselineSignature: string | null;
    /** The server row as loaded, before the kept edits were painted over it. */
    serverRow: LooseRecord;
}

const ISSUED_QUOTE_LIFECYCLE_STATUSES = new Set([
    'sent',
    'issued',
    'quoted',
    'ready_for_builder_review',
    'accepted',
    'declined',
    'won',
    'lost',
    'expired',
    'archived',
]);

function isUnissuedPreStage5Draft(project: LooseRecord): boolean {
    const aiContext = project?.aiContext && typeof project.aiContext === 'object'
        ? project.aiContext
        : (project?.ai_context && typeof project.ai_context === 'object' ? project.ai_context : {});
    const lifecycleStatuses = [
        project?.status,
        project?.quoteStatus,
        project?.quote_status,
    ].map(value => String(value || '').trim().toLowerCase());
    const quoteData = project?.quoteData && typeof project.quoteData === 'object'
        ? project.quoteData
        : (project?.quote_data && typeof project.quote_data === 'object' ? project.quote_data : {});
    const quoteCompletion = quoteData?.meta?.completion && typeof quoteData.meta.completion === 'object'
        ? quoteData.meta.completion
        : {};
    const issued = lifecycleStatuses.some(status => ISSUED_QUOTE_LIFECYCLE_STATUSES.has(status))
        || project?.emailSent === true
        || project?.email_sent === true
        || Boolean(project?.emailSentAt || project?.email_sent_at)
        || project?.pdfGenerated === true
        || project?.pdf_generated === true
        || Boolean(project?.pdfGeneratedAt || project?.pdf_generated_at)
        || project?.costingsSent === true
        || project?.costings_sent === true
        || Boolean(project?.costingsSentAt || project?.costings_sent_at)
        || Boolean(aiContext?.finalSentAt || aiContext?.final_sent_at)
        || Boolean(quoteCompletion?.reviewedAt || quoteCompletion?.reviewed_at)
        || Boolean(project?.wonAt || project?.won_at || project?.lostAt || project?.lost_at)
        || String(project?.projectType || project?.project_type || '').trim().toLowerCase() === 'history';
    if (issued || projectHasCompletedQuote(project)) return false;

    const completed = normalizeCompletedStages(project?.completedStages ?? project?.completed_stages);
    const stage = Number(project?.currentStage ?? project?.current_stage ?? 1);
    return !(Number.isFinite(stage) && stage >= 5) && !completed.has(5);
}

/** Options for loadProject / loadProjectUnchecked (see loadProject). */
export interface LoadProjectOptions {
    /**
     * Asked once, after every fetch, just before the project would paint.
     * `false` leaves the screen as it is: nothing painted, no navigation.
     */
    shouldPaint?: () => boolean;
    /**
     * Ask the unsaved-changes question even if the render that made this call saw nothing unsaved. Used by the paint boundary
     * (C29) when the project on screen was worked in while the next one was fetched.
     */
    forceUnsavedPrompt?: boolean;
}

/**
 * The project on screen and its content at the moment an open was accepted (a clean open, a Discard, or a Save & open once its
 * save was accepted). The paint compares against it: work newer than this decision was never saved, discarded or accepted as lost.
 */
export interface OutgoingParentStamp {
    projectId: string | null;
    fingerprint: string;
}

export interface ProjectSaveOptions {
    runContext?: ProjectRunContext | null;
    beforeCommit?: (() => void | Promise<void>) | null;
    /** Explicit Stage 1 intake boundary: create/adopt the Project before any local media write. */
    ensureProjectOwner?: boolean;
    /** A guarded evidence checkpoint adopts its verified readback atomically. */
    deferMediaStateAdoption?: boolean;
    /**
     * false: a save QuoteMaker's server does not take keeps nothing on this
     * device. For an answer the caller shows only once it is saved (the Stage 5
     * request-source check): a kept copy would paint it back on the next open.
     */
    keepUnsavedEdits?: boolean;
}

const PROJECT_SAVE_RUN_CONTEXT = Symbol('project-save-run-context');
const PROJECT_SAVE_BEFORE_COMMIT = Symbol('project-save-before-commit');
const PROJECT_SAVE_COMMIT_GUARDS = Symbol('project-save-commit-guards');
const PROJECT_SAVE_DEFER_MEDIA_ADOPTION = Symbol('project-save-defer-media-adoption');
// A queued save merges into the next one; it carries keepUnsavedEdits: false with it.
const PROJECT_SAVE_KEEP_NOTHING_ON_DEVICE = Symbol('project-save-keep-nothing-on-device');
type ProjectSaveCommitGuard = {
    predicate: () => void | Promise<void>;
    protectedFields: readonly string[];
};
type ProjectOwnedSaveOverrides = LooseRecord & {
    [PROJECT_SAVE_RUN_CONTEXT]?: ProjectRunContext;
    [PROJECT_SAVE_BEFORE_COMMIT]?: (() => void | Promise<void>) | null;
    [PROJECT_SAVE_COMMIT_GUARDS]?: readonly ProjectSaveCommitGuard[];
    [PROJECT_SAVE_DEFER_MEDIA_ADOPTION]?: boolean;
    [PROJECT_SAVE_KEEP_NOTHING_ON_DEVICE]?: boolean;
};

function keepNothingOnDevice<T extends LooseRecord>(overrides: T): T {
    Object.defineProperty(overrides, PROJECT_SAVE_KEEP_NOTHING_ON_DEVICE, { value: true, enumerable: false });
    return overrides;
}

function getProjectSaveRunContext(overrides: LooseRecord | null | undefined): ProjectRunContext | null {
    return (overrides as ProjectOwnedSaveOverrides | null | undefined)?.[PROJECT_SAVE_RUN_CONTEXT] || null;
}

function getProjectSaveBeforeCommit(overrides: LooseRecord | null | undefined): (() => Promise<void>) | null {
    const owned = overrides as ProjectOwnedSaveOverrides | null | undefined;
    const guards = owned?.[PROJECT_SAVE_COMMIT_GUARDS] || [];
    if (guards.length > 0) {
        return async () => {
            const invoked = new Set<() => void | Promise<void>>();
            for (const { predicate } of guards) {
                if (invoked.has(predicate)) continue;
                invoked.add(predicate);
                await predicate();
            }
        };
    }
    const beforeCommit = owned?.[PROJECT_SAVE_BEFORE_COMMIT];
    return beforeCommit ? async () => { await beforeCommit(); } : null;
}

function getProjectSaveCommitGuards(overrides: LooseRecord | null | undefined): ProjectSaveCommitGuard[] {
    return [...((overrides as ProjectOwnedSaveOverrides | null | undefined)?.[PROJECT_SAVE_COMMIT_GUARDS] || [])];
}

const PROJECT_SAVE_CONTROL_FIELDS = new Set(['silent', 'skipDuplicateProjectGuard']);

function projectSaveOverrideFields(overrides: LooseRecord | null | undefined): string[] {
    return Object.entries(overrides || {})
        .filter(([key, value]) => value !== undefined && !PROJECT_SAVE_CONTROL_FIELDS.has(key))
        .map(([key]) => key);
}

function normalizeProjectSaveCommitGuards(guards: readonly ProjectSaveCommitGuard[]): ProjectSaveCommitGuard[] {
    const fieldsByPredicate = new Map<() => void | Promise<void>, Set<string>>();
    guards.forEach(({ predicate, protectedFields }) => {
        if (typeof predicate !== 'function') return;
        const fields = fieldsByPredicate.get(predicate) || new Set<string>();
        protectedFields.forEach(field => fields.add(field));
        fieldsByPredicate.set(predicate, fields);
    });
    return Array.from(fieldsByPredicate, ([predicate, fields]) => ({
        predicate,
        protectedFields: Array.from(fields),
    }));
}

function withProjectSaveRunContext(
    overrides: LooseRecord,
    runContext: ProjectRunContext,
    beforeCommit: (() => void | Promise<void>) | null = null,
    inheritedCommitGuards: readonly ProjectSaveCommitGuard[] | null = null,
    deferMediaStateAdoption = (overrides as ProjectOwnedSaveOverrides)?.[PROJECT_SAVE_DEFER_MEDIA_ADOPTION] === true,
): ProjectOwnedSaveOverrides {
    const owned = { ...(overrides || {}) } as ProjectOwnedSaveOverrides;
    Object.defineProperty(owned, PROJECT_SAVE_RUN_CONTEXT, {
        value: runContext,
        enumerable: false,
        configurable: false,
        writable: false,
    });
    if (deferMediaStateAdoption) {
        Object.defineProperty(owned, PROJECT_SAVE_DEFER_MEDIA_ADOPTION, { value: true, enumerable: false });
    }
    const commitGuards = normalizeProjectSaveCommitGuards(inheritedCommitGuards
        || (beforeCommit ? [{ predicate: beforeCommit, protectedFields: projectSaveOverrideFields(overrides) }] : []));
    if (commitGuards.length > 0) {
        Object.defineProperty(owned, PROJECT_SAVE_COMMIT_GUARDS, {
            value: commitGuards,
            enumerable: false,
            configurable: false,
            writable: false,
        });
        Object.defineProperty(owned, PROJECT_SAVE_BEFORE_COMMIT, {
            value: getProjectSaveBeforeCommit({ [PROJECT_SAVE_COMMIT_GUARDS]: commitGuards } as ProjectOwnedSaveOverrides),
            enumerable: false,
            configurable: false,
            writable: false,
        });
    }
    return owned;
}

function sameProjectSaveOwner(left: ProjectRunContext | null, right: ProjectRunContext | null): boolean {
    return !!left && !!right
        && left.projectId === right.projectId
        && left.loadGeneration === right.loadGeneration
        && left.signal === right.signal;
}

function createProjectSaveOwnerChangedError(): Error & { code: string; retryable: boolean } {
    const error = createStaleProjectRunError('Queued project save') as Error & { code: string; retryable: boolean };
    error.code = 'PROJECT_SAVE_OWNER_CHANGED';
    return error;
}

// The post-save cloud mirror's outcome. Module-level on purpose: a quota retry
// keeps the mirror promise pending for up to about 21 minutes (s3-sync.ts), and
// a handler created inside the save would keep the whole save scope alive with
// it, not only the snapshot the retry needs.
export function reportCloudProjectionResult(projectionResult: LooseRecord | null | undefined): void {
    // A latched permanent refusal was already reported once, when the server
    // first gave it; it sent no request now. A cancelled retry belongs to a
    // job that was deleted or a session that ended.
    if (!projectionResult?.committed && !projectionResult?.refusalLatched && !projectionResult?.retryCancelled) {
        console.warn('[QM] Cloud JSON projection was not committed:', projectionResult?.errorDetails?.[0] || 'unknown sync error');
    }
}

function reportCloudProjectionFailure(projectionError: any): void {
    console.warn('[QM] Cloud JSON projection failed after the project save:', projectionError?.message || projectionError);
}

declare global {
    interface ImportMeta {
        /** Vite-injected env (tsconfig `types` doesn't include vite/client). */
        readonly env?: any;
    }
}
/**
 * React state setter (the seam passes values and functional updates).
 * Declared as an overload pair with the UPDATER form first: the collapsed
 * `Dispatch<SetStateAction<any>>` union folds to `any`, which under this
 * repo's strict settings leaves functional-update arrow params without a
 * contextual type (TS7006). The overload keeps `prev` contextually typed
 * so the moved bodies stay byte-exact.
 */
interface AnySetter {
    (updater: (prev: any) => any): void;
    (value: any): void;
}
/** Queued-save promise endpoints (see queueProjectSave / settleQueuedProjectSaves). */
interface SaveDeferred {
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
}
/** The DEC-COUNCIL-013 useQuoteEditor surface — only the methods this seam calls. */
interface QuoteEditorLike {
    reset: () => void;
    peek: () => void;
    [key: string]: unknown;
}

export interface UseProjectPersistenceDeps {
    // ── State values (fresh each render, exactly as the original App-local closures read them) ──
    user: { name?: string } | null;
    model: string;
    currentProjectId: string | null;
    formData: LooseRecord;
    quoteData: LooseRecord | null;
    scope: LooseRecord[];
    originalAiScope: any;
    quoteNote: string;
    analysis: any;
    photoAnalysis: any;
    diagnosticResult: any;
    /** H1: the on-demand Stage 2 Risk Report record (aiContext.riskReport). */
    riskReport?: any;
    photos: LooseRecord[];
    documents: LooseRecord[];
    videos: LooseRecord[];
    videoTranscripts: unknown[];
    projectSummary: any;
    projectWMS: any;
    projectStrategy: any;
    completedStages: any;
    stage: number;
    titleEditedByUser: boolean;
    quoteTitle: string;
    projectWorkflowMode: any;
    legacyBannerDismissed: boolean;
    scopeGstInclusive: boolean;
    selectedHeroImage: any;
    parentFolder: LooseRecord | null;
    quoteSubfolder: LooseRecord | null;
    folderFiles: any;
    projects: LooseRecord[];
    hasUnsavedChanges: boolean;
    /** B (C18): unsaved work only the open Stage 5 editor holds (text typed a moment ago, an edit it has not saved). Synchronous. */
    hasUnsavedVisibleWork?: () => boolean;
    /** B: saves the outgoing project's work. With the editor up it is the editor's own Save; { ok: false } is NOT a save. Never rejects. */
    saveOutgoingVisibleWork?: () => Promise<{ ok: boolean; error?: unknown; newerWork?: boolean }>;
    /** B: a token that changes when the operator edits or types in the open editor; null when none is open. */
    visibleWorkStamp?: () => string | null;
    /** B: an open that ended without painting leaves the outgoing project as owner; App keeps its editor's save lease usable. */
    retainOutgoingProject?: () => void;
    /** B: the parent's unsaved flag NOW (a ref read), for the moment an open is about to paint. */
    hasUnsavedChangesLive?: () => boolean;
    /**
     * Settles after React has rendered and committed the state updates queued before the call (App's `useWaitForCommit`). Save & open
     * awaits it after a save so the parent's content is compared once the state the save adopted has actually been applied. Absent,
     * the comparison is made immediately.
     */
    waitForCommit?: () => Promise<void>;
    /** B: explicit Discard drops the text still in the editor's fields so none of it can reach the next project. */
    discardOutgoingVisibleDrafts?: () => void;
    aiGeneratedItems: LooseRecord;
    projectDefaultMargin: number;
    appliedLearnings: unknown[];
    learningsRetrievalMode: string;
    projectCostingSheet: any;
    workMethodOverallProgramme: any;
    // ── React state setters (App-owned) ──
    setAiGeneratedItems: AnySetter;
    setAnalysis: AnySetter;
    setAppliedLearnings: AnySetter;
    setAssistantDraftSessionKey: AnySetter;
    setCompletedStages: AnySetter;
    setCostingRegenerating: AnySetter;
    setCurrentProjectId: AnySetter;
    setDiagnosticResult: AnySetter;
    setRiskReport?: AnySetter;
    setDocuments: AnySetter;
    setExtractedFacts: AnySetter;
    setFolderFiles: AnySetter;
    setFormData: AnySetter;
    setHasUnsavedChanges: AnySetter;
    setHasViewedRationale: AnySetter;
    setImportPhase: AnySetter;
    setInspectorNotesAiPaused: AnySetter;
    setIsAnalyzing: AnySetter;
    setIsSavingProject: AnySetter;
    setIsStage1Processing: AnySetter;
    setLastSavedAt: AnySetter;
    setLastSummarizedSnapshot: AnySetter;
    setLearningsRetrievalMode: AnySetter;
    setLegacyBannerDismissed: AnySetter;
    setMediaRecoveryNeeded: AnySetter;
    setMissingFiles: AnySetter;
    setOcrApplying: AnySetter;
    setOcrResults: AnySetter;
    setOcrScanning: AnySetter;
    setOriginalAiScope: AnySetter;
    setParentFolder: AnySetter;
    setPhotoAnalysis: AnySetter;
    setPhotoAssignBusy: AnySetter;
    setPhotos: AnySetter;
    setPostProcessContext: AnySetter;
    setPreprocessedFiles: AnySetter;
    setProjectCostingSheet: AnySetter;
    setProjectDefaultMargin: AnySetter;
    setProjectStrategy: AnySetter;
    setProjectSummary: AnySetter;
    setProjectWMS: AnySetter;
    setProjectWorkflowMode: AnySetter;
    setProjects: AnySetter;
    setQuoteData: AnySetter;
    setQuoteNote: AnySetter;
    setQuoteSubfolder: AnySetter;
    setQuoteTitle: AnySetter;
    setResetCounter: AnySetter;
    setRibbonDismissing: AnySetter;
    setRibbonNotification: AnySetter;
    setSaveError: AnySetter;
    setManualSaveRetryLatch: AnySetter;
    setSaveStatus: AnySetter;
    setScope: AnySetter;
    setScopeGstInclusive: AnySetter;
    setSelectedHeroImage: AnySetter;
    setStage: AnySetter;
    setSummaryError: AnySetter;
    /** Optional: the Summary out-of-date notice, cleared with summaryError. */
    setSummaryRerunHold?: AnySetter;
    setSummaryNeedsRefresh: AnySetter;
    /** Optional: the Stage 1 Risk countdown Hold, released for a new or reopened project. */
    setTitleEditedByUser: AnySetter;
    setToast: AnySetter;
    setVideoTranscripts: AnySetter;
    setVideos: AnySetter;
    setWorkMethodOverallProgramme: AnySetter;
    // ── Live refs (App-owned; the queue/dedupe/batch/open-quote decls were RELOCATED within App above the instantiation — see header) ──
    autoAssignAbortRef: MutableRefObject<AbortController | null>;
    autoOpenLoadedQuoteProjectRef: MutableRefObject<string | null>;
    batchActiveRef: MutableRefObject<boolean>;
    /**
     * The project the active Quote Run is driving: set by the queue's own
     * (batch-mode) load, cleared by App when a run starts or ends. Opening it
     * shows it instead of refusing. Optional so older harnesses keep working.
     */
    quoteRunProjectIdRef?: MutableRefObject<string | null>;
    /**
     * True once any stop of the active Quote Run has been raised, until the
     * run ends (App clears it). A refused open then says the run is stopping
     * instead of asking again.
     */
    quoteRunStopRequestedRef?: MutableRefObject<boolean>;
    /** Server `updated_at` the open working copy descends from; null = do not enforce. */
    baseUpdatedAtRef: MutableRefObject<string | null>;
    /** Content signature of the server row that token describes; see App.jsx. */
    staleWriteBaselineRef: MutableRefObject<{ projectId: string; signature: string } | null>;
    /** Bounded immutable-original context captured from the full cold-loaded Project. */
    loadedOriginalSnapshotContextRef: MutableRefObject<{
        projectId: string | null;
        context: LooseRecord;
    }>;
    currentProjectIdRef: MutableRefObject<string | null>;
    photosRef: MutableRefObject<LooseRecord[]>;
    videosRef: MutableRefObject<LooseRecord[]>;
    quoteDataRef: MutableRefObject<LooseRecord | null>;
    selectedHeroImageRef: MutableRefObject<any>;
    scopeRef: MutableRefObject<LooseRecord[]>;
    importAbortRef: MutableRefObject<boolean>;
    inspectorNotesAiPausedRef: MutableRefObject<boolean>;
    isAnalyzingRef: MutableRefObject<boolean>;
    isLoadingProjectRef: MutableRefObject<boolean>;
    isSavingProjectRef: MutableRefObject<boolean>;
    isSummarizingRef: MutableRefObject<boolean>;
    lastAssignedPhotoIdsRef: MutableRefObject<Set<unknown>>;
    lastAutoProjectFocusRef: MutableRefObject<string>;
    pendingOpenQuoteRef: MutableRefObject<boolean>;
    pendingSaveDeferredsRef: MutableRefObject<SaveDeferred[]>;
    pendingSaveEnsureProjectOwnerRef: MutableRefObject<boolean>;
    pendingSaveOverridesRef: MutableRefObject<any>;
    pendingSaveRef: MutableRefObject<boolean>;
    photoAssignBusyRef: MutableRefObject<boolean>;
    prevFilesProcessedRef: MutableRefObject<boolean>;
    projectAbortRef: MutableRefObject<AbortController>;
    /** Ordinary save/media owner; aborted only when the project lifecycle changes. */
    projectPersistenceAbortRef: MutableRefObject<AbortController>;
    /**
     * STOP-button halt latch (optional so older test harnesses keep working).
     * A project reset / load is a fresh pipeline context, so both clear it.
     */
    aiPipelineHaltedRef?: MutableRefObject<boolean>;
    /**
     * Review mode. SET when a load completes (the operator opened this project
     * to look at it), cleared by resetForm (a brand-new project must still
     * cascade). While set, every AUTO pipeline trigger refuses. See the
     * declaration comment in App.jsx.
     */
    openedForReviewRef?: MutableRefObject<boolean>;
    /**
     * The project markup the operator chose for one project in this session
     * (the Assistant's set_project_margin, D10), or null. Its saves write that
     * markup over the one on the project summary. Cleared by a load and by a
     * new project. Optional so older test harnesses keep working.
     */
    operatorMarginRef?: MutableRefObject<{ projectId: string; margin: number } | null>;
    projectLoadGenerationRef: MutableRefObject<number>;
    /** Last project identity + generation that actually completed paint. */
    paintedProjectLoadContextRef: MutableRefObject<{
        projectId: string | null;
        loadGeneration: number;
        /**
         * A recovered quote from Quick edit holds the new-project screen
         * (markRecoveredQuoteOnScreen). A reset or a paint replaces the
         * object, which ends it.
         */
        recoveredQuote?: boolean;
    }>;
    /**
     * App's record of the open still loading, the one useAppRouting's
     * openStillLoading() reads. Every load that lands QuoteMaker records
     * itself here until it settles (see recordOpenStillLoading). Optional so
     * older harnesses keep working.
     */
    pendingProjectOpenRef?: MutableRefObject<PendingProjectOpen | null>;
    /** Exact future fingerprint for one load-owned, possibly-delayed hydration write. */
    pendingProjectHydrationFingerprintRef?: MutableRefObject<ProjectHydrationFingerprintReceipt | null>;
    projectLoadGuardUntilRef: MutableRefObject<number>;
    /**
     * Baseline for the meaningful-change comparator: a normalised fingerprint
     * of the persisted state as of the last load or successful save. Re-pointed
     * here on save success so the dirty bit reflects a real diff rather than a
     * hydration-driven identity change.
     */
    savedFingerprintRef?: MutableRefObject<string | null>;
    /** Reads the project fingerprint, overlaid with the exact save envelope. */
    captureProjectFingerprint?: (overrides?: LooseRecord) => string;
    qmQuoteNoAllocationInFlightRef: MutableRefObject<Set<string>>;
    saveCurrentProjectRef: MutableRefObject<((overrides?: LooseRecord, options?: ProjectSaveOptions) => Promise<unknown>) | null>;
    saveDrainTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
    saveErrorRef: MutableRefObject<any>;
    suppressUnsavedRef: MutableRefObject<boolean>;
    titleSourceRef: MutableRefObject<string>;
    _crmLogDedupeRef: MutableRefObject<Map<any, any>>;
    // ── App-owned helpers + constants (all declared above the instantiation — direct threads, no lazy wrappers needed in this slice) ──
    QM_STATE_KEY: string;
    showConfirm: (opts: LooseRecord) => void;
    classifySaveError: (err: unknown) => { kind: string; message: string };
    setCrossTabConflict: AnySetter;
    saveCurrentProject: (overrides?: LooseRecord, options?: ProjectSaveOptions) => Promise<unknown>;
    generateQuoteTitle: (source?: LooseRecord) => string;
    handleNavigate: (page: string, options?: LooseRecord) => void;
    /**
     * Stops the active Quote Run exactly as the STOP pill does (App's
     * cancelAiPipeline): the queue's stop first, then the job's AI stage is
     * aborted so the run ends promptly. Optional so older harnesses keep working.
     */
    stopQuoteRun?: (options: { reason: string }) => void;
    editor: QuoteEditorLike;
    procDispatch: (action: { type: string; payload?: unknown }) => void;
    /** Transient, App-owned removal intents for this exact Project only. */
    getPendingProjectMediaRetirementReceipts: (projectId: string) => ProjectMediaRetirementReceipt[];
    /** Clear only the exact snapshot carried by a matching canonical save receipt. */
    acknowledgeProjectMediaRetirementReceipts: (
        projectId: string,
        receipts: ProjectMediaRetirementReceipt[],
    ) => void;
}

// Every write to `projects` from this tab, foreground save or background load
// repair, runs through the tab's ONE lock, one at a time (see
// project-write-lock.ts for why). Shared, not created here: the remote-version
// monitor asks the same lock whether one of this tab's writes is in flight.
const withProjectWriteLock = tabProjectWriteLock;

export function useProjectPersistence(deps: UseProjectPersistenceDeps) {
    const {
        user,
        model,
        currentProjectId,
        formData,
        quoteData,
        scope,
        originalAiScope,
        quoteNote,
        analysis,
        photoAnalysis,
        diagnosticResult,
        riskReport,
        photos,
        documents,
        videos,
        videoTranscripts,
        projectSummary,
        projectWMS,
        projectStrategy,
        completedStages,
        stage,
        titleEditedByUser,
        quoteTitle,
        projectWorkflowMode,
        legacyBannerDismissed,
        scopeGstInclusive,
        selectedHeroImage,
        parentFolder,
        quoteSubfolder,
        folderFiles,
        projects,
        hasUnsavedChanges,
        hasUnsavedVisibleWork,
        saveOutgoingVisibleWork,
        discardOutgoingVisibleDrafts,
        visibleWorkStamp,
        retainOutgoingProject,
        hasUnsavedChangesLive,
        waitForCommit,
        aiGeneratedItems,
        projectDefaultMargin,
        appliedLearnings,
        learningsRetrievalMode,
        projectCostingSheet,
        workMethodOverallProgramme,
        setAiGeneratedItems,
        setAnalysis,
        setAppliedLearnings,
        setAssistantDraftSessionKey,
        setCompletedStages,
        setCostingRegenerating,
        setCurrentProjectId,
        setDiagnosticResult,
        setRiskReport,
        setDocuments,
        setExtractedFacts,
        setFolderFiles,
        setFormData,
        setHasUnsavedChanges,
        setHasViewedRationale,
        setImportPhase,
        setInspectorNotesAiPaused,
        setIsAnalyzing,
        setIsSavingProject,
        setIsStage1Processing,
        setLastSavedAt,
        setLastSummarizedSnapshot,
        setLearningsRetrievalMode,
        setLegacyBannerDismissed,
        setMediaRecoveryNeeded,
        setMissingFiles,
        setOcrApplying,
        setOcrResults,
        setOcrScanning,
        setOriginalAiScope,
        setParentFolder,
        setPhotoAnalysis,
        setPhotoAssignBusy,
        setPhotos,
        setPostProcessContext,
        setPreprocessedFiles,
        setProjectCostingSheet,
        setProjectDefaultMargin,
        setProjectStrategy,
        setProjectSummary,
        setProjectWMS,
        setProjectWorkflowMode,
        setProjects,
        setQuoteData,
        setQuoteNote,
        setQuoteSubfolder,
        setQuoteTitle,
        setResetCounter,
        setRibbonDismissing,
        setRibbonNotification,
        setSaveError,
        setManualSaveRetryLatch,
        setSaveStatus,
        setScope,
        setScopeGstInclusive,
        setSelectedHeroImage,
        setStage,
        setSummaryError,
        setSummaryRerunHold,
        setSummaryNeedsRefresh,
        setTitleEditedByUser,
        setToast,
        setVideoTranscripts,
        setVideos,
        setWorkMethodOverallProgramme,
        autoAssignAbortRef,
        autoOpenLoadedQuoteProjectRef,
        batchActiveRef,
        quoteRunProjectIdRef,
        quoteRunStopRequestedRef,
        baseUpdatedAtRef,
        staleWriteBaselineRef,
        loadedOriginalSnapshotContextRef,
        currentProjectIdRef,
        photosRef,
        videosRef,
        quoteDataRef,
        selectedHeroImageRef,
        scopeRef,
        importAbortRef,
        inspectorNotesAiPausedRef,
        isAnalyzingRef,
        isLoadingProjectRef,
        isSavingProjectRef,
        isSummarizingRef,
        lastAssignedPhotoIdsRef,
        lastAutoProjectFocusRef,
        pendingOpenQuoteRef,
        pendingSaveDeferredsRef,
        pendingSaveEnsureProjectOwnerRef,
        pendingSaveOverridesRef,
        pendingSaveRef,
        photoAssignBusyRef,
        prevFilesProcessedRef,
        projectAbortRef,
        projectPersistenceAbortRef,
        aiPipelineHaltedRef,
        openedForReviewRef,
        operatorMarginRef,
        projectLoadGenerationRef,
        paintedProjectLoadContextRef,
        pendingProjectOpenRef,
        pendingProjectHydrationFingerprintRef,
        projectLoadGuardUntilRef,
        savedFingerprintRef,
        captureProjectFingerprint,
        qmQuoteNoAllocationInFlightRef,
        saveCurrentProjectRef,
        saveDrainTimerRef,
        saveErrorRef,
        suppressUnsavedRef,
        titleSourceRef,
        _crmLogDedupeRef,
        QM_STATE_KEY,
        showConfirm,
        classifySaveError,
        setCrossTabConflict,
        saveCurrentProject,
        generateQuoteTitle,
        handleNavigate,
        stopQuoteRun,
        editor,
        procDispatch,
        getPendingProjectMediaRetirementReceipts,
        acknowledgeProjectMediaRetirementReceipts,
    } = deps;

    // ─── Quote Run guard (decision D1, 2026-09-28) ───────────────────────
    // While a Quote Run is active (batchActiveRef, both phases), an open the
    // queue did not start and a reset would abort the running job: the job's
    // AI signal and its first photo upload both hang off projectAbortRef, and
    // the next load generation makes its saves stale. Refuse instead, with
    // ONE message offering "Stop the run" and "Keep running". Returns true
    // when refused. Reads refs, the stable showConfirm and setToast, and the
    // App-supplied stop, so a copy held by an older closure still answers for
    // the live run.
    //
    // "Stop the run" (A3 review) goes through App's STOP path, which raises
    // the queue stop and then aborts the job's AI stage. The bare queue event
    // left that stage running for minutes, so the open never happened and
    // every retry asked the same question. The operator is now told the run
    // is stopping, and a retry before it has stopped says so again.
    //
    // These notices are 'warning', not 'info' (A3 review round 2): App renders
    // the global toast only through shouldShowToast(), which drops 'info' and
    // 'success', so an 'info' notice here was never seen and a retried click
    // off the QuoteMaker page did nothing and said nothing.
    const stopQuoteRunForProjectChange = () => {
        // The run ended on its own while the question was open. A stop now
        // would abort whatever AI work the operator started next.
        if (!batchActiveRef?.current) {
            setToast({ type: 'warning', message: QUOTE_RUN_ALREADY_STOPPED_MESSAGE });
            return;
        }
        if (quoteRunStopRequestedRef) quoteRunStopRequestedRef.current = true;
        stopQuoteRun?.({ reason: QUOTE_RUN_STOP_REASON });
        setToast({ type: 'warning', message: QUOTE_RUN_STOPPING_MESSAGE });
    };
    const refuseProjectChangeDuringQuoteRun = (change: QuoteRunRefusedChange, projectId?: unknown): boolean => {
        if (!batchActiveRef?.current) return false;
        const stopping = quoteRunStopRequestedRef?.current === true;
        const what = change === 'reset' ? 'Project reset' : `Project ${projectId ?? '(unknown)'} open`;
        console.info(`[QM] ${what} refused: a Quote Run is active${stopping ? ' and stopping' : ''}.\n${new Error(`${change} caller`).stack || ''}`);
        if (stopping) setToast({ type: 'warning', message: QUOTE_RUN_STOPPING_MESSAGE });
        else showQuoteRunActiveNotice(showConfirm, stopQuoteRunForProjectChange);
        return true;
    };
    // The job the active Quote Run is driving, or null (no run, or the run is
    // still reading files). The queue's own load records it before its
    // backend fetch, so for a moment it is the project in progress while
    // nothing is on screen yet. handleNavigate's fast path asks this (A3
    // review round 3): it read that empty screen as "no project", reset the
    // form (refused, so View live asked to stop the run being watched) or
    // reopened the tab's last project (when that was this job, the open and
    // the show called each other until the stack ran out).
    const quoteRunJobInProgress = (): string | null => {
        if (!batchActiveRef?.current) return null;
        const runProjectId = quoteRunProjectIdRef?.current;
        return runProjectId ? String(runProjectId) : null;
    };
    // Opening the job the run is driving (View live on its Processing row, its
    // Run Sheet row) is not a change of project: QuoteMaker is already working
    // on it. Show it instead of refusing, and never load it: a load would
    // abort the job. force: this navigates INSTEAD of loading, so it has no
    // completeProjectLoad tail to land it (CLAUDE.md routing rule). Returns
    // true when shown.
    const showQuoteRunJobInsteadOfOpening = (projectId?: unknown): boolean => {
        const runProjectId = quoteRunJobInProgress();
        if (!runProjectId || projectId == null || String(projectId) !== runProjectId) return false;
        console.info(`[QM] Project ${String(projectId)} is the Quote Run's job in progress; showing it instead of opening it again.`);
        handleNavigate('quotemaker', { force: true });
        return true;
    };
    // The choke-point form: throws QUOTE_RUN_ACTIVE, never returns silently.
    // A silent return would strand a caller's own guard (the intake bridge
    // sets isLoadingProjectRef before loading and clears it only in its catch).
    const assertNoQuoteRunForProjectChange = (change: QuoteRunRefusedChange, projectId?: unknown) => {
        if (refuseProjectChangeDuringQuoteRun(change, projectId)) {
            throw createQuoteRunActiveError(change, projectId);
        }
    };

    // The operator threw away the project never saved that is on screen
    // (Discard changes, Cancel & Start New, a discard during an outage): forget
    // its copy in the new-project slot. Otherwise the next blank QuoteMaker
    // page painted it back ("It is being saved now") and the autosave created
    // it (review of the outage lane, 2026-09-30). Only a copy this editor kept
    // or painted back: another tab's is not this screen's to throw away. Run
    // it BEFORE resetForm or an open, which release the slot in this tab.
    const discardKeptNewProject = () => {
        if (hasKeptUnsavedEdits(NEW_PROJECT_SLOT)) void clearUnsavedProjectEdits(NEW_PROJECT_SLOT);
    };

    // Reset form
    const resetForm = () => {
        // A Quote Run refuses the reset before anything is cleared or aborted.
        assertNoQuoteRunForProjectChange('reset');
        // v5.46.73: claim a fresh load generation — a stale in-flight project
        // open must not paint over the freshly reset form (New Project /
        // delete-current-project both route through here).
        projectLoadGenerationRef.current += 1;
        // The editor leaves the new project it held; any copy of it kept on
        // this device stays there for the next blank new project.
        releaseNewProjectSlot();
        paintedProjectLoadContextRef.current = {
            projectId: null,
            loadGeneration: projectLoadGenerationRef.current,
        };
        // ─── Cancel ALL in-flight AI processes ───────────────────────────
        // Abort the master controller so every async function that holds its
        // signal (analysis, summary, rationale, quote gen, orchestrator) gets
        // an AbortError and stops writing state into the old project. The
        // reason names the reset: a bare abort() read "signal is aborted
        // without reason" and could not be traced. Keep the AbortError name and
        // the word "aborted"; both are what the abort classifiers read.
        try { projectAbortRef.current.abort(new DOMException('Project work aborted: the project was reset.', 'AbortError')); } catch {}
        projectAbortRef.current = new AbortController(); // fresh controller for the new project
        try { projectPersistenceAbortRef.current.abort('project-reset'); } catch {}
        projectPersistenceAbortRef.current = new AbortController();
        // Fresh project = fresh pipeline context: release the STOP-button halt
        // latch so the new project's auto cascade runs normally.
        if (aiPipelineHaltedRef) aiPipelineHaltedRef.current = false;
        // A brand-new project is the one case that is NOT a review: the
        // operator is starting work, so the cascade must run as it always has.
        if (openedForReviewRef) openedForReviewRef.current = false;
        // Abort pre-emptive photo auto-assign if in-flight
        if (autoAssignAbortRef.current) {
            try { autoAssignAbortRef.current.abort(); } catch {}
            autoAssignAbortRef.current = null;
        }
        // v5.19.18: also clear App-level busy mirror — project switch must
        // not leave a stale "busy" flag locking the next project's flow.
        photoAssignBusyRef.current = false;
        setPhotoAssignBusy(false);
        // v5.19.24: reset the photo-id seen-set so the new project's first
        // photo+scope pair fires the auto-assign, instead of the previous
        // project's set blocking it as "already seen".
        lastAssignedPhotoIdsRef.current = new Set();
        // Release concurrency guards so the new project can run these
        isAnalyzingRef.current = false;
        isSummarizingRef.current = false;
        inspectorNotesAiPausedRef.current = false;
        lastAutoProjectFocusRef.current = '';

        suppressUnsavedRef.current = true;
        if (pendingProjectHydrationFingerprintRef) {
            pendingProjectHydrationFingerprintRef.current = null;
        }
        // Document preview object URLs are project-owned browser resources.
        // Release this project's URLs before clearing its document lane; keeping
        // them across every New Project cycle pins each PDF Blob until tab exit.
        revokeProjectDocumentUrls(currentProjectIdRef.current || currentProjectId);
        // Time-windowed belt-and-braces suppression - same mechanism used in
        // completeProjectLoad. Covers async stragglers after reset.
        projectLoadGuardUntilRef.current = Date.now() + 2000;
        importAbortRef.current = true; // Signal any in-progress imports to stop
        setResetCounter(c => c + 1); // Bump counter so Stage1 resets its local state
        currentProjectIdRef.current = null;
        setCurrentProjectId(null);
        setManualSaveRetryLatch(null);
        setAssistantDraftSessionKey(createAssistantDraftSessionKey());
        PipelineMonitor.setProjectId(null);
        setHasUnsavedChanges(false);
        setLastSavedAt(null);
        // No open project means nothing to be stale against; the next save
        // mints a new row. Leaving a previous project's token here would make
        // that save look like a conflicting edit of the record we just left.
        baseUpdatedAtRef.current = null;
        loadedOriginalSnapshotContextRef.current = { projectId: null, context: {} };
        setFormData({
            assessor: AssessorNamesService.getLastUsed() || user?.name || '',
            visitDate: new Date().toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
            address: '',
            strataplan: '',
            orderNumber: '',
            notes: '',
            clientName: '',
            clientCompany: '',
            clientEmail: '',
            clientPhone: '',
            clientTitle: '',
            siteContactName: '',
            siteContactPhone: '',
            unitNumber: '',
            jobDescription: '',
            attentionTo: '',  // Phase 3C Gap #8 Layer A
        });
        setPhotos([]);
        setDocuments([]);
        videos.forEach(v => { if (v.url) revokeVideoObjectUrl(v.url); });
        setVideos([]);
        setVideoTranscripts([]);
        setPreprocessedFiles([]);
        setPostProcessContext(null);
        setSelectedHeroImage(null);
        setAnalysis(null);
        setPhotoAnalysis(null);
        setDiagnosticResult(null);
        setRiskReport?.(null);
        setScope([]);
        setQuoteData(null);
        // M2/M3 polish: wipe retrieval-mode + attribution + WMS programme
        // so the new project doesn't inherit the previous project's audit
        // trail or programme narrative.
        setAppliedLearnings([]);
        setLearningsRetrievalMode('none');
        setWorkMethodOverallProgramme(null);
        setProjectStrategy(null);
        setProjectWMS(null);
        setLegacyBannerDismissed(false);
        setProjectCostingSheet(null);
        setQuoteNote('');
        setScopeGstInclusive(true); // Reset to default inc-GST for new project
        setAiGeneratedItems({});
        setOriginalAiScope(null);
        setProjectDefaultMargin(15); // New project - fresh 15% default
        if (operatorMarginRef) operatorMarginRef.current = null;
        setQuoteTitle(generateQuoteTitle());
        setTitleEditedByUser(false);
        setHasViewedRationale(false);
        // Reset Stage 1.5 (Project Summary) state
        setProjectSummary(null);
        setProjectWorkflowMode(PROJECT_WORKFLOW_MODES.FULL_AI);
        setSummaryError(null);
        setSummaryRerunHold?.(null);
        setSummaryNeedsRefresh(false);
        setLastSummarizedSnapshot(null);
        setInspectorNotesAiPaused(false);
        setStage(1);
        setCompletedStages(new Set());

        setQuoteSubfolder(null);
        setFolderFiles({ photos: [], documents: [], outputs: [] });
        setSaveStatus('idle');
        setMissingFiles({ photos: [], documents: [], subfolder: null });
        // 5A / O-S1: the "files are missing from the project folder" notice belongs to the project that raised it. A project load clears it
        // (loadProjectUnchecked), but New Project never did, so the previous project's PDF or photo recovery banner stayed up over a blank
        // project. Any recovery still in flight for the old project cannot republish it: it re-checks isCurrentProjectLoad, which reads the
        // project id this reset has just nulled.
        setMediaRecoveryNeeded(null);

        // Reset file system parent folder
        setParentFolder(null);

        // Reset analysis/processing state
        setIsAnalyzing(false);
        setIsStage1Processing(false);

        // ─── Reset lifted Stage 1 state (the previously-local-to-Stage1 stuff) ───
        setImportPhase('import');
        procDispatch({ type: PROC_ACTIONS.CLEAR });
        setExtractedFacts([]);
        // Clear stored QM view state so the next nav doesn't restore the just-cleared one
        try { sessionStorage.removeItem(QM_STATE_KEY); } catch {}

        // Reset generation state
        setCostingRegenerating(false);

        // Reset OCR state
        setOcrScanning(false);
        setOcrResults(null);
        setOcrApplying(false);

        // Reset quote editor state (prevents grayed-out stage nav on new project)
        editor.reset();

        // Clear ribbon notification
        setRibbonNotification(null);
        setRibbonDismissing(false);
    };

    // Merge contract (behavioural round 2, P1 — DOCUMENTED, not a bug):
    // SHALLOW, last-wins, `undefined` skipped. Every caller queues WHOLE-VALUE
    // snapshots — complete quoteData/wms/scope/completedStages/formData
    // records (use-quote-editor.js, use-wms-generation.ts,
    // use-stage-navigation.ts, App.jsx costing/save-quote paths) — never
    // partial nested patches, so the later snapshot must land verbatim. A
    // deep merge would be WRONG here: arrays have no meaningful element
    // merge, and grafting an older quoteData/wms snapshot's keys under a
    // newer one resurrects deletions. Skipping `undefined` keeps
    // `{ key: undefined }` from erasing an earlier queued override (absence
    // means "no opinion", not "clear it").
    const mergeProjectSaveOverrides = (base: LooseRecord = {}, next: LooseRecord = {}) => {
        const baseOwner = getProjectSaveRunContext(base);
        const nextOwner = getProjectSaveRunContext(next);
        if (baseOwner && nextOwner && !sameProjectSaveOwner(baseOwner, nextOwner)) {
            throw createProjectSaveOwnerChangedError();
        }
        const replacedFields = new Set(projectSaveOverrideFields(next));
        const retainedBaseGuards = getProjectSaveCommitGuards(base)
            .map(guard => ({
                ...guard,
                protectedFields: guard.protectedFields.filter(field => !replacedFields.has(field)),
            }))
            .filter(guard => guard.protectedFields.length > 0);
        const commitGuards = normalizeProjectSaveCommitGuards([
            ...retainedBaseGuards,
            ...getProjectSaveCommitGuards(next),
        ]);
        const merged = { ...(base || {}) };
        Object.entries(next || {}).forEach(([key, value]) => {
            if (value !== undefined) merged[key] = value;
        });
        const owner = nextOwner || baseOwner;
        const beforeCommit = commitGuards.length > 0
            ? getProjectSaveBeforeCommit({ [PROJECT_SAVE_COMMIT_GUARDS]: commitGuards } as ProjectOwnedSaveOverrides)
            : null;
        const deferMediaStateAdoption = (base as ProjectOwnedSaveOverrides)?.[PROJECT_SAVE_DEFER_MEDIA_ADOPTION] === true
            || (next as ProjectOwnedSaveOverrides)?.[PROJECT_SAVE_DEFER_MEDIA_ADOPTION] === true;
        const result = owner ? withProjectSaveRunContext(merged, owner, beforeCommit, commitGuards, deferMediaStateAdoption) : merged;
        return (base as ProjectOwnedSaveOverrides)?.[PROJECT_SAVE_KEEP_NOTHING_ON_DEVICE] === true
            || (next as ProjectOwnedSaveOverrides)?.[PROJECT_SAVE_KEEP_NOTHING_ON_DEVICE] === true
            ? keepNothingOnDevice(result)
            : result;
    };

    const queueProjectSave = (
        overrides: LooseRecord = {},
        options: ProjectSaveOptions = {},
    ) => {
        const queuedOverrides = mergeProjectSaveOverrides(pendingSaveOverridesRef.current, overrides);
        pendingSaveRef.current = true;
        pendingSaveOverridesRef.current = queuedOverrides;
        pendingSaveEnsureProjectOwnerRef.current = pendingSaveEnsureProjectOwnerRef.current
            || options.ensureProjectOwner === true;
        return new Promise((resolve, reject) => {
            pendingSaveDeferredsRef.current.push({ resolve, reject });
        });
    };

    const settleQueuedProjectSaves = (deferreds: SaveDeferred[], error: unknown, result?: unknown) => {
        deferreds.forEach(({ resolve, reject }) => {
            if (error) reject(error);
            else resolve(result);
        });
    };

    const scheduleQueuedProjectSaveDrain = () => {
        if (saveDrainTimerRef.current) return;
        saveDrainTimerRef.current = setTimeout(() => {
            saveDrainTimerRef.current = null;
            if (isSavingProjectRef.current || !pendingSaveRef.current) return;

            const queuedOverrides = pendingSaveOverridesRef.current || {};
            const ensureProjectOwner = pendingSaveEnsureProjectOwnerRef.current;
            const queuedDeferreds = pendingSaveDeferredsRef.current;
            pendingSaveRef.current = false;
            pendingSaveOverridesRef.current = null;
            pendingSaveEnsureProjectOwnerRef.current = false;
            pendingSaveDeferredsRef.current = [];

            Promise.resolve(ensureProjectOwner
                ? saveCurrentProjectRef.current?.(queuedOverrides, { ensureProjectOwner: true })
                : saveCurrentProjectRef.current?.(queuedOverrides))
                .then(result => settleQueuedProjectSaves(queuedDeferreds, null, result))
                .catch(error => settleQueuedProjectSaves(queuedDeferreds, error));
        }, 300);
    };

    // Guard: prevent auto-saving new projects with no meaningful content
    const isProjectWorthSaving = () => {
        if (currentProjectId) return true;
        if (formData?.address?.trim()) return true;
        if (formData?.notes?.trim()) return true;
        if (formData?.clientName?.trim()) return true;
        if (formData?.clientCompany?.trim()) return true;
        if (formData?.jobDescription?.trim()) return true;
        if (photos?.length > 0) return true;
        if (documents?.length > 0) return true;
        if (scope?.length > 0) return true;
        if (videos?.length > 0) return true;
        if (quoteData || analysis || projectSummary) return true;
        return false;
    };

    // ─── Optimistic-concurrency baseline ────────────────────────────────────
    // Record the server row this tab's token describes. Called from exactly the
    // two places that set baseUpdatedAtRef — project load, and a successful
    // save — and always from the SAME record, so the token and the content it
    // describes can never drift apart.
    // Absent ref is tolerated deliberately. This baseline only ever ENABLES an
    // automatic retry; without it a refused save behaves exactly as it did
    // before, which is a working product. It sits on the save and load paths,
    // so it must not be able to throw there — an optional resilience feature
    // that can break the thing it protects is worse than no feature.
    const rememberServerProjectBaseline = (projectId: unknown, record: unknown) => {
        if (!staleWriteBaselineRef) return;
        if (!projectId || typeof projectId !== 'string' || !record || typeof record !== 'object') {
            staleWriteBaselineRef.current = null;
            return;
        }
        staleWriteBaselineRef.current = {
            projectId,
            signature: serverProjectContentSignature(record),
        };
    };

    /**
     * The version token a record carries, normalised, or null when it carries
     * none we can trust. Camel FIRST per the apiFetch HARD RULE: a server-served
     * record exposes snake_case only as a NON-ENUMERABLE alias, which does not
     * survive the spread `_withProjectLifecycleAliases` returns.
     */
    const projectVersionToken = (record: unknown): string | null => {
        const raw = (record as LooseRecord)?.updatedAt || (record as LooseRecord)?.updated_at || null;
        if (!raw || typeof raw !== 'string') return null;
        const parsed = new Date(raw);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    };

    /**
     * Persist a repair the operator never asked for — a photo re-key, a video
     * File scrub, a pipelineVersion back-stamp — without poisoning this tab's
     * save token.
     *
     * All of these fire during a project OPEN, and each is a write to
     * `projects`, so migration 102's trigger advances updated_at for it. That
     * column IS the token the browser presents as expectedUpdatedAt, so an
     * unadopted background write leaves the tab holding a spent version and the
     * operator's first real save of a project they only looked at comes back
     * 409 "changed somewhere else" — one device, one tab, nobody else logged in.
     * The self-heal cannot rescue it either: these writes change CONTENT
     * (`photos` is not server-volatile), so resolveStaleWriteRetryToken
     * correctly refuses to auto-retry and the operator gets the banner.
     *
     * Two rules make adopting the returned version safe rather than merely
     * convenient:
     *
     *   1. The write is GATED on the version it descends from. An ungated POST
     *      is a blind overwrite of whatever the row has since become, and a
     *      version adopted from a blind overwrite would arm this tab to replay
     *      its stale copy over another device's work — the exact loss the token
     *      exists to prevent. A refusal is the right outcome there: the repaired
     *      values are already in state and ride the next ordinary save.
     *   2. We adopt ONLY when this tab is still on the project AND still holds
     *      exactly the version the write was gated on. Anything else means the
     *      token now describes a different row than the one we wrote.
     *
     * Returns the version the write produced — for the one caller that must
     * hand it onward because it runs BEFORE completeProjectLoad stamps the
     * token — or null when nothing durable happened. Never throws: a failed
     * repair must not be able to break a project open.
     */
    const persistLoadRepair = async (
        projectId: string,
        payload: LooseRecord,
        baseToken: string | null,
        label: string,
    ): Promise<string | null> => {
        try {
            // Serialised with every other project write in this tab, and the new
            // version is adopted BEFORE the lock is released. The foreground
            // save takes the same lock and reads its token inside it, so it
            // presents the version this repair produced instead of the one the
            // repair just spent (see project-write-lock.ts).
            return await withProjectWriteLock(async () => {
                const persisted = await saveProject(
                    baseToken ? { ...payload, expectedUpdatedAt: baseToken } : payload,
                );
                const nextToken = projectVersionToken(persisted);
                if (!nextToken) return null;
                if (baseToken
                    && currentProjectIdRef.current === projectId
                    && baseUpdatedAtRef.current === baseToken) {
                    baseUpdatedAtRef.current = nextToken;
                    // Same record, same moment — see rememberServerProjectBaseline.
                    rememberServerProjectBaseline(projectId, persisted);
                }
                return nextToken;
            });
        } catch (err) {
            console.warn(`[QM] ${label} was not persisted (non-fatal):`,
                (err as { message?: string })?.message || err);
            return null;
        }
    };

    /**
     * Decide whether a refused save may be retried, and with which token.
     *
     * Returns the server's current `updated_at` when the refusal is provably
     * NOT a content conflict, and null in every other case — including every
     * case it cannot decide. The asymmetry is the point: a wrong null costs a
     * banner the operator did not need; a wrong token overwrites work that
     * someone else did, which is the loss this guard was added to prevent.
     *
     * Four things must all hold before a retry is offered:
     *   1. the refusal really is PROJECT_STALE_WRITE, not some other 409
     *      (a dexie_id collision, a lineage conflict, a quote-number clash);
     *   2. the server told us which version it holds;
     *   3. this tab has a baseline for THIS project, so there is something to
     *      compare against;
     *   4. the row's content right now is byte-identical to that baseline.
     */
    const resolveStaleWriteRetryToken = async (
        err: unknown,
        projectId: unknown,
    ): Promise<string | null> => {
        const body = (err as LooseRecord)?.body as LooseRecord | undefined;
        if (body?.code !== 'PROJECT_STALE_WRITE') return null;
        const actual = body?.actualUpdatedAt;
        if (!actual || typeof actual !== 'string') return null;

        const baseline = staleWriteBaselineRef?.current;
        if (!baseline || !projectId || baseline.projectId !== projectId) return null;

        let fresh;
        try {
            fresh = await getProject(projectId);
        } catch (fetchErr) {
            // Cannot read the row, cannot prove anything. Fall through to the
            // operator rather than guess.
            console.warn('[QM] Could not re-read the project after a stale-write refusal:',
                (fetchErr as { message?: string })?.message || fetchErr);
            return null;
        }
        if (!fresh) return null;
        if (serverProjectContentSignature(fresh) !== baseline.signature) return null;

        const parsed = new Date(actual);
        if (Number.isNaN(parsed.getTime())) return null;
        return parsed.toISOString();
    };

    // v5.16.24: per-project dedupe for the CRM linkage log lines. saveCurrentProject
    // runs many times per session (after analysis, strategy, WMS, scope edits,
    // quote gen, etc) and each run re-resolves company + property and logs the
    // SAME outcome. Peter's console log 2026-05-04 showed the same two lines
    // firing 30+ times for one project. Track the last-logged outcome per
    // project and skip identical re-emits — keep the first emit per project so
    // troubleshooting still shows the linkage was made.
    const _crmShouldLog = (projectId: string | null, kind: string, payload: string) => {
        const key = `${projectId || 'global'}::${kind}::${payload}`;
        const last = _crmLogDedupeRef.current.get(key);
        if (last) return false;
        _crmLogDedupeRef.current.set(key, Date.now());
        // Cap the map at 256 entries; oldest evicted (covers a long session
        // touching many projects without unbounded growth).
        if (_crmLogDedupeRef.current.size > 256) {
            const firstKey = _crmLogDedupeRef.current.keys().next().value;
            _crmLogDedupeRef.current.delete(firstKey);
        }
        return true;
    };

    // Duplicate-fork guard prompt (2026-07-11 dedup investigation). Promise-
    // wrapped ConfirmModal; resolves exactly once:
    //   'reuse'     — primary key: continue in the existing project
    //   'new'       — secondary key: create a separate project anyway
    //   'dismissed' — backdrop / Escape / logout: no decision was made
    // The onDismiss leg is load-bearing — without it a dismissed dialog
    // leaves the awaiting save (and every queued save behind it) pending
    // forever because ConfirmModal routes backdrop/Escape straight to
    // onClose without touching onConfirm/onCancel.
    const promptDuplicateProjectResolution = (twin: LooseRecord) => new Promise<'reuse' | 'new' | 'dismissed'>((resolve) => {
        const quoteNo = twin.qmQuoteNo ?? twin.qm_quote_no ?? twin.quoteNumber ?? '';
        let lastSaved = '';
        const updatedAt = twin.updated_at ?? twin.updatedAt ?? null;
        if (updatedAt) {
            const parsed = new Date(updatedAt);
            if (!Number.isNaN(parsed.getTime())) lastSaved = ` (last saved ${parsed.toLocaleDateString()})`;
        }
        showConfirm({
            title: 'This looks like an existing project',
            message: `A project named "${twin.title}"${quoteNo ? ` — Quote ${quoteNo}` : ''}${lastSaved} is already in Project History. ` +
                `Continue in the existing project, or create a separate new one? ` +
                `Creating a new one adds a second entry with the same name.`,
            confirmText: 'Continue in existing',
            cancelText: 'Create new anyway',
            confirmColor: 'orange',
            icon: 'info',
            onConfirm: () => resolve('reuse'),
            onCancel: () => resolve('new'),
            onDismiss: () => resolve('dismissed'),
        });
    });

    // Implementation reads latest state directly (defined inside component scope)
    const saveCurrentProjectImpl = async (
        inputOverrides: LooseRecord = {},
        options: ProjectSaveOptions = {},
    ) => {
        const inheritedRunContext = options.runContext || getProjectSaveRunContext(inputOverrides);
        const inheritedCommitGuards = getProjectSaveCommitGuards(inputOverrides);
        const beforeCommit = options.beforeCommit || getProjectSaveBeforeCommit(inputOverrides);
        const commitGuards = options.beforeCommit
            ? normalizeProjectSaveCommitGuards([
                ...inheritedCommitGuards,
                { predicate: options.beforeCommit, protectedFields: projectSaveOverrideFields(inputOverrides) },
            ])
            : (inheritedCommitGuards.length > 0
                ? inheritedCommitGuards
                : (beforeCommit
                    ? [{ predicate: beforeCommit, protectedFields: projectSaveOverrideFields(inputOverrides) }]
                    : []));
        const saveUsesPersistenceOwner = !inheritedRunContext
            || inheritedRunContext.signal === projectPersistenceAbortRef.current?.signal;
        const saveRunContext = captureProjectRunContext(inheritedRunContext || {
            projectId: currentProjectIdRef.current ?? currentProjectId ?? null,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectPersistenceAbortRef.current?.signal || null,
        });
        let legacyProjectOwnerAdoption: { from: string; to: string } | null = null;
        const liveSaveOwnerSignal = () => saveUsesPersistenceOwner
            ? projectPersistenceAbortRef.current?.signal || null
            : projectAbortRef.current?.signal || null;
        const liveSaveOwnerProjectId = () => {
            const liveProjectId = currentProjectIdRef.current;
            return legacyProjectOwnerAdoption && liveProjectId === legacyProjectOwnerAdoption.to
                ? legacyProjectOwnerAdoption.from
                : liveProjectId;
        };
        const deferMediaStateAdoption = options.deferMediaStateAdoption === true
            || (inputOverrides as ProjectOwnedSaveOverrides)[PROJECT_SAVE_DEFER_MEDIA_ADOPTION] === true;
        const keepsNothingOnDevice = options.keepUnsavedEdits === false
            || (inputOverrides as ProjectOwnedSaveOverrides)[PROJECT_SAVE_KEEP_NOTHING_ON_DEVICE] === true;
        const overrides = withProjectSaveRunContext(inputOverrides, saveRunContext, beforeCommit, commitGuards, deferMediaStateAdoption);
        if (keepsNothingOnDevice) keepNothingOnDevice(overrides);
        const assertSaveRunCurrent = () => {
            if (!isProjectRunContextCurrent(saveRunContext, {
                projectId: liveSaveOwnerProjectId(),
                loadGeneration: projectLoadGenerationRef.current,
                signal: liveSaveOwnerSignal(),
            })) {
                throw createStaleProjectRunError('Project save');
            }
        };
        const saveRunIsCurrent = () => isProjectRunContextCurrent(saveRunContext, {
            projectId: liveSaveOwnerProjectId(),
            loadGeneration: projectLoadGenerationRef.current,
            signal: liveSaveOwnerSignal(),
        });

        assertSaveRunCurrent();
        if (!isProjectWorthSaving() && options.ensureProjectOwner !== true) {
            if (import.meta.env.DEV) console.log('[QM] Auto-save skipped - project has no meaningful content yet');
            return;
        }
        if (isSavingProjectRef.current || pendingSaveRef.current) {
            const queued = queueProjectSave(overrides, options);
            if (!isSavingProjectRef.current) scheduleQueuedProjectSaveDrain();
            return queued;
        }
        // v5.40.14 (Wave 14 fix R4): capture the project-id at SAVE-START, not at
        // catch-time. Pre-fix the catch block read currentProjectIdRef.current
        // which may have changed if the user switched projects during the
        // in-flight save → banner named the WRONG project + Retry button would
        // operate on the current project, not the one whose save failed.
        let savingProjectIdSnapshot = currentProjectIdRef.current || currentProjectId || null;
        // The job the active Quote Run is driving, captured at save start: the
        // run's own load names it, and a run that ends while this save is in
        // flight clears both refs before the catch reads them.
        const quoteRunJobAtSave = batchActiveRef.current === true && quoteRunProjectIdRef?.current != null
            ? String(quoteRunProjectIdRef.current)
            : null;
        // Capture the exact state this save attempt owns before any document or
        // media durability work can fail. The catch path uses this fingerprint
        // to stop retrying only this rejected envelope; a later edit produces a
        // different fingerprint and is allowed to save.
        const fingerprintAtSave = captureProjectFingerprint ? captureProjectFingerprint(overrides) : null;
        // The envelope this save assembled, for keeping on this device when
        // QuoteMaker's server does not take it (outage lane, 2026-09-29). Set
        // once the record exists; the catch below reads it.
        let keepableSave: {
            projectId: string;
            envelope: () => LooseRecord;
            baseToken: () => string | null;
            baselineSignature: string | null;
        } | null = null;
        // The same for a project that was never saved (review of the outage
        // lane, 2026-09-29). It has no id, so its edits are kept in the
        // new-project slot; once this save has created its server row (the
        // shell) they are kept under that id instead.
        let keepableNewProject: { envelope: () => LooseRecord } | null = null;
        // True once this save's commit guard (beforeCommit) has accepted the
        // envelope. A save that fails before the guard ran (the media step met
        // the outage) asks the guard in the catch before keeping anything.
        let commitGuardPassed = false;
        let adoptedShellId: string | null = null;
        const startedAsNewProject = !savingProjectIdSnapshot;
        const newProjectSlotHeldAtStart = startedAsNewProject && hasKeptUnsavedEdits(NEW_PROJECT_SLOT);
        // The project markup a save of project `pid` writes: an override, else
        // the markup the operator applied to that project this session (the
        // Assistant's set_project_margin, D10), else the one on the project
        // summary. Preferring the summary alone lost the operator's markup,
        // and a reopen repriced every unlocked item back (QA round 2).
        const marginForSave = (pid: unknown, existingRecord: LooseRecord | null | undefined) => {
            if (overrides.defaultMarginPercent !== undefined) return overrides.defaultMarginPercent;
            const chosen = operatorMarginRef?.current;
            if (chosen && pid !== null && pid !== undefined && String(chosen.projectId) === String(pid)) return chosen.margin;
            return existingRecord?.defaultMarginPercent ?? projectDefaultMargin;
        };
        isSavingProjectRef.current = true;
        setIsSavingProject(true);
        try {
            // let (not const): the duplicate-fork guard below may redirect an
            // id-less save into an existing record before any id is minted.
            let projectId = currentProjectIdRef.current || currentProjectId || null;
            let isNew = !projectId;
            let existing = isNew ? null : projects.find(p => p.id === projectId);
            const effectiveFormData = overrides.formData !== undefined ? overrides.formData : formData;
            const effectiveScope = overrides.scope !== undefined ? overrides.scope : scope;
            // Stage 4 and Stage 5 save as one set of items: the quote's items
            // are the Stage 4 items this save writes (stage4-stage5-mirror.ts).
            const quoteDataAtSaveStart = overrides.quoteData !== undefined ? overrides.quoteData : quoteData;
            const effectiveQuoteData = pairQuoteRowsForSave(
                quoteDataAtSaveStart,
                effectiveScope,
                {
                    quoteCurrentForScope: (quote, rows) => quoteRowsCurrentForScope(rows, quote),
                },
            ) as typeof quoteData;
            const effectiveNeedsReview = overrides.needsReview !== undefined
                ? overrides.needsReview === true
                : !!(
                    effectiveQuoteData?.meta?.readinessReview
                    || existing?.needsReview === true
                    || existing?.needs_review === true
                );
            const effectiveQuoteTitle = overrides.quoteTitle !== undefined
                ? String(overrides.quoteTitle)
                : quoteTitle;
            const effectiveTitleEditedByUser = overrides.titleEditedByUser !== undefined
                ? overrides.titleEditedByUser === true
                : titleEditedByUser;
            const summaryAiContext = existing?.aiContext || existing?.ai_context || {};
            const loadedOriginalContext = (
                loadedOriginalSnapshotContextRef.current?.projectId
                && String(loadedOriginalSnapshotContextRef.current.projectId) === String(projectId)
            ) ? loadedOriginalSnapshotContextRef.current.context : {};
            const existingAiContext = {
                ...summaryAiContext,
                ...loadedOriginalContext,
            };
            const rawEffectiveOriginalAiScope = overrides.originalAiScope !== undefined
                ? overrides.originalAiScope
                : (originalAiScope || existing?.originalAiScope || existing?.original_ai_scope || existingAiContext?.originalScope || null);
            const effectiveOriginalAiScope = stripOriginalScopePhotoLocators(rawEffectiveOriginalAiScope);
            const effectiveQuoteNote = overrides.quoteNote !== undefined ? overrides.quoteNote : quoteNote;
            const effectiveRiskAnalysis = overrides.riskAnalysis !== undefined ? overrides.riskAnalysis : analysis;
            const effectivePhotoAnalysis = overrides.photoAnalysis !== undefined ? overrides.photoAnalysis : photoAnalysis;
            const effectiveDiagnosticResult = overrides.diagnosticResult !== undefined
                ? overrides.diagnosticResult
                : (diagnosticResult ?? existingAiContext?.diagnosticResult);
            // H1: the on-demand Risk Report lives only in React state and in
            // the saved ai_context (the lean projection does not carry it), so
            // state is the carrier. Omitted when absent, so a save never sends
            // riskReport: null and erases the server copy; an explicit null
            // override still clears it.
            const effectiveRiskReport = overrides.riskReport !== undefined
                ? overrides.riskReport
                : (riskReport ?? existingAiContext?.riskReport);
            const effectiveQuoteArtifacts = overrides.quoteArtifacts !== undefined
                ? overrides.quoteArtifacts
                : existingAiContext?.quoteArtifacts;
            const effectiveRawPhotos = overrides.photos !== undefined ? overrides.photos : photos;
            // Evidence preparation persists a prepared video (its receipt, its
            // transcript row) through this save BEFORE adopting it into React
            // state, exactly as it does for photos and documents. These two
            // lanes used to read React state unconditionally, so the prepared
            // video was silently dropped from the record and the readback that
            // follows failed with PROJECT_EVIDENCE_SAVE_UNVERIFIED for every
            // project whose video was successfully processed.
            const effectiveVideos = overrides.videos !== undefined ? overrides.videos : videos;
            const effectiveVideoTranscripts = overrides.videoTranscripts !== undefined
                ? overrides.videoTranscripts
                : videoTranscripts;
            const effectiveRawSelectedHeroImage = overrides.selectedHeroImage !== undefined
                ? overrides.selectedHeroImage
                : selectedHeroImage;
            const selectedHeroStateWriteBaseline = effectiveRawSelectedHeroImage;
            const effectiveRawDocumentsSource = overrides.documents !== undefined
                ? overrides.documents
                : documents;
            const backendMode = import.meta.env?.VITE_USE_BACKEND !== 'false';
            // Canonical document write-back happens after uploads and the
            // project save have completed. Preserve both the exact lane owner
            // and a semantic snapshot from save-start so an operator add,
            // remove, or in-place metadata edit made during that async window
            // cannot be repainted with the older saved array.
            const documentsStateWriteBaseline = effectiveRawDocumentsSource;
            const documentsStateWriteBaselineSignature = (() => {
                try { return JSON.stringify(effectiveRawDocumentsSource); }
                catch { return null; }
            })();
            const effectiveRawDocuments = dedupeDocsByIdentity(effectiveRawDocumentsSource);
            const getCompletedStagesForSave = ({ photos: photosForSave = effectiveRawPhotos, scope: scopeForSave = effectiveScope, quoteData: quoteDataForSave = effectiveQuoteData } = {}) => {
                if (overrides.completedStages !== undefined) {
                    return Array.from(normalizeCompletedStages(overrides.completedStages));
                }
                return Array.from(inferCompletedStages({
                    completedStages,
                    currentStage: overrides.currentStage !== undefined ? overrides.currentStage : stage,
                    formData: effectiveFormData,
                    photos: photosForSave,
                    documents: effectiveRawDocuments,
                    videos: effectiveVideos,
                    projectSummary: overrides.projectSummary !== undefined ? overrides.projectSummary : projectSummary,
                    riskAnalysis: effectiveRiskAnalysis,
                    photoAnalysis: effectivePhotoAnalysis,
                    wms: overrides.wms !== undefined ? overrides.wms : projectWMS,
                    projectStrategy: overrides.projectStrategy !== undefined ? overrides.projectStrategy : projectStrategy,
                    scope: scopeForSave,
                    quoteData: quoteDataForSave,
                    // 5B: the saved completion must not claim Risk or Method for a route that skipped them, or the next load reads the
                    // claim back as a saved flag.
                    workflowMode: normalizeProjectWorkflowMode(overrides.workflowMode ?? projectWorkflowMode),
                }));
            };

            // Build a meaningful title. The visible bottom-nav quoteTitle is
            // canonical once it exists; do not regenerate it during saves.
            const resolvedTitle = (() => {
                // 1. User-typed title always wins
                if (effectiveTitleEditedByUser && effectiveQuoteTitle?.trim()) return effectiveQuoteTitle.trim();
                // Folder names are temporary intake labels. Once the address
                // exists, save the same quote code as an attended import.
                const importedFolderTitle = isImportedFolderTitle(effectiveQuoteTitle, {
                    ...existing,
                    projectFolder: existing?.projectFolder || quoteSubfolder,
                    formData: effectiveFormData,
                }) && String(effectiveFormData.address || '').trim();
                // 2. Preserve existing quote codes (including their original month) and custom titles.
                //    A contact's name is not a custom title (isContactNameQuoteTitle), and
                //    neither is step 4's job description (isJobDescriptionQuoteTitle).
                if (effectiveQuoteTitle?.trim() && !effectiveQuoteTitle.includes('/XXX') && !importedFolderTitle
                    && !isContactNameQuoteTitle(effectiveQuoteTitle, effectiveFormData)
                    && !isJobDescriptionQuoteTitle(effectiveQuoteTitle, effectiveFormData)) return effectiveQuoteTitle.trim();
                // 3. Auto-generated from address (only if address is real)
                const generated = generateQuoteTitle(effectiveFormData);
                if (effectiveFormData.address?.trim() && !generated.includes('/XXX')) return generated;
                // 4. Fall back to address → job description. Never the client's
                //    or Attention person's name: it names no job, and once saved
                //    it became the quote's printed title.
                if (effectiveFormData.address?.trim()) return effectiveFormData.address.trim();
                if (effectiveFormData.jobDescription?.trim()) {
                    const desc = effectiveFormData.jobDescription.trim();
                    return desc.length > 80 ? desc.substring(0, 77) + '...' : desc;
                }
                // 5. Existing title if re-saving
                if (existing?.title?.trim() && !existing.title.includes('/XXX')
                    && !isContactNameQuoteTitle(existing.title, effectiveFormData)) return existing.title.trim();
                return 'Untitled Project';
            })();

            // AI Context - stores the complete reasoning chain for learning comparison
            // When a final quote is sent and compared, the gap between aiContext and final scope
            // reveals what needs to be learned for future improvements.
            // One builder for the assembled record below and for the copy an
            // existing project keeps on this device during an outage, so the
            // kept copy carries the same diagnosis and quote artifacts the
            // record would have saved (outage lane review, 2026-09-29).
            const buildAiContextForSave = (quoteDataForSave: LooseRecord | null | undefined): LooseRecord => {
                const baseAiContext: LooseRecord = {
                    // Stage 5 staff artifacts stay in builder-only AI
                    // context; the adjacent Client Quote projection is the
                    // only member allowed to cross the client boundary.
                    ...withQuoteArtifactsInAiContext(existingAiContext, effectiveQuoteArtifacts),
                    // The diagnostic ensemble is the evidence/reasoning chain
                    // consumed by Strategy, WMS, scope seeding and quote
                    // generation. Keep it beside the other durable AI context
                    // so reload/regeneration uses the same diagnosis instead of
                    // silently degrading to a fresh or empty interpretation.
                    ...(effectiveDiagnosticResult !== undefined
                        ? { diagnosticResult: effectiveDiagnosticResult }
                        : {}),
                    // H1: the on-demand Stage 2 Risk Report. Never inside
                    // riskAnalysis, which the Work Method lineage hashes whole.
                    ...(effectiveRiskReport !== undefined
                        && (effectiveRiskReport !== null || overrides.riskReport === null)
                        ? { riskReport: effectiveRiskReport }
                        : {}),
                    // What AI originally generated (before any user edits)
                    originalScope: effectiveOriginalAiScope || existingAiContext?.originalScope || null,
                    // What learnings were active at analysis time
                    learningsUsed: existingAiContext?.learningsUsed || [],
                    // What pricing data was injected
                    pricingInjected: existingAiContext?.pricingInjected || true,
                    // The model used for analysis
                    analysisModel: existingAiContext?.analysisModel || model || null,
                    // Timestamp of analysis
                    analysisTimestamp: existingAiContext?.analysisTimestamp || (effectiveRiskAnalysis ? new Date().toISOString() : null),
                    // The final scope the user was happy with (populated when quote is sent/exported)
                    finalSentScope: existingAiContext?.finalSentScope || null,
                    // Gold standard comparison result (populated by benchmark system)
                    comparisonResult: existingAiContext?.comparisonResult || null,
                };
                if (Object.prototype.hasOwnProperty.call(overrides, 'needsReview')) {
                    delete baseAiContext.readinessReview;
                    delete baseAiContext.readiness_review;
                }
                const hasStage5Snapshot = getOriginalGeneratedSnapshots(baseAiContext)
                    .some(snapshot => Number(snapshot?.stage) === 5 && snapshot?.kind === 'quote');
                const quoteGenerationMarked = !!quoteDataForSave?.meta?.generatedAt
                    && /ai-quote-generation|stage-5-generation|auto-draft|assistant-final-quote-regenerate/i.test([
                        quoteDataForSave?.meta?.generatedBy,
                        quoteDataForSave?.meta?.generationSource,
                    ].filter(Boolean).join(' '));
                const canCaptureQuoteData = !!quoteDataForSave
                    && (
                        overrides.captureOriginalQuoteSnapshot === true
                        || (!hasStage5Snapshot && quoteGenerationMarked)
                    );
                return ensureOriginalGeneratedSnapshots(baseAiContext, {
                    projectId,
                    originalAiScope: effectiveOriginalAiScope,
                    quoteData: quoteDataForSave,
                    canCaptureQuoteData,
                    model,
                    metadata: {
                        appSurface: 'saveCurrentProject',
                        quoteTitle: resolvedTitle,
                    },
                }).aiContext;
            };

            // ─── A project never saved, while the server is not answering ───
            // (review of the outage lane, 2026-09-29.) The keep below only
            // covered a project with a server row, so every autosave round of a
            // new project minted a fresh id, read it back and, with photos or
            // documents, POSTed a project shell into the 502, kept nothing, and
            // re-armed every 2 s for the whole outage. Stop here instead:
            // nothing is minted or sent, and the catch keeps these edits in the
            // new-project slot, which the QuoteMaker page paints back after a
            // refresh. Only a project with something in it is kept: a Stage 1
            // import asks for an owner on a blank project (ensureProjectOwner
            // skips the worth-saving check above), and keeping that would bring
            // an empty project back on every blank QuoteMaker page.
            //
            // The editor state this save was asked to send, taken before any
            // media work. Also the kept envelope of an existing project until
            // its record below is assembled (see keepableSave just after).
            const editorStateEnvelope = (): LooseRecord => ({
                title: resolvedTitle,
                quoteTitle: effectiveQuoteTitle,
                titleEditedByUser: effectiveTitleEditedByUser,
                address: effectiveFormData?.address || '',
                formData: { ...effectiveFormData },
                currentStage: overrides.currentStage !== undefined ? overrides.currentStage : stage,
                completedStages: getCompletedStagesForSave(),
                workflowMode: normalizeProjectWorkflowMode(overrides.workflowMode ?? projectWorkflowMode),
                photos: effectiveRawPhotos || [],
                documents: effectiveRawDocuments || [],
                videos: effectiveVideos || [],
                videoTranscripts: effectiveVideoTranscripts || [],
                selectedHeroImage: effectiveRawSelectedHeroImage ?? null,
                scope: effectiveScope || [],
                scopeGstInclusive: overrides.scopeGstInclusive !== undefined
                    ? overrides.scopeGstInclusive
                    : scopeGstInclusive,
                quoteNote: effectiveQuoteNote,
                quoteData: effectiveQuoteData ?? null,
                riskAnalysis: effectiveRiskAnalysis ?? null,
                photoAnalysis: effectivePhotoAnalysis ?? null,
                projectSummary: overrides.projectSummary !== undefined ? overrides.projectSummary : projectSummary,
                wms: overrides.wms !== undefined ? overrides.wms : projectWMS,
                projectStrategy: overrides.projectStrategy !== undefined ? overrides.projectStrategy : projectStrategy,
                originalAiScope: effectiveOriginalAiScope ?? null,
                aiGeneratedItems,
                appliedLearnings: overrides.appliedLearnings !== undefined ? overrides.appliedLearnings : appliedLearnings,
                learningsRetrievalMode: overrides.learningsRetrievalMode !== undefined
                    ? overrides.learningsRetrievalMode
                    : learningsRetrievalMode,
                defaultMarginPercent: overrides.defaultMarginPercent !== undefined
                    ? overrides.defaultMarginPercent
                    : projectDefaultMargin,
            });
            if (isNew && isProjectWorthSaving()) {
                keepableNewProject = { envelope: editorStateEnvelope };
            }
            // ─── An existing project, before any media work ───
            // (review of the outage lane, e2e report, 2026-09-29.) The keep
            // used to start only once the record below was assembled, and the
            // one outage gate sat inside the write lock after the media step.
            // So a PDF attached during the outage sent its upload into the 502,
            // the save threw before anything was keepable, and a refresh lost
            // the edits; the next round found the upload claim 'waiting' and
            // latched the autosave. Keepable from here, with the version token
            // and baseline this editor state descends from; the assembled
            // record replaces this envelope once it exists.
            if (!isNew && projectId) {
                const keptProjectId = String(projectId);
                const existingAtStart = existing;
                const baseTokenAtStart = typeof baseUpdatedAtRef.current === 'string' ? baseUpdatedAtRef.current : null;
                const baselineAtStart = staleWriteBaselineRef?.current;
                keepableSave = {
                    projectId: keptProjectId,
                    envelope: () => ({
                        id: keptProjectId,
                        ...editorStateEnvelope(),
                        // The record's own rules for an existing project.
                        legacyBannerDismissed: overrides.legacyBannerDismissed !== undefined
                            ? overrides.legacyBannerDismissed
                            : legacyBannerDismissed,
                        workMethodOverallProgramme: overrides.workMethodOverallProgramme !== undefined
                            ? overrides.workMethodOverallProgramme
                            : workMethodOverallProgramme,
                        costingSheet: overrides.costingSheet !== undefined
                            ? overrides.costingSheet
                            : projectCostingSheet,
                        defaultMarginPercent: marginForSave(keptProjectId, existingAtStart),
                        // The record's AI context (Stage 2's diagnostic
                        // ensemble, Stage 5's quote artifacts). Without it
                        // a copy kept during an outage replaced a fuller one
                        // and a refresh reopened the project with the
                        // server's older diagnosis, or none (review of
                        // 55c46488, 2026-09-29).
                        aiContext: buildAiContextForSave(effectiveQuoteData),
                    }),
                    baseToken: () => baseTokenAtStart,
                    baselineSignature: baselineAtStart && baselineAtStart.projectId === keptProjectId
                        ? baselineAtStart.signature
                        : null,
                };
            }
            // Asked only while the server is known not to answer, so a healthy
            // save still reaches the duplicate prompt below without an await.
            // Every project, before any upload: while the server is known not
            // to answer nothing is sent (no PDF presign, no photo upload, no
            // project shell) and the catch keeps the edits on this device.
            if (backendMode && !isBackendReachable() && !(await ensureBackendAnswering())) {
                // The same checks the write lock runs before it sends. A save
                // its own commit guard refuses (a quote written from a Stage 4
                // scope the operator has since changed) throws that guard's
                // code, and the catch keeps nothing: kept, it came back on the
                // next open with a valid token and saved over the operator's
                // newer scope (review of 55c46488, 2026-09-29).
                assertSaveRunCurrent();
                await beforeCommit?.();
                commitGuardPassed = true;
                throw createServerUnavailableError({ label: 'Project save', method: 'POST' });
            }

            // ── Duplicate-project fork guard (2026-07-11 dedup investigation) ──
            // A save firing with currentProjectId === null used to mint a fresh
            // UUID unconditionally, and the address-derived title regenerates
            // identically — the mechanism that forked 23 groups / 57 excess
            // twin rows May–June 2026 (worst case: 11 records in 14 minutes
            // from a 10-second autosave loop with a lost id). Before minting,
            // check the project list (API-backed summaries in backend mode, so
            // backend-only records are visible) for an existing project with
            // the same title + quote number and let the USER decide: continue
            // in the existing project (adopt its id — this save becomes an
            // update) or create a new one anyway (legitimate re-quotes exist).
            // The answer is remembered per natural key for the session, so a
            // repeatedly-lost id can neither re-fork nor re-prompt.
            //
            // Unattended runs (batch queue) NEVER see the prompt and keep the
            // pre-guard behaviour (mint a new id): forking is recoverable via
            // the dedup cleanup; silently writing this session's state over an
            // existing record is not. We still log so a batch-mode id loss is
            // diagnosable.
            let adoptedProjectIdFromDuplicateGuard = null;
            if (isNew) {
                const guardCandidate = {
                    title: resolvedTitle,
                    quoteNumber: effectiveFormData?.orderNumber || '',
                };
                const duplicateGuardKey = buildDuplicateMatchKey(guardCandidate);
                const remembered = duplicateGuardKey ? recallDuplicateSaveDecision(duplicateGuardKey) : null;
                if (remembered?.projectId) {
                    // Asked-and-answered this session: reuse the chosen id
                    // silently ('reuse' → the existing record; 'new' → the id
                    // minted for that answer, so a re-lost currentProjectId
                    // updates the user's chosen fork instead of forking again).
                    projectId = remembered.projectId;
                    existing = projects.find(p => p.id === projectId) || null;
                    isNew = !existing;
                    adoptedProjectIdFromDuplicateGuard = projectId;
                } else if (duplicateGuardKey) {
                    const duplicates = findDuplicateProjectCandidates(projects, guardCandidate);
                    if (duplicates.length > 0) {
                        if (batchActiveRef.current || overrides.skipDuplicateProjectGuard) {
                            console.warn('[QM][duplicate-guard] Unattended save has no currentProjectId but matches existing project(s) by title+quote# — not prompting; creating a new record (recoverable) rather than overwriting.', {
                                title: resolvedTitle,
                                matchedProjectIds: duplicates.map(d => d.id),
                            });
                        } else {
                            const twin = duplicates[0];
                            const choice = await promptDuplicateProjectResolution(twin);
                            if (choice === 'reuse') {
                                rememberDuplicateSaveDecision(duplicateGuardKey, { choice: 'reuse', projectId: twin.id });
                                projectId = twin.id;
                                existing = twin;
                                isNew = false;
                                adoptedProjectIdFromDuplicateGuard = twin.id;
                            } else if (choice === 'new') {
                                projectId = crypto.randomUUID();
                                rememberDuplicateSaveDecision(duplicateGuardKey, { choice: 'new', projectId });
                            } else {
                                // Dismissed without deciding — abort THIS save
                                // attempt without forking or overwriting. The
                                // work stays in the tab (hasUnsavedChanges),
                                // and the next save re-asks. AbortError is the
                                // classified user-cancel: momentary toast, no
                                // persistent red banner.
                                const abortErr = new Error('user aborted the save at the duplicate-project prompt');
                                abortErr.name = 'AbortError';
                                throw abortErr;
                            }
                        }
                    }
                }
            }

            // Pre-UUID Dexie projects still exist in historical browser
            // caches. Blob stores intentionally reject those ids, so resolve
            // or mint the canonical Project owner before any document/photo/
            // video lane can touch IndexedDB. When the backend already knows
            // the legacy dexie_id, its UUID wins. Otherwise keep the legacy id
            // as the POST upsert key while creating one stable UUID now.
            const projectNeedsCanonicalMediaOwner = !!projectId
                && !isCanonicalProjectUuid(projectId)
                && (
                    options.ensureProjectOwner === true
                    || projectValueHasDeviceLocalMedia({
                        photos: effectiveRawPhotos,
                        documents: effectiveRawDocuments,
                        videos: effectiveVideos,
                        selectedHeroImage: effectiveRawSelectedHeroImage,
                        quoteHeroImage: effectiveQuoteData?.header?.heroImage,
                        scope: effectiveScope,
                    })
                );
            if (projectNeedsCanonicalMediaOwner) {
                const legacyProjectId = String(projectId);
                let canonicalOwner = findCanonicalProjectOwner(projects, legacyProjectId);
                if (!canonicalOwner && backendMode) {
                    const latestProjectSummaries = await DataService.projects.getAllSummaries();
                    assertSaveRunCurrent();
                    canonicalOwner = findCanonicalProjectOwner(latestProjectSummaries, legacyProjectId);
                }
                const canonicalProjectId = canonicalOwner?.id || crypto.randomUUID();
                legacyProjectOwnerAdoption = {
                    from: legacyProjectId,
                    to: String(canonicalProjectId).toLowerCase(),
                };
                projectId = legacyProjectOwnerAdoption.to;
                if (canonicalOwner) {
                    existing = canonicalOwner;
                    isNew = false;
                } else {
                    existing = existing && typeof existing === 'object'
                        ? { ...existing, id: projectId, dexieId: legacyProjectId }
                        : null;
                    isNew = true;
                }
            }
            if (!projectId) projectId = crypto.randomUUID();
            const mediaRetirementProjectId: string = projectId;

            // Capture once, before any async durability/upload work. A removal
            // queued while this save is in flight must travel only with the
            // NEXT canonical Project envelope, never this older snapshot.
            // Local-only persistence has no server retirement transaction, so
            // it must neither store nor acknowledge these transient receipts.
            const mediaRetirementReceiptSnapshot = backendMode
                ? getPendingProjectMediaRetirementReceipts(mediaRetirementProjectId).map(receipt => ({ ...receipt }))
                : [];
            const hasPhotosNeedingBackendUpload = Array.isArray(effectiveRawPhotos)
                && effectiveRawPhotos.some(p => p && typeof p === 'object'
                    && !p.s3Key
                    && (p.blobId || (typeof p.data === 'string' && p.data.startsWith('data:')) || (typeof p.dataUrl === 'string' && p.dataUrl.startsWith('data:'))));
            const hasDocumentsNeedingBackendUpload = Array.isArray(effectiveRawDocuments)
                && effectiveRawDocuments.some(documentNeedsBackendUpload);

            // S3/file_metadata has a project_id FK, so a brand-new UUID project
            // must exist before we request photo upload URLs. Without this
            // first-save bootstrap, blob-only photos save as local references
            // and reopen as "No image" from Project History.
            if (isNew && backendMode && (hasPhotosNeedingBackendUpload || hasDocumentsNeedingBackendUpload)) {
                assertSaveRunCurrent();
                const shellResponse = await saveProject({
                    id: projectId,
                    ...(legacyProjectOwnerAdoption
                        ? { dexieId: legacyProjectOwnerAdoption.from }
                        : {}),
                    title: resolvedTitle,
                    address: effectiveFormData.address || '',
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    currentStage: overrides.currentStage !== undefined ? overrides.currentStage : stage,
                    completedStages: getCompletedStagesForSave({ photos: effectiveRawPhotos, scope: effectiveScope, quoteData: effectiveQuoteData }),
                    pipelineVersion: pipelineVersionForSave(projectId),
                    workflowMode: normalizeProjectWorkflowMode(overrides.workflowMode ?? projectWorkflowMode),
                    formData: { ...effectiveFormData },
                    photos: [],
                    documents: [],
                    videos: [],
                    scope: [],
                    scopeGstInclusive: overrides.scopeGstInclusive !== undefined
                        ? overrides.scopeGstInclusive
                        : scopeGstInclusive,
                    quoteData: null,
                    status: 'draft',
                });
                // The empty project is not disposable scaffolding: it is the
                // durable parent row every upload registration now targets. If
                // a document upload fails after this point, adopt the shell
                // immediately so Retry updates the same UUID instead of minting
                // another parent and another orphaned file_metadata lineage.
                assertSaveRunCurrent();
                if (!shellResponse || typeof shellResponse !== 'object') {
                    const receiptError = new Error('Project shell save completed without a canonical server persistence receipt.') as Error & { code?: string };
                    receiptError.code = 'PROJECT_SAVE_RECEIPT_MISSING';
                    throw receiptError;
                }
                const shellId = (shellResponse as LooseRecord).id || projectId;
                const liveProjectId = currentProjectIdRef.current;
                if (liveProjectId
                    && liveProjectId !== shellId
                    && liveProjectId !== legacyProjectOwnerAdoption?.from) {
                    throw createStaleProjectRunError('Project shell adoption');
                }
                // Spec 2026-10-07 F09: this unsaved quote became this job, so
                // its Assistant chat may move into the job's conversation.
                if (!liveProjectId) reportAssistantDraftBecameProject(shellId);
                currentProjectIdRef.current = shellId;
                setCurrentProjectId(shellId);
                PipelineMonitor.setProjectId(shellId);
                projectId = shellId;
                isNew = false;
                existing = shellResponse as LooseRecord;
                savingProjectIdSnapshot = shellId;
                // The project has a server row now: from here its edits are
                // kept under its id, so the new-project copy (this project's)
                // is done with.
                adoptedShellId = String(shellId);
                if (hasKeptUnsavedEdits(NEW_PROJECT_SLOT)) void clearUnsavedProjectEdits(NEW_PROJECT_SLOT);
                baseUpdatedAtRef.current = projectVersionToken(shellResponse);
                rememberServerProjectBaseline(shellId, shellResponse);
            }

            let effectivePhotosForSave = effectiveRawPhotos || [];
            let effectiveDocumentsForSave = effectiveRawDocuments || [];
            let effectiveScopeForSave = effectiveScope;
            let effectiveQuoteDataForSave = effectiveQuoteData;
            let effectiveSelectedHeroImageForSave = effectiveRawSelectedHeroImage;
            // A local blob migration is already a durable browser commit even if
            // the later cloud project POST fails. Track the exact live photo
            // identity that may safely receive the final S3-backed projection;
            // a user edit or project switch changes the ref and blocks repaint.
            let photoStateWriteBaseline = effectiveRawPhotos;
            let documentsProvisionalAdopted = false;
            let provisionalDocumentsStateBaseline: any = null;
            const mediaPersistenceOptions = { signal: saveRunContext.signal };
            try {
                // Stage 5 keeps a compressed data URL for immediate cover-image
                // preview/cropping. That display value may be restored into
                // quoteData by the editor even though the project-photo catalog
                // already has durable media. Materialise the exact displayed
                // cover as an ordinary project photo before migration/upload so
                // every save path (navigation, autosave, AI completion and the
                // explicit editor Save) crosses the same fail-closed boundary.
                const preparedHero = await prepareQuoteHeroForProjectSave({
                    quoteData: effectiveQuoteDataForSave,
                    selectedHeroImage: effectiveSelectedHeroImageForSave,
                    photos: effectivePhotosForSave,
                });
                effectiveQuoteDataForSave = preparedHero.quoteData;
                effectiveSelectedHeroImageForSave = preparedHero.selectedHeroImage;
                effectivePhotosForSave = preparedHero.photos;
                // Persist PDFs first. They are usually RFQs/reports/drawings and
                // must not be starved by large photo/video-frame upload batches.
                effectiveDocumentsForSave = await makeDocumentArrayPortableForPersistence(effectiveDocumentsForSave, projectId);
                // Give any document that did NOT get durable S3 backing (a failed
                // upload, or a DOCX which is never uploaded) a local blob-store
                // copy + docBlobId, so the inline-byte strip below can't lose it
                // and it stays previewable after reload. Mirrors the photo rescue.
                const documentMigration = await migrateProjectDocuments(projectId, effectiveDocumentsForSave);
                effectiveDocumentsForSave = documentMigration.documents;
                // Fail closed if the local migration boundary returned an
                // un-backed byte-bearing document. Continuing would let the
                // project-record mapper strip fields such as `docBytes`, turning
                // a recoverable local-store failure into a successful
                // metadata-only cloud save. Keep the live state untouched and
                // leave the already-created parent shell available for Retry.
                const unbackedDocumentIndex = effectiveDocumentsForSave.findIndex((document: any) => (
                    document
                    && typeof document === 'object'
                    && !document.docBlobId
                    && documentNeedsBackendUpload(document)
                ));
                if (unbackedDocumentIndex >= 0) {
                    const unbackedDocument = effectiveDocumentsForSave[unbackedDocumentIndex] as any;
                    const failure = new Error(
                        `Document "${unbackedDocument?.name || unbackedDocument?.originalName || unbackedDocumentIndex + 1}" `
                        + 'could not be stored locally; source bytes were retained for Retry.',
                    ) as Error & { code?: string; retryable?: boolean };
                    // Reuse the canonical document durability code so the save
                    // retry policy latches this exact fingerprint for manual
                    // Retry instead of repeatedly exercising a failed local
                    // storage boundary in the autosave loop.
                    failure.code = 'DOCUMENT_DURABILITY_REQUIRED';
                    failure.retryable = true;
                    throw failure;
                }
                // Adopt a newly-created docBlobId before the cloud POST. The
                // local row is already durable; leaving the live document on its
                // original inline bytes makes Retry allocate another row after a
                // later network/backend failure. The functional identity guard
                // refuses to overwrite a document lane edited during migration.
                if (!deferMediaStateAdoption && documentMigration.migrated > 0 && saveRunIsCurrent()) {
                    const provisionalDocuments = effectiveDocumentsForSave;
                    provisionalDocumentsStateBaseline = provisionalDocuments;
                    setDocuments((prev: any) => {
                        if (prev !== effectiveRawDocumentsSource) return prev;
                        suppressUnsavedRef.current = true;
                        return provisionalDocuments;
                    });
                    documentsProvisionalAdopted = true;
                }
                // Move every inline photo into the browser blob store before
                // cloud sanitisation. Upload may still add S3 backing below,
                // but a failed upload must never leave the cloud boundary with
                // permission to discard the only source bytes.
                const photoMigration = await migrateProjectPhotos(projectId, effectivePhotosForSave);
                effectivePhotosForSave = photoMigration.photos;
                const unbackedLocalOriginalIndex = effectivePhotosForSave.findIndex((photo: any) => (
                    photo
                    && typeof photo === 'object'
                    && photo?.cloudMedia?.mediaClass === 'local_original'
                    && !photo.s3Key
                    && [photo.data, photo.dataUrl, photo.originalData, photo.aiMarkupData]
                        .some(value => typeof value === 'string' && value.startsWith('data:'))
                ));
                if (unbackedLocalOriginalIndex >= 0) {
                    const unbackedPhoto = effectivePhotosForSave[unbackedLocalOriginalIndex] as any;
                    const failure = new Error(
                        `Photo "${unbackedPhoto?.name || unbackedPhoto?.originalName || unbackedLocalOriginalIndex + 1}" `
                        + 'could not be stored locally; source bytes were retained for Retry.',
                    ) as Error & { code?: string; retryable?: boolean };
                    failure.code = 'PHOTO_LOCAL_ORIGINAL_DURABILITY_REQUIRED';
                    failure.retryable = true;
                    throw failure;
                }
                // Same provisional-identity rule for photos. Update both the
                // render state and its synchronous owner ref now, so a queued or
                // manual Retry sees blobId instead of re-migrating the base64.
                if (!deferMediaStateAdoption && photoMigration.migrated > 0
                    && saveRunIsCurrent()
                    && photosRef.current === effectiveRawPhotos) {
                    suppressUnsavedRef.current = true;
                    photosRef.current = effectivePhotosForSave;
                    photoStateWriteBaseline = effectivePhotosForSave;
                    setPhotos(effectivePhotosForSave);
                }
                effectivePhotosForSave = await makePhotoArrayPortableForPersistence(effectivePhotosForSave, projectId, mediaPersistenceOptions);
                const photoCatalog = buildPhotoCatalog(effectivePhotosForSave);
                effectiveScopeForSave = await makeScopeImagesPortableForPersistence(effectiveScopeForSave || [], projectId, photoCatalog, mediaPersistenceOptions);
                effectiveQuoteDataForSave = await makeQuoteDataPortableForPersistence(effectiveQuoteDataForSave, projectId, photoCatalog, mediaPersistenceOptions);
                // An explicit Stage 5 quote save owns the cover selection. Bind
                // the top-level project hero to the exact portable quote header
                // reference (or null for an explicit Remove) in this same write.
                // Other saves retain the independent project-level selection,
                // merging its matching catalog fields when possible.
                const quoteOverrideHeader = overrides.quoteData?.header;
                if (quoteOverrideHeader && typeof quoteOverrideHeader === 'object'
                    && Object.prototype.hasOwnProperty.call(quoteOverrideHeader, 'heroImage')) {
                    effectiveSelectedHeroImageForSave = effectiveQuoteDataForSave?.header?.heroImage || null;
                } else if (effectiveSelectedHeroImageForSave
                    && typeof effectiveSelectedHeroImageForSave === 'object'
                    && !Array.isArray(effectiveSelectedHeroImageForSave)) {
                    effectiveSelectedHeroImageForSave = mergePortablePhotoFields(
                        effectiveSelectedHeroImageForSave,
                        findCatalogPhoto(effectiveSelectedHeroImageForSave, photoCatalog),
                    );
                }
            } catch (mediaErr) {
                // A cancelled upload is not a storage failure. A project load,
                // a reset or STOP aborts the signal this media work runs under;
                // wrapping that as "could not be stored safely" told the
                // operator storage was broken and hid the abort from every
                // classifier that reads the error name (Quote Run, 2026-09-28).
                // The name alone is not proof: Dexie names an aborted
                // IndexedDB transaction (a full disk) AbortError too, and that
                // is a storage failure of a save nothing cancelled.
                const mediaErrCode = (mediaErr as { code?: string })?.code;
                if (mediaErrCode === 'file_upload_aborted'
                    || mediaErrCode === 'PROJECT_RUN_STALE'
                    || ((mediaErr as { name?: string })?.name === 'AbortError' && !saveRunIsCurrent())) {
                    throw mediaErr;
                }
                const failure = new Error(
                    'Project save stopped because customer media could not be stored safely. '
                    + 'Your edits and media remain on this screen; retry after storage recovers.',
                    { cause: mediaErr as Error },
                ) as Error & { code?: string; retryable?: boolean };
                failure.code = mediaErrCode || 'MEDIA_DURABILITY_REQUIRED';
                failure.retryable = true;
                throw failure;
            }

            // v5.37.22 verification-pass fix: load `pricingMatchesDismissed`
            // directly from Dexie since `getAllSummaries()` (the source of
            // the React `projects` state) does NOT include this field, so
            // `existing?.pricingMatchesDismissed` was always undefined. The
            // canonical source is the Dexie row itself. Done OUTSIDE the
            // project record builder so we await ONCE per save (not per field
            // access). For new projects, default to empty map.
            let canonicalDismissedMap = {};
            if (!isNew && projectId) {
                try {
                    // services/db only has a default export. The previous
                    // `const { db } = await import(...)` destructure pulled
                    // `undefined`, every save logged the catch as a WARNING,
                    // and `canonicalDismissedMap` stayed empty — silently
                    // clobbering any previously-dismissed pricing-match
                    // signatures on every save. Found during 2026-05-22 E2E
                    // run: 30+ warnings across one Stage 1->5 traversal.
                    const { default: db } = await import('../services/db') as any;
                    const dexieRow = await db.projects.get(projectId);
                    if (dexieRow?.pricingMatchesDismissed && typeof dexieRow.pricingMatchesDismissed === 'object') {
                        canonicalDismissedMap = dexieRow.pricingMatchesDismissed;
                    }
                } catch (loadErr) {
                    console.warn('[save] Could not load pricingMatchesDismissed from Dexie (will save with empty map):', (loadErr as { message?: string })?.message || loadErr);
                }
            }

            const projectRecord: LooseRecord = {
                id: projectId,
                ...(legacyProjectOwnerAdoption
                    ? { dexieId: legacyProjectOwnerAdoption.from }
                    : {}),
                // Optimistic-concurrency token. Only meaningful for a save
                // that UPDATES an existing row — a new project has nothing to
                // conflict with, and sending a token there would be a claim we
                // cannot support. The backend treats an absent token as "no
                // enforcement", so this stays back-compatible with every
                // non-App writer (pipeline-runner, intake, sidecars).
                //
                // Deliberately NOT overridable through `overrides`: the token
                // describes which server version this tab actually holds, and
                // no caller is in a position to assert a different one.
                ...(isNew || !projectId
                    ? {}
                    : { expectedUpdatedAt: baseUpdatedAtRef.current || undefined }),
                title: resolvedTitle,
                address: effectiveFormData.address || '',
                // Dual-read, camel first: `existing` is a summary row off the
                // projects prop, which apiFetch delivers camelCased — the old
                // snake-only read missed every time, so EVERY save of an
                // existing project re-stamped created_at to now. The server
                // column survived (updates ignore it) but the in-memory
                // summary + Dexie row corrupted until the next full refetch:
                // on the Run Sheet the opened project's age pill snapped to
                // "today" (critique pass 4, 2026-07-28). Same class-of-bug as
                // the aiContext dual-read above.
                created_at: existing?.createdAt || existing?.created_at || new Date().toISOString(),
                updated_at: new Date().toISOString(),
                // Preserve the canonical outcome column through the save's
                // summary replacement: without these, saving a project that
                // AllQuotes marked won/lost dropped quoteStatus from the
                // in-memory row and the Run Sheet lane flipped in-session.
                quoteStatus: existing?.quoteStatus ?? existing?.quote_status ?? undefined,
                quoteStatusChangedAt: existing?.quoteStatusChangedAt ?? existing?.quote_status_changed_at ?? undefined,
                // v5.50.6: same class-of-bug as quoteStatus above, for the
                // History marker. Opening an imported historical quote armed
                // the 2s autosave, and this envelope (which never carried
                // projectType) replaced the in-memory summary row — the Run
                // Sheet's History label vanished and the row fell into
                // finish/send until the next full refetch (Peter 2026-08-05).
                // The server column was never at risk: the UPDATE's SET list
                // omits project_type by design. Carried here so the summary
                // replacement, Dexie dual-write and the INSERT fork path all
                // keep the marker. `overrides` first so an explicit promotion
                // to a live project stays expressible.
                projectType: overrides.projectType !== undefined
                    ? overrides.projectType
                    : (existing?.projectType ?? existing?.project_type ?? null),

                currentStage: overrides.currentStage !== undefined ? overrides.currentStage : stage,
                completedStages: getCompletedStagesForSave({ photos: effectivePhotosForSave, scope: effectiveScopeForSave, quoteData: effectiveQuoteDataForSave }),
                // Tags every save made by the post-refactor code so the project
                // loader can distinguish genuinely-legacy projects (saved on
                // the old 4-stage flow) from post-refactor projects that just
                // haven\u2019t generated a WMS yet. Without this marker the
                // load-path heuristic misclassifies new projects as legacy.
                // A brain project stays one (Quote Brain DESIGN.md 2.11): the
                // version comes from the loaded record, never a constant.
                pipelineVersion: pipelineVersionForSave(projectId),
                workflowMode: normalizeProjectWorkflowMode(overrides.workflowMode ?? projectWorkflowMode),
                // Phase 8: persist the builder\u2019s "dismiss the upgrade banner"
                // choice so it survives reload. Override-driven override path
                // takes precedence if the caller passed a value explicitly.
                legacyBannerDismissed: overrides.legacyBannerDismissed !== undefined
                    ? overrides.legacyBannerDismissed
                    : legacyBannerDismissed,

                // Stage 1: Data Entry
                formData: { ...effectiveFormData },
                // Media persistence repair: prefer durable S3-backed photo refs,
                // preserve video-frame data, and mark any remaining local-only
                // blob refs instead of silently treating them as portable.
                photos: effectivePhotosForSave,
                // Strip inline PDF bytes from documents before save.
                //
                // v5.41.3 (2026-05-24 hot-fix): tightened from "strip only when
                // S3 backup exists" to "strip whenever pdfDataUrl is a data:
                // URL OR a durable backup exists". Customer Zero hit 413
                // Payload Too Large because failed S3 uploads (auth / rate-
                // limit / network) left multi-MB base64 PDFs inline →
                // `documents` JSONB ballooned past the 50 MB backend limit.
                // Stage2RiskScan.jsx:352 already re-fetches PDF previews via
                // pdfS3Key/fileMetadataId on reload, and `d.content` keeps the
                // extracted text the AI needs; blob: URLs are tiny and
                // session-local so they stay. See also the last-mile guard in
                // data-service-api.js projects.save which is the catch-all.
                documents: (effectiveDocumentsForSave || []).map((d: any) => {
                    if (!d || typeof d !== 'object') return d;
                    // Byte-loss-free strip. migrateProjectDocuments (above) has
                    // already given every un-backed inline doc a `docBlobId`, so
                    // by here inline bytes are safe to drop whenever there is
                    // durable backing (docBlobId / pdfS3Key / fileMetadataId) —
                    // they're rebuilt on load from that backing. A `blob:` URL is
                    // always session-local (dead on reload) so it's dropped too.
                    // Only if a doc somehow reaches here with inline base64 and NO
                    // durable backing (migration failed) do we KEEP the bytes,
                    // rather than lose them; the last-mile guard + oversized
                    // rescue bound the payload size.
                    const durable = d.docBlobId || d.pdfS3Key || d.fileMetadataId;
                    const isInlineBase64 = typeof d.pdfDataUrl === 'string'
                        && d.pdfDataUrl.startsWith('data:')
                        && d.pdfDataUrl.includes(';base64,');
                    const isObjectUrl = typeof d.pdfDataUrl === 'string' && d.pdfDataUrl.startsWith('blob:');
                    let out = d;
                    if (d.docBytes !== undefined) out = { ...out, docBytes: undefined };
                    if (isObjectUrl || (isInlineBase64 && durable)) out = { ...out, pdfDataUrl: undefined };
                    return out;
                }),
                // Keep local video bytes until the API boundary has either
                // migrated them to videoBlobId or refused the save. That
                // boundary strips them from the actual cloud JSON projection.
                videos: effectiveVideos,
                videoTranscripts: effectiveVideoTranscripts,
                // Auto photo selection resolves a display image in React but
                // persists its bounded photo reference through this same awaited save.
                // Honour that narrow override without changing manual/state
                // authority for ordinary saves.
                selectedHeroImage: effectiveSelectedHeroImageForSave,

                // Stage 1.5: Project Summary (best-guess pre-analysis pass)
                // Allow override so runProjectSummary can persist immediately on completion
                projectSummary: overrides.projectSummary !== undefined ? overrides.projectSummary : projectSummary,

                // Stage 2: Risk Scan
                riskAnalysis: effectiveRiskAnalysis,
                photoAnalysis: effectivePhotoAnalysis,

                // Stage 4: Scope Builder
                scopeGstInclusive: overrides.scopeGstInclusive !== undefined
                    ? overrides.scopeGstInclusive
                    : scopeGstInclusive,
                scope: effectiveScopeForSave,
                quoteNote: effectiveQuoteNote,
                aiGeneratedItems: aiGeneratedItems,
                // Stage 2.5: Work Method Statement - project-level programme.
                // Per-item workMethod fields already ride along inside `scope`
                // above. This top-level entry captures the cross-item
                // programme narrative produced by the WMS prompt's
                // overall_programme block.
                workMethodOverallProgramme: overrides.workMethodOverallProgramme !== undefined
                    ? overrides.workMethodOverallProgramme
                    : workMethodOverallProgramme,
                // Method-first Phase 2: persist the Strategy result so alternatives
                // + chosen approach + rejected rationales survive save/reload.
                // Phase 7 adds a dedicated Dexie v20 index; for now it rides on
                // the project envelope.
                projectStrategy: overrides.projectStrategy !== undefined
                    ? overrides.projectStrategy
                    : projectStrategy,
                // Method-first Phase 14: project-level Work Method Statement
                // document (builder\u2019s execution plan + contractor brief).
                // Rides on the envelope so reload / cloud restore preserves it.
                wms: overrides.wms !== undefined
                    ? overrides.wms
                    : projectWMS,
                // v5.37.21 audit fix (schema-drift FINDING #3): Dexie `put()`
                // replaces the entire row, so omitting fields from this builder
                // WIPES them. `pricingMatchesDismissed` was being clobbered on
                // every Stage 1 save \u2014 user dismissed a Pricing Matches row in
                // Stage 5, then any subsequent save erased the map \u2192 dismissed
                // rows resurrected on next Stage 5 open.
                //
                // v5.37.22 verification-pass fix: the initial v5.37.21 attempt
                // read from `existing` (React state), but `getAllSummaries()`
                // at data-service.js:92-114 doesn't include this field \u2014 so
                // `existing.pricingMatchesDismissed` was always undefined and
                // the fallback `|| {}` wiped the field anyway. Caught while
                // writing the verification test. Now uses
                // `canonicalDismissedMap` (loaded from Dexie directly above)
                // which IS the authoritative source.
                pricingMatchesDismissed: overrides.pricingMatchesDismissed !== undefined
                    ? overrides.pricingMatchesDismissed
                    : canonicalDismissedMap,
                // Method-first Phase 16: admin Costing Sheet \u2014 cost-focused
                // sibling of the WMS. Rides on the envelope. Admin-entered
                // actuals preserved across regens.
                costingSheet: overrides.costingSheet !== undefined
                    ? overrides.costingSheet
                    : projectCostingSheet,
                // M2 polish: attribution trail from semantic-retrieval triage.
                // Each entry is a frozen createAppliedLearning object:
                // { id, section, rationale, strength, appliedBy, appliedAt, contradicts?, contradictReason? }.
                // Surfaces in the "Why this price / Why this wording" UI.
                appliedLearnings: overrides.appliedLearnings !== undefined
                    ? overrides.appliedLearnings
                    : appliedLearnings,
                // Which retrieval path produced the current attributions - lets
                // the UI show "semantic" vs "legacy" without running the math again.
                learningsRetrievalMode: overrides.learningsRetrievalMode !== undefined
                    ? overrides.learningsRetrievalMode
                    : learningsRetrievalMode,
                originalAiScope: effectiveOriginalAiScope,
                // Per-project default margin - used as the initial value of the
                // Scope Builder (Stage 4) margin slider. Preserves existing value on re-save so
                // legacy projects that were created before this field existed
                // keep whatever the user has set this session. For BRAND NEW
                // projects we default to 15% in the state initializer below.
                defaultMarginPercent: marginForSave(projectId, existing),

                // AI Context: the reasoning chain for learning comparison
                // (see buildAiContextForSave above).
                aiContext: buildAiContextForSave(effectiveQuoteDataForSave),

                // Stage 5: Final Quote
                quoteData: effectiveQuoteDataForSave,
                quoteNumber: effectiveFormData?.orderNumber || existing?.quoteNumber || '',
                // Migration 070 / v5.20: QM-issued Quote Number. Preserved on
                // every save so the column survives Stage-4-only writes that
                // don't touch quoteData. Allocation happens out-of-band via
                // POST /api/projects/:id/allocate-qm-quote-no on first email
                // send; this save path just round-trips the value. Reads
                // from both forms because apiFetch camelCases the column.
                // An explicit Stage 5 save may follow the focused Quote # PATCH.
                // Its live quote snapshot must win over the stale project-summary
                // row or this full save can silently restore the prior number.
                qmQuoteNo: effectiveQuoteDataForSave?.meta?.qmQuoteNo
                    ?? existing?.qmQuoteNo
                    ?? existing?.qm_quote_no
                    ?? null,

                // Title metadata - prevents auto-regeneration from overwriting user edits
                titleEditedByUser: effectiveTitleEditedByUser,
                // Stage 5 review state is derived from the sole canonical
                // receipt in quoteData.meta. Keep the local/offline row
                // explicit while the backend projects it from that receipt.
                needsReview: effectiveNeedsReview,

                // Tracking
                status: overrides.status !== undefined
                    ? overrides.status
                    : (existing?.status || (effectiveQuoteDataForSave ? 'quoted' : 'draft')),
                pdfGenerated: existing?.pdfGenerated || false,
                pdfGeneratedAt: existing?.pdfGeneratedAt || null,
                emailSent: existing?.emailSent || false,
                emailSentAt: existing?.emailSentAt || null,

                // File system
                projectFolder: parentFolder ? {
                    // A clean Folder 01 + 02 restart preserves the prior backend
                    // attempt for audit. Keep the restart lineage inside the
                    // persisted project_folder JSONB on every later save so a
                    // fresh browser can still identify the newest source-bound
                    // attempt without collapsing it into the old quote.
                    ...(existing?.projectFolder?.restartOfProjectId ? {
                        restartOfProjectId: existing.projectFolder.restartOfProjectId,
                        intakeAttemptKind: existing.projectFolder.intakeAttemptKind || 'restart-from-source',
                        intakeRestartedAt: existing.projectFolder.intakeRestartedAt || null,
                    } : {}),
                    parentName: parentFolder.name,
                    subfolderName: quoteSubfolder?.name || null,
                    folderName: quoteSubfolder?.folderName || quoteSubfolder?.name || null,
                    relativePath: quoteSubfolder?.relativePath ?? quoteSubfolder?.syncRelativePath ?? quoteSubfolder?.path ?? null,
                    sourcePath: quoteSubfolder?.sourcePath || quoteSubfolder?.path || quoteSubfolder?.relativePath || null,
                    files: folderFiles
                } : (existing?.projectFolder || null),

                // KPI (only calculate when quoteData exists)
                kpiReport: effectiveQuoteDataForSave ? (() => {
                    try {
                        return calculateComprehensiveKPI({
                            formData: effectiveFormData, photos: effectivePhotosForSave, videos: effectiveVideos || [],
                            docs: documents, riskAnalysis: effectiveRiskAnalysis, quoteData: effectiveQuoteDataForSave
                        });
                    } catch (e) { return null; }
                })() : null
            };

            // CRM identity is an operator decision. Saving/reprocessing never
            // creates, renames or fuzzy-reassigns clients, contacts or sites.
            // Keep links even while offline; the API UPDATE also preserves its
            // authoritative links with COALESCE against stale browser envelopes.
            // The explicit client-matching workflow uses the narrow crm-link API.
            // Not read while the server is known not to answer: the gate below
            // sends nothing this round, and the summary row is the owner the
            // API's COALESCE falls back to anyway (outage lane review).
            const storedCrmRow = (isBackendReachable()
                ? await getProject(projectId).catch(() => null)
                : null) as LooseRecord | null;
            const crmOwner = storedCrmRow || existing;
            projectRecord.company_id = crmOwner?.company_id ?? crmOwner?.companyId ?? null;
            projectRecord.property_id = crmOwner?.property_id ?? crmOwner?.propertyId ?? null;

            // CRITICAL 2026-04-19 fix: the API saver (data-service-api.js
            // `projects.save`) gates PUT vs POST on isUUID(project.id). Our
            // client-generated id `proj_<timestamp>` fails that regex, so
            // every save was POST-ing a NEW record instead of UPDATE-ing the
            // existing one. Each save spawned a duplicate on the backend;
            // Peter observed 106 rows for ~10 real projects (roughly 10x).
            // Fix: capture the server's response, adopt the server-assigned
            // UUID as the authoritative id, and use IT for all downstream
            // wiring (currentProjectId, PipelineMonitor, projects list,
            // SyncFolder) so subsequent saves PUT rather than POST.
            // ─── The write, with ONE self-healing retry ──────────────────────
            // A PROJECT_STALE_WRITE 409 says the row moved since this tab read
            // it. That is usually somebody else's edit and must reach the
            // operator as a decision. But it is also what a purely BOOKKEEPING
            // write produces — a cost rollup, a server-minted quote number —
            // and there is no decision to make about those.
            //
            // resolveStaleWriteRetryToken tells the two apart by comparing the
            // server's content now against the content this tab last saw, and
            // returns a token ONLY when they are identical. Anything it cannot
            // prove identical falls through to the banner, because a wrong
            // "identical" would silently overwrite somebody's work.
            //
            // Exactly one retry: the second attempt rethrows. A refusal we
            // cannot explain must never become a loop against the backend.
            const projectSaveRequest = () => mediaRetirementReceiptSnapshot.length > 0
                ? { ...projectRecord, mediaRetirementReceipts: mediaRetirementReceiptSnapshot }
                : projectRecord;
            if (!isNew && projectRecord.id) {
                const baselineAtAssembly = staleWriteBaselineRef?.current;
                keepableSave = {
                    projectId: String(projectRecord.id),
                    envelope: () => projectRecord,
                    // The token the write presents is read inside the lock
                    // below; fall back to the assembly-time one if it never ran.
                    baseToken: () => (typeof projectRecord.expectedUpdatedAt === 'string'
                        ? projectRecord.expectedUpdatedAt
                        : null),
                    baselineSignature: baselineAtAssembly && baselineAtAssembly.projectId === String(projectRecord.id)
                        ? baselineAtAssembly.signature
                        : null,
                };
            }
            // The record above was stamped with its version token when it was
            // ASSEMBLED, and assembling it can take minutes (media is made
            // portable first, which on the cloud means uploading to S3). A
            // background load repair that lands in that window moves the server
            // version AND adopts it into baseUpdatedAtRef, leaving this record
            // holding a spent token: production refused every queued project's
            // Stage 1 checkpoint this way (see project-write-lock.ts).
            //
            // So the token is read at the last moment, inside the same lock the
            // repairs take, and the version this write produces is adopted
            // before the lock is released. baseUpdatedAtRef only ever advances
            // through THIS tab's own writes and adoptions, whose content is
            // already in state, and assertSaveRunCurrent() has just proved the
            // tab is still on this project and load generation. Another
            // device's change never adopts silently, so it still arrives here
            // as a refusal and reaches the operator.
            const savedResponse = await withProjectWriteLock(async () => {
                let response;
                try {
                    assertSaveRunCurrent();
                    // While QuoteMaker's server is known not to answer, send
                    // nothing: the catch keeps this envelope on this device and
                    // the one banner says so. During the 2026-09-29 deploy every
                    // retry re-sent the whole project into a 502. One shared
                    // /api/health probe decides; free while the server answers.
                    // Before the token read: nothing may await between reading
                    // the token and sending it.
                    if (!(await ensureBackendAnswering())) {
                        throw createServerUnavailableError({ label: 'Project save', method: 'POST' });
                    }
                    // The health check above can be a probe another caller started, and it can settle much later than anything before it.
                    // An edit, a project switch or a reload made while it was pending happened BEFORE this send. So the owner is asserted
                    // again after it, and the one commit guard runs here, after the last await that precedes the token read, not before
                    // the gate. A guard may itself be asynchronous: the owner is asserted once more after it. Nothing is awaited between
                    // that last assertion, the token read and the send. (If the server is away, the gate throws first and the catch below
                    // asks the guard whether the envelope may be kept on this device: it has not run, commitGuardPassed is still false.)
                    assertSaveRunCurrent();
                    await beforeCommit?.();
                    assertSaveRunCurrent();
                    commitGuardPassed = true;
                    if (Object.prototype.hasOwnProperty.call(projectRecord, 'expectedUpdatedAt')) {
                        projectRecord.expectedUpdatedAt = baseUpdatedAtRef.current || undefined;
                    }
                    response = await saveProject(projectSaveRequest());
                } catch (writeErr) {
                    const retryToken = await resolveStaleWriteRetryToken(writeErr, projectRecord.id);
                    if (!retryToken) throw writeErr;
                    assertSaveRunCurrent();
                    await beforeCommit?.();
                    // The retry is its own attempt: its guard may be asynchronous, so the owner is asserted once more after it, exactly as
                    // the first attempt does, before the token is replaced and the project is sent.
                    assertSaveRunCurrent();
                    console.info('[QM] Save was refused as stale, but the project content on the server is '
                        + 'unchanged — only bookkeeping moved. Re-syncing the version and saving once more.');
                    baseUpdatedAtRef.current = retryToken;
                    projectRecord.expectedUpdatedAt = retryToken;
                    response = await saveProject(projectSaveRequest());
                }
                const producedToken = projectVersionToken(response);
                if (producedToken && saveRunIsCurrent()) baseUpdatedAtRef.current = producedToken;
                return response;
            });
            const serverAssignedId = (savedResponse && typeof savedResponse === 'object' && savedResponse.id)
                ? savedResponse.id
                : null;
            const authoritativeId = serverAssignedId || projectRecord.id;
            if (!savedResponse || typeof savedResponse !== 'object') {
                const receiptError = new Error('Project save completed without a canonical server persistence receipt.') as Error & { code?: string };
                receiptError.code = 'PROJECT_SAVE_RECEIPT_MISSING';
                throw receiptError;
            }
            if (mediaRetirementReceiptSnapshot.length > 0
                && (!serverAssignedId
                    || String(serverAssignedId).toLowerCase() !== mediaRetirementProjectId.toLowerCase())) {
                const receiptError = new Error(
                    'Project save did not return a matching canonical receipt for pending media retirement.',
                ) as Error & { code?: string };
                receiptError.code = 'PROJECT_SAVE_RECEIPT_MISSING';
                throw receiptError;
            }
            if (serverAssignedId && serverAssignedId !== projectRecord.id) {
                projectRecord.id = serverAssignedId;
            }
            const canonicalSavedProject: LooseRecord = stripProjectMediaRetirementTransientFields({
                ...(savedResponse as LooseRecord),
                id: authoritativeId,
            });
            const mediaRetirementOutcome = (savedResponse as LooseRecord).mediaRetirementOutcome
                ?? (savedResponse as LooseRecord).media_retirement_outcome
                ?? null;
            if (saveRunIsCurrent()) {
                loadedOriginalSnapshotContextRef.current = {
                    projectId: String(authoritativeId),
                    context: getOriginalSnapshotPersistenceContext(canonicalSavedProject),
                };
            }
            // These are request-scoped transaction commands, never Project
            // JSON. Defensively strip either alias even if an intermediary
            // were ever to echo the request instead of returning the DB row.
            if (mediaRetirementReceiptSnapshot.length > 0
                && serverAssignedId
                && String(serverAssignedId).toLowerCase() === mediaRetirementProjectId.toLowerCase()) {
                const confirmedRetirements = confirmedProjectMediaRetirementReceipts(
                    mediaRetirementOutcome,
                    mediaRetirementReceiptSnapshot,
                );
                acknowledgeProjectMediaRetirementReceipts(
                    mediaRetirementProjectId,
                    confirmedRetirements,
                );
            }

            // Advance the optimistic-concurrency token to the version this save
            // just produced, so the NEXT save from this tab compares against
            // the right row. Without this every save after the first would
            // present a spent token and 409 against our own write.
            //
            // Dual-read per the camelCase HARD RULE: apiFetch camelises top
            // level (`updatedAt`), while a Dexie-shaped record keeps
            // `updated_at`. Falling back to null (rather than the local clock)
            // is deliberate — an unreadable response means we do not know the
            // server version, and "do not enforce" is safer than asserting a
            // value the server never issued.
            const savedUpdatedAt = (savedResponse && typeof savedResponse === 'object')
                ? ((savedResponse as LooseRecord).updatedAt || (savedResponse as LooseRecord).updated_at || null)
                : null;
            const savedUpdatedAtDate = savedUpdatedAt ? new Date(savedUpdatedAt as string) : null;
            const canonicalSavedUpdatedAt = savedUpdatedAtDate && !Number.isNaN(savedUpdatedAtDate.getTime())
                ? savedUpdatedAtDate.toISOString()
                : null;
            if (saveRunIsCurrent()) {
                baseUpdatedAtRef.current = canonicalSavedUpdatedAt;
                // The row the server just acknowledged IS what it now holds, so it
                // is the baseline a later refusal gets compared against.
                rememberServerProjectBaseline(authoritativeId, canonicalSavedProject);
                // The row can hold a descriptive title where this tab only has the
                // placeholder a blank address generates. Take it when the tab still
                // shows exactly the title this save sent (an edit made while the save
                // was in flight is never overwritten); a title the operator wrote or
                // any real title already showing is left alone (adoptCanonicalQuoteTitle).
                const adoptedTitle = adoptCanonicalQuoteTitle({
                    sentTitle: effectiveQuoteTitle,
                    titleEditedByUser: effectiveTitleEditedByUser,
                    canonicalTitle: canonicalSavedProject.title,
                    formData: effectiveFormData,
                });
                if (adoptedTitle) {
                    setQuoteTitle((previous: unknown) => (previous === effectiveQuoteTitle ? adoptedTitle : previous));
                }
            }

            // Cloud projection v2 is a post-save mirror of the canonical
            // project, never a second source of truth. It stages immutable JSON
            // members and advances one server-side manifest CAS; failures are
            // non-fatal to the already-completed project save and never mark a
            // partial generation as current.
            if (serverAssignedId && canonicalSavedUpdatedAt) {
                // The save response is the canonical row after backend merge
                // rules (including lifecycle/PDF/email fields). Local
                // projectRecord is only a fallback for fields an older server
                // omitted; response values must win or the mirror could
                // certify a stale pre-save lifecycle snapshot under the fresh
                // server updated_at token.
                const canonicalProjectionProject: LooseRecord = {
                    ...projectRecord,
                    ...canonicalSavedProject,
                    id: authoritativeId,
                };
                // apiFetch camelises the authoritative server field, while
                // projectRecord already carries a pre-save local snake alias.
                // Stamp BOTH aliases from the validated server timestamp so
                // the projector cannot pair stale local JSON with a fresh CAS
                // source receipt.
                canonicalProjectionProject.updatedAt = canonicalSavedUpdatedAt;
                canonicalProjectionProject.updated_at = canonicalSavedUpdatedAt;
                void import('../services/s3-sync').then(({ queueProjectSyncToS3 }) =>
                    queueProjectSyncToS3(authoritativeId, '', {
                        project: canonicalProjectionProject,
                        sourceUpdatedAt: canonicalSavedUpdatedAt,
                    })
                ).then(reportCloudProjectionResult).catch(reportCloudProjectionFailure);
            }

            // The selected customer folder is a read-only evidence source during
            // ordinary project saves. QuoteMaker writes there only after an
            // explicit user action (for example Save risk report, Save quote, or
            // final PDF export). Keeping this boundary here prevents autosave and
            // background pipeline transitions from creating local-sync conflicts.

            // v5.47.37 stale-save guard on the id write-back. This branch was the
            // ONE unguarded member of the family: the `else if` below, the three
            // `savedProjectStillCurrent` write-backs and the document repaint all
            // check whether the saved project is still the one on screen, and
            // this did not — even though it runs after `await saveProject(...)`
            // and the S3 work inside the portable-for-persistence pass, so it can
            // resolve seconds late.
            //
            // The failure it caused: a first save of an id-less project (fresh
            // import, or the CRM prefill path which sets currentProjectId=null)
            // fires fire-and-forget as the operator leaves QuoteMaker. While it
            // is in flight they open a different project, so completeProjectLoad
            // sets currentProjectId + formData + scope + quoteData + quoteTitle to
            // project B. Then this line hijacked currentProjectId back to the
            // in-flight record N. State now reads "id = N, everything on screen =
            // B", and because resolvedTitle rule 2 returns the live navbar title
            // unconditionally, EVERY later save — the 2s autosave included, no
            // typing required — wrote B's title and body onto N. That is a quote
            // being renamed without the operator touching it, which is the other
            // half of the 2026-07-30 report.
            //
            // Adopt only while the session is still id-less, exactly as the
            // duplicate-guard sibling does. If the operator has moved on, record N
            // is still saved correctly under its own id and shows up in the list;
            // we simply stop steering this session's writes at it.
            //
            // Compressed-only video retry: a worker-created 720p proxy needs a
            // project id for the protected project_video_proxy lane. When an
            // id-less import receives (or adopts) its durable id, upload only
            // those proxy bytes; the camera original remains local.
            // Fires on id adoption and successful ordinary saves. The retry
            // helper ignores in-flight/confirmed proxies and honours bounded
            // backoff, so autosave can recover failures without duplicate
            // uploads. The
            // closure `videos` is the same snapshot this save just persisted
            // (the sync-folder pass above reads it too). The s3Key stamp is
            // non-user-visible metadata, so the wrapped setter suppresses the
            // dirty tracker exactly like the syncFileName stamps above.
            const kickPendingVideoCloudUploads = (adoptedId: string) => {
                if (deferMediaStateAdoption) return;
                import('../services/video-cloud-upload')
                    .then(({ retryPendingVideoCloudUploads }) => {
                        if (!saveRunIsCurrent()) return;
                        // Do not suppress this setter. Upload completion retires
                        // inline proxy bytes and adds the durable cloud pointer;
                        // the normal dirty/autosave path must persist that swap.
                        const setVideosIfOwned: AnySetter = (next: any) => {
                            if (!saveRunIsCurrent() || currentProjectIdRef.current !== adoptedId) return;
                            setVideos(next);
                        };
                        const kicked = retryPendingVideoCloudUploads(effectiveVideos, adoptedId, setVideosIfOwned, {
                            signal: saveRunContext.signal,
                        });
                        if (kicked > 0) {
                            console.info(`[QM] Started compressed proxy upload for ${kicked} pre-save video(s) now that project ${adoptedId} has an id.`);
                        }
                    })
                    .catch(() => { /* durability retry only — never block or fail the save */ });
            };
            if (legacyProjectOwnerAdoption
                && saveRunIsCurrent()
                && currentProjectIdRef.current === legacyProjectOwnerAdoption.from) {
                currentProjectIdRef.current = authoritativeId;
                setCurrentProjectId(authoritativeId);
                PipelineMonitor.setProjectId(authoritativeId);
                kickPendingVideoCloudUploads(authoritativeId);
            } else if (isNew && saveRunIsCurrent() && !currentProjectIdRef.current) {
                reportAssistantDraftBecameProject(authoritativeId);
                currentProjectIdRef.current = authoritativeId;
                setCurrentProjectId(authoritativeId);
                PipelineMonitor.setProjectId(authoritativeId);
                kickPendingVideoCloudUploads(authoritativeId);
            } else if (isNew) {
                console.warn(
                    `[QM] Not adopting freshly-minted project ${authoritativeId}: the operator opened ${currentProjectIdRef.current} while this save was in flight. The record is saved; this session stays on the open project.`,
                );
            } else if (adoptedProjectIdFromDuplicateGuard && saveRunIsCurrent() && !currentProjectIdRef.current) {
                // The duplicate-fork guard redirected this id-less save into an
                // existing record — adopt that project as the session's current
                // one so subsequent saves, photo-ownership guards, and the
                // pipeline monitor all key on it. Skip if the user opened a
                // different project while this save was in flight. The draft
                // was saved into this job, so its Assistant chat goes with it.
                reportAssistantDraftBecameProject(authoritativeId);
                currentProjectIdRef.current = authoritativeId;
                setCurrentProjectId(authoritativeId);
                PipelineMonitor.setProjectId(authoritativeId);
                kickPendingVideoCloudUploads(authoritativeId);
            }

            const persistenceValueChanged = (before: any, after: any) => {
                if (before === after) return false;
                try { return JSON.stringify(before) !== JSON.stringify(after); }
                catch { return true; }
            };
            const scopeItemReceiptIdentity = (item: any) => {
                if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
                for (const key of ['id', 'scopeId', 'scope_id', 'code', 'scopeCode', 'scope_code']) {
                    const value = item[key];
                    if (value !== null && value !== undefined && String(value).trim()) {
                        return `${key.replace(/_/g, '').toLowerCase()}:${String(value)}`;
                    }
                }
                return null;
            };
            const reconcileCanonicalScopeMediaReceipt = (
                liveScope: any,
                savedStartScope: any,
                canonicalScope: any,
            ) => {
                if (!Array.isArray(liveScope)
                    || !Array.isArray(savedStartScope)
                    || !Array.isArray(canonicalScope)
                    || liveScope.length !== savedStartScope.length
                    || liveScope.length !== canonicalScope.length) {
                    return liveScope;
                }
                let changed = false;
                const reconciled = liveScope.map((liveItem: any, index: number) => {
                    const savedStartItem = savedStartScope[index];
                    const canonicalItem = canonicalScope[index];
                    const identity = scopeItemReceiptIdentity(liveItem);
                    if (!identity
                        || identity !== scopeItemReceiptIdentity(savedStartItem)
                        || identity !== scopeItemReceiptIdentity(canonicalItem)) {
                        return liveItem;
                    }
                    const liveImages = liveItem?.images;
                    const savedStartImages = savedStartItem?.images;
                    const canonicalImages = canonicalItem?.images;
                    if (!Array.isArray(liveImages)
                        || !Array.isArray(savedStartImages)
                        || !Array.isArray(canonicalImages)
                        || persistenceValueChanged(liveImages, savedStartImages)
                        || !persistenceValueChanged(liveImages, canonicalImages)) {
                        return liveItem;
                    }
                    changed = true;
                    return { ...liveItem, images: canonicalImages };
                });
                return changed ? reconciled : liveScope;
            };
            const savedProjectStillCurrent = saveRunIsCurrent() && (
                currentProjectIdRef.current === projectId
                || currentProjectIdRef.current === authoritativeId
            );
            const canonicalScopeForState = Array.isArray(canonicalSavedProject.scope)
                ? canonicalSavedProject.scope
                : effectiveScopeForSave;
            const canonicalQuoteDataForState = canonicalSavedProject.quoteData
                || canonicalSavedProject.quote_data
                || effectiveQuoteDataForSave;
            const canonicalSelectedHeroForState = Object.prototype.hasOwnProperty.call(canonicalSavedProject, 'selectedHeroImage')
                || Object.prototype.hasOwnProperty.call(canonicalSavedProject, 'selected_hero_image')
                ? (canonicalSavedProject.selectedHeroImage ?? canonicalSavedProject.selected_hero_image ?? null)
                : effectiveSelectedHeroImageForSave;

            if (!isNew && savedProjectStillCurrent) {
                // A failed proxy keeps its local bytes. Every successful
                // ordinary save is a safe retry checkpoint; the helper itself
                // enforces in-flight dedupe, retry limits, and retryAfter.
                kickPendingVideoCloudUploads(authoritativeId);
            }

            if (!deferMediaStateAdoption && savedProjectStillCurrent && Array.isArray((savedResponse as LooseRecord)?.videos)) {
                import('../services/video-cloud-upload')
                    .then(async ({ retireAcknowledgedVideoProxies }) => {
                        if (!saveRunIsCurrent()
                            || (currentProjectIdRef.current !== projectId
                            && currentProjectIdRef.current !== authoritativeId)) return;
                        const videoStateBaseline = videosRef.current;
                        const ownedMerge = await mergeProjectSaveResultForDexieOwned(
                            { id: authoritativeId, videos: videoStateBaseline },
                            { id: authoritativeId, videos: (savedResponse as LooseRecord).videos },
                        );
                        if (!saveRunIsCurrent()
                            || (currentProjectIdRef.current !== projectId
                            && currentProjectIdRef.current !== authoritativeId)
                            || videosRef.current !== videoStateBaseline) return;
                        const reconciled = retireAcknowledgedVideoProxies(
                            ownedMerge.videos,
                            (savedResponse as LooseRecord).videos,
                        );
                        if (!persistenceValueChanged(videoStateBaseline, reconciled)) return;
                        suppressUnsavedRef.current = true;
                        videosRef.current = reconciled;
                        setVideos(reconciled);
                    })
                    .catch(() => { /* local recovery bytes remain; retry on next save */ });
            }

            // Portable media helpers can replace inline bytes with canonical
            // S3/blob references. Commit those repaired values back to live
            // state only if the user has not edited that lane while the async
            // upload was running. Without this write-back every later autosave
            // reprocessed the same base64 and could re-upload it indefinitely.
            if (!deferMediaStateAdoption && savedProjectStillCurrent
                && photosRef.current === photoStateWriteBaseline
                && persistenceValueChanged(photoStateWriteBaseline, effectivePhotosForSave)) {
                suppressUnsavedRef.current = true;
                photosRef.current = effectivePhotosForSave;
                setPhotos(effectivePhotosForSave);
            }
            // React and the Stage 5 presentation can allocate a fresh scope
            // array while an otherwise unchanged save is awaiting media or the
            // backend. Object identity alone then mistakes harmless render
            // churn for an authored edit, refuses the canonical write-back,
            // and leaves the save fingerprint permanently ahead of live state.
            // The autosave sees `scope` dirty again and repeats forever. Keep
            // the strict value comparison so a real concurrent edit still wins.
            const liveScopeStillMatchesSaveStart = scopeRef.current === effectiveScope
                || !persistenceValueChanged(scopeRef.current, effectiveScope);
            // A newer scope value appeared while this save was awaiting media
            // or the backend. Most authored fields will also differ in the App
            // fingerprint, but derived/internal costing is deliberately omitted
            // there to prevent load-time churn. Keep one explicit dirty receipt
            // so that newer live value receives its own follow-up save instead
            // of disappearing on close/reload.
            const scopeChangedDuringSave = savedProjectStillCurrent
                && !liveScopeStillMatchesSaveStart;
            const canonicalScopeReceiptForLiveState = liveScopeStillMatchesSaveStart
                ? canonicalScopeForState
                : reconcileCanonicalScopeMediaReceipt(
                    scopeRef.current,
                    effectiveScope,
                    canonicalScopeForState,
                );
            if (savedProjectStillCurrent
                && persistenceValueChanged(scopeRef.current, canonicalScopeReceiptForLiveState)) {
                suppressUnsavedRef.current = true;
                scopeRef.current = canonicalScopeReceiptForLiveState;
                setScope(canonicalScopeReceiptForLiveState);
            }
            if (savedProjectStillCurrent
                && (quoteDataRef.current === effectiveQuoteData || quoteDataRef.current === quoteDataAtSaveStart)
                && persistenceValueChanged(quoteDataRef.current, canonicalQuoteDataForState)) {
                suppressUnsavedRef.current = true;
                quoteDataRef.current = canonicalQuoteDataForState;
                setQuoteData(canonicalQuoteDataForState);
            }
            if (savedProjectStillCurrent
                && selectedHeroImageRef.current === selectedHeroStateWriteBaseline
                && persistenceValueChanged(selectedHeroStateWriteBaseline, canonicalSelectedHeroForState)) {
                suppressUnsavedRef.current = true;
                selectedHeroImageRef.current = canonicalSelectedHeroForState;
                setSelectedHeroImage(canonicalSelectedHeroForState);
            }

            // v5.46.72 stale-save guard: a slow save (S3 uploads inside the
            // portable-for-persistence pass) can complete AFTER the user has
            // opened a different project — repainting the previous project's
            // documents over the new one. Only repaint while the saved project
            // is still the one on screen; the record itself is saved by id
            // either way.
            const serverDocuments = Array.isArray((savedResponse as LooseRecord).documents)
                ? (savedResponse as LooseRecord).documents
                : null;
            const ownedDocumentMerge = serverDocuments
                ? await mergeProjectSaveResultForDexieOwned(
                    { id: authoritativeId, documents: effectiveDocumentsForSave },
                    { id: authoritativeId, documents: serverDocuments },
                )
                : null;
            const canonicalDocuments = Array.isArray(ownedDocumentMerge?.documents)
                ? ownedDocumentMerge.documents
                : (serverDocuments || effectiveDocumentsForSave);
            const documentsSaveStillCurrent = !deferMediaStateAdoption && savedProjectStillCurrent
                && saveRunIsCurrent()
                && (currentProjectIdRef.current === projectId
                    || currentProjectIdRef.current === authoritativeId);
            if (documentsProvisionalAdopted
                && documentsSaveStillCurrent
                && provisionalDocumentsStateBaseline) {
                if (persistenceValueChanged(provisionalDocumentsStateBaseline, canonicalDocuments)) {
                    setDocuments((prev: any) => {
                        if (prev !== provisionalDocumentsStateBaseline) return prev;
                        suppressUnsavedRef.current = true;
                        return canonicalDocuments;
                    });
                }
            } else if (documentsSaveStillCurrent
                && persistenceValueChanged(effectiveRawDocuments, canonicalDocuments)) {
                setDocuments((prev: any) => {
                    let liveSignature: string | null = null;
                    try { liveSignature = JSON.stringify(prev); }
                    catch { /* fail closed below unless identity is unchanged */ }
                    const stillOwnsSaveStartLane = prev === documentsStateWriteBaseline
                        ? (documentsStateWriteBaselineSignature === null
                            || liveSignature === documentsStateWriteBaselineSignature)
                        : (documentsStateWriteBaselineSignature !== null
                            && liveSignature === documentsStateWriteBaselineSignature);
                    if (!stillOwnsSaveStartLane) return prev;
                    suppressUnsavedRef.current = true;
                    return canonicalDocuments;
                });
            }

            // Update the projects listing from the canonical server receipt,
            // with the optimistic record only filling fields an older server
            // omitted. Server lifecycle corrections must win here exactly as
            // they do for the returned project and cloud projection; otherwise
            // a Stage 5 edit can leave the Run Sheet falsely showing the stale
            // pre-save ready/completed state until the next refetch.
            const canonicalSummaryProject: LooseRecord = {
                ...projectRecord,
                ...canonicalSavedProject,
                id: authoritativeId,
            };
            const { photos: _p, documents: _d, videos: _v, videoTranscripts: _vt, photoAnalysis: _pa, ...summary } = canonicalSummaryProject;
            summary.photoCount = photos?.length || 0;
            summary.documentCount = effectiveDocumentsForSave?.length || 0;
            summary.videoCount = effectiveVideos?.length || 0;

            // Match by BOTH the server-assigned UUID and the client-side
            // temp id. Without the client-id match, a first-save of a new
            // project would leave a ghost row under `proj_<ts>` alongside
            // the real UUID-keyed row.
            setProjects(prev => {
                const idx = prev.findIndex((p: any) => (
                    p.id === authoritativeId
                    || p.id === projectId
                    || p.id === legacyProjectOwnerAdoption?.from
                ));
                if (idx >= 0) {
                    const updated = [...prev];
                    updated[idx] = summary;
                    return updated;
                }
                return [summary, ...prev];
            });
            // A3: notify other tabs that this project's summary changed
            // (create OR update — both flow through this same setProjects).
            broadcastProjectsChanged({ kind: 'save', projectIds: [authoritativeId || projectId] });

            // The server has these edits now: forget any copy kept on this
            // device while it was not answering (outage lane, 2026-09-29).
            // A pre-UUID project's copy is kept under the id the editor held
            // (the keep runs before the media step adopts its UUID).
            const savedProjectIds = [...new Set([authoritativeId, projectId, projectRecord.id, legacyProjectOwnerAdoption?.from]
                .filter(Boolean)
                .map(String))];
            // This save created a new project whose edits this editor kept in
            // the new-project slot: that copy is on the server now too.
            const newProjectSlotSaved = startedAsNewProject
                && (newProjectSlotHeldAtStart || hasKeptUnsavedEdits(NEW_PROJECT_SLOT));
            if (newProjectSlotSaved) savedProjectIds.push(NEW_PROJECT_SLOT);
            const hadKeptEditsOnDevice = newProjectSlotSaved || savedProjectIds.some(id => hasKeptUnsavedEdits(id));
            for (const savedId of savedProjectIds) void clearUnsavedProjectEdits(savedId);

            // This state is now the persisted state — edits are measured from
            // here until the next load or save.
            if (saveRunIsCurrent()) {
                const fingerprintAtCommit = rebaseProjectFingerprintWithSaveOverrides(
                    fingerprintAtSave,
                    {
                        photos: effectivePhotosForSave,
                        documents: canonicalDocuments,
                        selectedHeroImage: canonicalSelectedHeroForState,
                        scope: canonicalScopeForState,
                        quoteData: canonicalQuoteDataForState,
                    },
                );
                if (savedFingerprintRef && fingerprintAtCommit !== null) {
                    savedFingerprintRef.current = fingerprintAtCommit;
                }
                setHasUnsavedChanges(scopeChangedDuringSave);
                setLastSavedAt(new Date());
                // If this success follows a failure (e.g. the autosave retried after
                // the connection came back), say so plainly so the vanished red
                // banner reads as "recovered", not "silently lost" (Peter 2026-06-30).
                const recoveredFromError = !!saveErrorRef.current || hadKeptEditsOnDevice;
                saveErrorRef.current = null;
                setManualSaveRetryLatch(null);
                setSaveError(null); // v5.40.0: clear the persistent save-error banner on success
                if (overrides.silent !== true) {
                    setToast({
                        message: recoveredFromError ? 'Back online. Your changes have been saved.' : 'Project saved',
                        type: 'success',
                    });
                }
            }

            if (options.ensureProjectOwner !== true) {
                ActivityLogService.log('save', {
                    type: 'project',
                    description: `Saved project: ${projectRecord.title || 'Untitled'}`
                });
            }

            // v5.37.16 (Peter 2026-05-20): allocate the QM-issued Quote # at
            // first project save. Idempotent — calls a backend route guarded
            // by a partial unique index, so concurrent / repeat fires return
            // the same value. Fire-and-forget: allocation must NEVER block
            // the save success path.
            //
            // Allocation moment shift: pre-v5.37.16 this was wired to
            // FullQuoteEditorModal.onEmailSent (Wave B, v5.37.11). Peter wants
            // the number visible from project creation — it's the canonical
            // identifier for the quote, not an email-time artifact. The Wave B
            // onEmailSent allocator stays in place as a defensive backup; both
            // call the same idempotent route so a double-fire here is harmless.
            //
            // Skip when qmQuoteNo is already populated (avoids a network round-
            // trip per save call — typical session has dozens of saves).
            const existingQmQuoteNo = effectiveQuoteData?.meta?.qmQuoteNo
                || projectRecord?.qmQuoteNo
                || projectRecord?.qm_quote_no
                // Migration 120: a quote imported from another builder keeps
                // that builder's number and is never given one of ours. The
                // server refuses too; skipping here saves a round trip per save.
                || effectiveQuoteData?.meta?.sourceQuoteRef
                || projectRecord?.sourceQuoteRef
                || projectRecord?.source_quote_ref
                // An imported quote that printed no number keeps none (GOAL N6).
                || (effectiveQuoteData?.meta?.importSource ? 'imported' : null)
                || null;
            const allocatingProjectId = projectRecord?.id;
            // Frontend dedup: skip if an allocation is already in flight for
            // this project (rapid-fire saves on the same project within the
            // allocate roundtrip window).
            const alreadyAllocating = allocatingProjectId
                && qmQuoteNoAllocationInFlightRef.current.has(allocatingProjectId);
            if (options.ensureProjectOwner !== true
                && !existingQmQuoteNo
                && allocatingProjectId
                && !alreadyAllocating) {
                qmQuoteNoAllocationInFlightRef.current.add(allocatingProjectId);
                (async () => {
                    try {
                        const { allocateQmQuoteNo } = await import('../services/qm-quote-no');
                        const result = await allocateQmQuoteNo(allocatingProjectId);
                        if (result?.qmQuoteNo) {
                            // Project-switch guard: only stamp into local
                            // quoteData if the user is still on the SAME project
                            // the allocation was for. Without this guard, the
                            // allocation's setQuoteData would clobber the
                            // currently-loaded project's quoteData with the
                            // OLD project's qmQuoteNo. (User navigates away
                            // during the ~100ms allocate roundtrip.)
                            const liveProjectId = currentProjectIdRef.current;
                            if (saveRunIsCurrent() && liveProjectId === allocatingProjectId) {
                                setQuoteData(prev => {
                                    if (!prev || prev.meta?.qmQuoteNo) return prev;
                                    return { ...prev, meta: { ...(prev.meta || {}), qmQuoteNo: result.qmQuoteNo } };
                                });
                            }
                            if (result.allocated) {
                                console.log(`[QM Quote #] Allocated ${result.qmQuoteNo} for project ${allocatingProjectId} (first-save trigger).`);
                            }
                        }
                    } catch (allocErr) {
                        // Defensive: an allocator failure must never break the
                        // save UX. Surface as a warning so the next save retries.
                        console.warn('[QM Quote #] First-save allocation failed (non-fatal, will retry on next save):', (allocErr as { message?: string })?.message || allocErr);
                    } finally {
                        qmQuoteNoAllocationInFlightRef.current.delete(allocatingProjectId);
                    }
                })();
            }

            // Callers that gate Stage 4/5 must inspect the canonical server
            // receipt, never the optimistic local envelope they asked to save.
            return canonicalSavedProject;
        } catch (err) {
            // A commit guard's own refusal is not a failed save: no banner, no
            // error toast, nothing kept on this device. QUOTE_SOURCE_CHANGED is
            // the Full AI background author setting its quote aside (DEC-098).
            if ((err as { code?: string })?.code === 'PROJECT_RUN_STALE'
                || (err as { code?: string })?.code === 'PROJECT_SAVE_OWNER_CHANGED'
                || (err as { code?: string })?.code === 'QUOTE_SCOPE_CHANGED'
                || (err as { code?: string })?.code === 'QUOTE_SOURCE_CHANGED') {
                throw err;
            }
            // ─── QuoteMaker's server did not take it (outage lane, 2026-09-29)
            // Keep the envelope on this device, with the version it descends
            // from. It survives a refresh and signing in again, and the next
            // open of this project paints it back. Kept even when the tab has
            // moved on to another project: these are still that project's
            // unsaved edits.
            const serverUnavailable = isServerUnavailableError(err);
            // A guarded save that met the outage before its guard ran (in the
            // media step): ask the guard now. One it refuses is not kept,
            // exactly as it would not have been sent (review of 55c46488,
            // 2026-09-29). A guard that could not finish because the server is
            // away refused nothing, and the save is kept as before.
            let guardAllowsKeep = true;
            if (serverUnavailable && !keepsNothingOnDevice && (keepableSave || keepableNewProject) && beforeCommit && !commitGuardPassed) {
                try {
                    await beforeCommit();
                } catch (guardErr) {
                    if (!isServerUnavailableError(guardErr)) guardAllowsKeep = false;
                }
            }
            const mayKeep = serverUnavailable && guardAllowsKeep && !keepsNothingOnDevice;
            if (mayKeep && keepableSave) {
                let envelope: LooseRecord | null = null;
                try { envelope = keepableSave.envelope(); } catch { envelope = null; }
                if (envelope) {
                    // A save of the Quote Run's own job only remembers the
                    // state: the run drives the server's copy and never
                    // restores, and a copy on this device would come back on
                    // the next normal open as the operator's own edits. Any
                    // other project saved during a run (the operator's own,
                    // open while files are read) keeps its copy as before.
                    const kept = await keepUnsavedProjectEdits({
                        projectId: keepableSave.projectId,
                        expectedUpdatedAt: keepableSave.baseToken(),
                        baselineSignature: keepableSave.baselineSignature,
                        fingerprint: fingerprintAtSave,
                        envelope,
                        onDevice: quoteRunJobAtSave !== keepableSave.projectId,
                    });
                    if (kept && err && typeof err === 'object') (err as LooseRecord).keptOnDevice = true;
                }
            } else if (mayKeep && keepableNewProject) {
                // A project never saved: into the new-project slot, or under
                // the id of the shell this save created before the outage met
                // it. A Quote Run only remembers the state (it never restores);
                // a save the tab has moved on from writes the copy but does not
                // claim the editor now showing something else.
                let envelope: LooseRecord | null = null;
                try { envelope = keepableNewProject.envelope(); } catch { envelope = null; }
                if (envelope) {
                    const kept = await keepUnsavedProjectEdits({
                        projectId: adoptedShellId || NEW_PROJECT_SLOT,
                        expectedUpdatedAt: adoptedShellId ? (baseUpdatedAtRef.current || null) : null,
                        baselineSignature: null,
                        fingerprint: fingerprintAtSave,
                        envelope: adoptedShellId ? { ...envelope, id: adoptedShellId } : envelope,
                        onDevice: batchActiveRef.current !== true,
                        trackInEditor: saveRunIsCurrent(),
                    });
                    if (kept && err && typeof err === 'object') (err as LooseRecord).keptOnDevice = true;
                }
            }
            if (!saveRunIsCurrent()) throw err;
            // The "QuoteMaker is updating" banner is already up and says the
            // work is kept on this device. A second red banner and a toast per
            // attempt (the 2026-09-29 console) added nothing but alarm.
            const quietWhileServerAway = serverUnavailable && !isBackendReachable();
            if (quietWhileServerAway) {
                // Marked so a Save press can answer for itself: the banner
                // can be dismissed, and then nothing on screen said why the
                // press did not save (manual-save-outage-notice.ts).
                if (err && typeof err === 'object') (err as LooseRecord).quietWhileServerAway = true;
                console.warn('[QM] Project not saved yet: QuoteMaker\'s server is not answering. The changes are kept on this device.');
                throw err;
            }
            console.error('Error saving project:', err);
            // v5.40.0 (UX audit Wave-1 P0 #2 + #4): classify the error so the
            // persistent banner can surface specific guidance. Pre-v5.40.0 we
            // showed a 5-second "Save failed - please try again" toast then
            // hid forever — user stepped away, came back, no signal anything
            // failed. Now we set `saveError` (cleared on the next successful
            // save) so a persistent banner renders until the user acts.
            const classified = classifySaveError(err);
            const retryDecision = classifyProjectSaveRetry(err);
            if (retryDecision.mode === 'manual' && fingerprintAtSave !== null) {
                setManualSaveRetryLatch({
                    projectId: savingProjectIdSnapshot,
                    fingerprint: fingerprintAtSave,
                    reasonCode: retryDecision.reasonCode,
                    at: Date.now(),
                });
            }
            // ─── Stale-write refusal (server-detected cross-device edit) ────
            // The backend held the line: the row moved after this tab loaded
            // it, so nothing was overwritten. Two things have to happen here,
            // and the second one is the easy one to forget.
            //
            // 1. Surface it as a CONFLICT, not a failure. It routes through
            //    the same banner the cross-tab detector uses, because it is
            //    the same event — that detector rides a BroadcastChannel and
            //    so only ever sees other tabs in THIS browser. A second
            //    device was always invisible to it, which is precisely the
            //    case the cloud makes normal.
            // 2. LATCH it. `hasUnsavedChanges` stays true after any failed
            //    save, and the 2s auto-save effect keys on exactly that — so
            //    without a latch this refusal re-fires every 2 seconds
            //    forever, hammering the backend with an envelope that can
            //    never be accepted. Same shape as the coverage-gate retry
            //    loop: a refusal upstream of the state an auto-effect keys on
            //    re-arms itself unless something pins it. The latch clears
            //    only through an operator decision (Reload or Overwrite).
            if (classified.kind === 'conflict') {
                const conflictBody = (err as LooseRecord)?.body as LooseRecord | undefined;
                setCrossTabConflict({
                    projectId: savingProjectIdSnapshot,
                    kind: 'server-stale',
                    at: Date.now(),
                    actualUpdatedAt: conflictBody?.actualUpdatedAt || null,
                    expectedUpdatedAt: conflictBody?.expectedUpdatedAt || null,
                });
            }
            // v5.40.15 (Wave 15 fix F7): suppress the persistent banner for
            // user-initiated aborts. The banner is for failures the user
            // needs to act on — a deliberate cancel isn't one.
            // A conflict is likewise not a save FAILURE: the red retry banner
            // would offer "Retry Save", which is the one action guaranteed to
            // 409 again. The conflict banner owns this case.
            if (classified.kind !== 'aborted' && classified.kind !== 'conflict') {
                setSaveError({
                    kind: classified.kind,
                    message: classified.message,
                    detail: (err as { message?: string })?.message || String(err),
                    // v5.40.14 R4: use the save-start snapshot, not the live ref —
                    // a project switch during the in-flight save shouldn't relabel
                    // the banner.
                    projectId: savingProjectIdSnapshot,
                    at: Date.now(),
                });
            }
            // Keep the toast as a momentary cue too (some users miss the top
            // banner). The persistent banner is the authoritative surface.
            // Behavioural round 2 (P5 — DOCUMENTED, not a bug): this
            // deliberately includes kind 'aborted'. The v5.46.74
            // duplicate-prevention contract is "AbortError → 'Save was
            // cancelled.' toast, no red banner" — a user-initiated cancel
            // still gets its momentary cue; only the persistent banner is
            // suppressed above.
            setToast({ message: classified.message, type: 'error' });
            // Re-throw so OUTER callers (loadProject's Save & switch rescue,
            // any explicit await saveCurrentProject() in a critical flow) can
            // branch on failure. Prior behaviour swallowed the error and let
            // the outer code continue - meaning "Save & switch" would silently
            // discard the user's edits after a failed save. Identified during
            // Pass-4 rigorous-audit 2026-04-20.
            throw err;
        } finally {
            isSavingProjectRef.current = false;
            setIsSavingProject(false);
            // Drain queued save request if one came in while we were saving.
            // Use setTimeout (not microtask) to:
            //   a) Let React flush the setIsSavingProject(false) update
            //   b) Break any tight loop that could form from re-entrant saves
            //   c) Give the user's edits time to settle before re-saving
            if (pendingSaveRef.current) {
                scheduleQueuedProjectSaveDrain();
            }
        }
    };

    // Load a project from history (with folder reconnection check)
    // Fetches full project data from IndexedDB since the listing only holds lightweight summaries
    //
    // `shouldPaint` (2026-09-29): asked once, when the project is ready to
    // paint. A false leaves everything as it is and resolves without painting
    // or navigating. The routing hook's reopen of the tab's saved project
    // passes it, so a reopen the operator walked away from (a reload lands the
    // Run Sheet, they go to Clients while it loads) does not pull them to
    // QuoteMaker: completeProjectLoad navigates there as its last statement.
    // C29. The project on screen (Stages 1 to 4) is "the parent": its form and scope are canonical state published by the stage
    // inputs, and App's fingerprint (ref-backed, so current without a render) and the last persisted baseline are how App itself tells
    // saved from unsaved. A valid baseline decides; equality with it outranks a stale dirty flag (App's equality branch returns
    // without clearing the flag, so a reverted edit leaves it true). Only without a baseline is the flag the conservative answer.
    const parentContentUnsavedNow = (): boolean => {
        if (!captureProjectFingerprint) return hasUnsavedChangesLive?.() === true;
        const baseline = savedFingerprintRef?.current ?? null;
        if (baseline !== null) return captureProjectFingerprint() !== baseline;
        return hasUnsavedChangesLive?.() === true;
    };
    // C29. Whether the project on screen now is still the project that was on screen before. A project's own save can change its id
    // without anyone else taking the screen and without moving the load generation (an open or a reload always does): a project that
    // began unsaved gets its id from its first save (persistence 2922-2927), and a legacy Dexie id that holds device media is
    // canonicalised to a UUID (legacyProjectOwnerAdoption, 2915-2921). Admit only those two shapes. Any other change of a non-null id,
    // including one canonical id replaced by another, is somebody else's screen. A null owner is admitted here on the strength of the
    // load generation, which both callers check themselves (the paint boundary after its last await, Save & open against the
    // generation it captured): another project taking the screen has always moved it.
    const isSameOwnerOrAdoptedCanonical = (before: string | null, now: string | null): boolean =>
        before === now
        || before === null
        || (!isCanonicalProjectUuid(before) && now !== null && isCanonicalProjectUuid(now));
    // The accepted-decision stamp, taken the moment an open is allowed to go ahead.
    const takeOutgoingParentStamp = (): OutgoingParentStamp | null => {
        if (!captureProjectFingerprint) return null;
        return {
            projectId: (currentProjectIdRef?.current ?? currentProjectId ?? null) as string | null,
            fingerprint: captureProjectFingerprint(),
        };
    };
    // True when the project on screen holds work newer than the stamp that is not saved: it changed since the decision AND it is
    // not saved now. That lets an unchanged accepted Discard through (its work is deliberately still in state), lets an edit that was
    // saved before the paint through, and holds back an edit made while the destination was fetched.
    const parentWorkNewerThanDecision = (stamp: OutgoingParentStamp | null): boolean => {
        if (!stamp || !captureProjectFingerprint) return false;
        if (!isSameOwnerOrAdoptedCanonical(stamp.projectId, (currentProjectIdRef?.current ?? currentProjectId ?? null) as string | null)) return false;
        if (captureProjectFingerprint() === stamp.fingerprint) return false;
        return parentContentUnsavedNow();
    };

    const loadProject = async (projectSummary: LooseRecord, { shouldPaint, forceUnsavedPrompt = false }: LoadProjectOptions = {}) => {
        // Behavioural round 2 (P2 fix): reject an id-less summary up front.
        // It used to slip past the unsaved-changes guard below (which keyed
        // on projectSummary?.id) straight into loadProjectUnchecked — which
        // claimed a load generation (abandoning any legitimate in-flight
        // open) and hit the fetch path with `undefined` before toasting
        // 'Project not found'. Nothing is loadable without an id, so fail
        // fast with the same operator-visible outcome and no side effects.
        if (!projectSummary?.id) {
            console.warn('[QM] loadProject called without a project id — ignoring.', projectSummary);
            setToast({ message: 'Project not found', type: 'error' });
            return;
        }
        // The job a Quote Run is driving is shown, not reopened (A3 review).
        // Nothing is loaded, so a caller that moves to a stage after an open
        // must not move this one: it gets QUOTE_RUN_JOB_SHOWN back.
        if (showQuoteRunJobInsteadOfOpening(projectSummary.id)) return QUOTE_RUN_JOB_SHOWN;
        // A Quote Run refuses the open before the unsaved-changes dialog: that
        // dialog would save or discard work for an open that cannot happen.
        assertNoQuoteRunForProjectChange('open', projectSummary.id);
        // ─── Guard against silent data loss when switching projects ─────────
        // If the CURRENT project has unsaved changes and we're switching to a
        // DIFFERENT project, prompt before discarding. Without this guard,
        // clicking a project in the Dashboard silently overwrites state.
        const switchingToDifferentProject = projectSummary.id !== currentProjectId;
        // B (C18): work only the open Stage 5 editor holds is unsaved work too, and the parent's flag lags it. Asked now,
        // from the editor's refs (it flushes nothing and consumes nothing).
        // C29: the closure's `hasUnsavedChanges` is the value of the render that made this call, so a call made before an edit
        // (or the paint boundary asking again after one) can see false while the project on screen is dirty. App's live flag is read
        // now, and the paint boundary can force the question.
        const unsavedWork = forceUnsavedPrompt || hasUnsavedChanges || hasUnsavedChangesLive?.() === true || hasUnsavedVisibleWork?.() === true;
        if (unsavedWork && switchingToDifferentProject) {
            // A Quote Run that started while this dialog was open refuses the
            // load, and its message has already been shown.
            // The stamp is taken here, at the moment the operator's choice lets the open go ahead (saved, discarded, or accepted as
            // kept on this device): work the editor holds NOW was decided on. Work typed after it, while the project is fetched, was not.
            const proceedWithLoad = () => {
                loadProjectUnchecked(projectSummary, {
                    shouldPaint,
                    outgoingWorkStamp: visibleWorkStamp?.() ?? null,
                    outgoingParent: takeOutgoingParentStamp(),
                }).catch(ignoreQuoteRunActive);
            };

            // Name both ends of the switch (operator feedback 2026-07-27: the
            // anonymous dialog forced navigating back to check WHICH project
            // held the unsaved work). Source resolves from the projects list —
            // the same summaries the dashboard renders — with the address as a
            // fallback for a not-yet-titled draft.
            const sourceRecord = (Array.isArray(projects) ? projects : [])
                .find((p: LooseRecord) => p?.id === currentProjectId);
            const sourceTitle = String(
                sourceRecord?.title
                || (formData as LooseRecord | null)?.address
                || 'the current project',
            );
            const destTitle = String(projectSummary?.title || projectSummary?.name || 'the selected project');

            // ─── Save-before-switch with timeout + retry affordance ─────────
            // Wraps saveCurrentProject() in a 15s race so a hung save (slow
            // network, backend stall) can't silently freeze the switch flow.
            // On timeout we offer an explicit "Try again" via a follow-up
            // confirm modal; on other errors we surface the message as a toast.
            const SAVE_TIMEOUT_MS = 15000;
            // B (C17): with the Stage 5 editor up this is the editor's own Save, which takes in what is typed and says whether it
            // saved. A Save that was refused, or came back not-ok, is NOT a save: the old code opened the next project after
            // ANY fulfilled value. It throws what the Save caught (so an outage's "kept on this device" and a conflict reach the
            // branches below unchanged) or, for a refusal with no error, a plain failure. With no editor it is the ordinary save.
            let lastOutcome: { ok: boolean; error?: unknown; newerWork?: boolean } | null = null;
            const saveOutgoing = async () => {
                if (!saveOutgoingVisibleWork) return saveCurrentProject();
                const outcome = await saveOutgoingVisibleWork();
                lastOutcome = outcome ?? null;
                if (outcome?.ok === true) return outcome;
                throw outcome?.error ?? new Error('Save did not complete');
            };
            const attemptSaveAndSwitch = async () => {
                // Who owns the screen when this Save & open begins. Anything awaited after the save (the commit wait below) can let
                // another open, a switch or a Quote Run take it, and then this continuation is obsolete.
                const ownerAtSave = {
                    projectId: (currentProjectIdRef?.current ?? currentProjectId ?? null) as string | null,
                    generation: (projectLoadGenerationRef?.current ?? null) as number | null,
                };
                // Persistent "Saving…" indicator so the user has feedback even
                // if the save runs longer than the default 3s toast window.
                setToast({ message: 'Saving\u2026', type: 'info', sticky: true });
                let timedOut = false;
                try {
                    await Promise.race([
                        saveOutgoing(),
                        new Promise((_, reject) => setTimeout(() => {
                            timedOut = true;
                            reject(new Error('timeout'));
                        }, SAVE_TIMEOUT_MS)),
                    ]);
                } catch (err) {
                    console.error('[QM] Save-before-switch failed:', err);
                    if (timedOut || (err as { message?: string })?.message === 'timeout') {
                        // Clear the sticky "Saving…" toast, then offer retry.
                        setToast(null);
                        showConfirm({
                            title: 'Save taking longer than expected',
                            message: `Saving "${sourceTitle}" hasn't finished after 15 seconds. Check your network and try again.`,
                            confirmText: 'Try again',
                            cancelText: 'Stay here',
                            confirmColor: 'blue',
                            icon: 'warning',
                            hint: 'Nothing has been discarded; your work is still in the editor.',
                            onConfirm: () => { attemptSaveAndSwitch(); },
                            onCancel: () => {},
                        });
                    } else if (isServerUnavailableError(err) && (err as LooseRecord)?.keptOnDevice === true) {
                        // Outage lane (2026-09-29): the server is not answering,
                        // but the changes ARE kept on this device and come back
                        // when this project is opened again. Let the operator
                        // go on, with the one consequence said plainly.
                        setToast(null);
                        // The copy kept on this device is the envelope the Save SUBMITTED. If the operator kept working after
                        // it (typed while the save was held), that newer work is not in it, and opening would drop it. Say so,
                        // keep the screen as the default, and make losing it an explicit choice.
                        if ((lastOutcome as { newerWork?: boolean } | null)?.newerWork === true) {
                            showConfirm({
                                title: 'QuoteMaker is not answering',
                                message: `"${sourceTitle}" could not be saved yet because QuoteMaker can't reach its server. The changes up to that save are kept on this device, but you kept working after it, and those newer changes are not kept.`,
                                confirmText: 'Stay here',
                                cancelText: `Open "${destTitle}" without them`,
                                confirmColor: 'blue',
                                icon: 'warning',
                                defaultFocus: 'confirm',
                                hint: 'Staying keeps everything you typed on this screen. The newer changes are not saved automatically: press Save again when QuoteMaker answers.',
                                onConfirm: () => {},
                                onCancel: () => { proceedWithLoad(); },
                            });
                            return;
                        }
                        showConfirm({
                            title: 'QuoteMaker is not answering',
                            message: `"${sourceTitle}" could not be saved yet because QuoteMaker can't reach its server. Your changes are kept on this device and come back when you open "${sourceTitle}" again.`,
                            confirmText: `Open "${destTitle}"`,
                            cancelText: 'Stay here',
                            confirmColor: 'blue',
                            icon: 'info',
                            defaultFocus: 'cancel',
                            hint: 'Staying keeps this screen. It saves by itself when the server answers.',
                            onConfirm: () => { proceedWithLoad(); },
                            onCancel: () => {},
                        });
                        return;
                    } else if (classifySaveError(err as Error).kind === 'conflict') {
                        // 2026-08-19 (operator report, workspace tabs): the
                        // save was REFUSED because this tab's copy is older
                        // than the server row (typically the preparation queue
                        // finished this project after the tab opened it). The
                        // old behaviour dead-ended: error toast, switch
                        // aborted, operator stuck. But a stale-write refusal
                        // during a SWITCH has a truthful resolution the banner
                        // itself teaches: the newer version is already saved,
                        // so "reload" here simply means proceed WITHOUT saving
                        // this out-of-date copy. Offer exactly that, named for
                        // what it does; Stay leaves the conflict banner up for
                        // the Reload/Overwrite decision on this screen.
                        setToast(null);
                        showConfirm({
                            title: 'This copy is out of date',
                            message: `"${sourceTitle}" was updated somewhere else after this tab opened it (usually the preparation queue finishing it), so the changes on this screen cannot be saved over the newer version.\n\nThe newer version is already safely saved.`,
                            confirmText: `Keep the newer version & open "${destTitle}"`,
                            cancelText: 'Stay here',
                            confirmColor: 'blue',
                            icon: 'warning',
                            defaultFocus: 'confirm',
                            hint: 'Staying keeps this screen so you can Reload or Overwrite from the red banner.',
                            onConfirm: () => { proceedWithLoad(); },
                            onCancel: () => {},
                        });
                        return;
                    } else {
                        const msg = (err as { message?: string })?.message ? `Save failed: ${(err as { message?: string }).message}` : 'Save failed \u2014 project not switched';
                        setToast({ message: msg, type: 'error' });
                    }
                    return; // Abort switch so user doesn't lose work
                }
                // Success - saveCurrentProject() will have already replaced the
                // sticky "Saving…" toast with its own "Project saved" toast
                // (non-sticky, 3s auto-clear), so we don't touch the toast here.
                // If for some reason the sticky toast is still showing (e.g. a
                // no-op save that returned early without toast), clear it so it
                // doesn't linger forever.
                setToast(prev => (prev && prev.sticky ? null : prev));
                // B (C17): the save that just succeeded carried the envelope it was SENT with. Saving does not lock editing, so
                // anything the operator typed or edited while it ran (the editor keeps that newer revision) is not in it, and
                // opening the next project would reset the editor and drop it. Stay, say so, and offer the useful next step.
                // Asked now, from the editor's refs.
                // C29 / PM 04:17 and 06:06: parent-state edits (Stages 1 to 4) made while the save ran are not in the envelope it sent.
                // The save committed its baseline from that envelope, so parent content that differs from the baseline is newer work,
                // whichever editor (if any) is open and whoever made the save. The Stage 5 wrapper adopts the canonical quote, scope and
                // hero through queued React setters and moves the baseline at once, while App's fingerprint reads the latest RENDER
                // (measured: `selectedHeroImage` null in the latest render against '' in the baseline), so the comparison waits for
                // the commit of what the save adopted. That wait is the commit contract, not a delay: it settles once the updates queued
                // before the call are rendered and committed.
                await waitForCommit?.();
                // The wait is an await: re-check, AFTER it, that this Save & open still owns the screen and that no Quote Run holds
                // the project. Otherwise the answer below is for a screen that is no longer there.
                // The owner's id may change in THIS save only in the ways isSameOwnerOrAdoptedCanonical names.
                const idNow = (currentProjectIdRef?.current ?? currentProjectId ?? null) as string | null;
                if (!isSameOwnerOrAdoptedCanonical(ownerAtSave.projectId, idNow)
                    || (projectLoadGenerationRef?.current ?? null) !== ownerAtSave.generation) return;
                try {
                    assertNoQuoteRunForProjectChange('open', projectSummary.id);
                } catch (err) {
                    ignoreQuoteRunActive(err);
                    return;
                }
                // Editor newer work and parent newer work, both asked now, after everything that was awaited.
                if (hasUnsavedVisibleWork?.() === true || parentContentUnsavedNow()) {
                    showConfirm({
                        title: 'More changes since that save',
                        message: `"${sourceTitle}" was saved, but you kept working while it saved. Those newer changes are not saved yet.`,
                        confirmText: 'Save again & open',
                        cancelText: 'Stay here',
                        confirmColor: 'blue',
                        icon: 'warning',
                        defaultFocus: 'confirm',
                        hint: `Staying keeps everything you typed on "${sourceTitle}". Nothing has been discarded.`,
                        onConfirm: () => { attemptSaveAndSwitch(); },
                        onCancel: () => {},
                    });
                    return;
                }
                proceedWithLoad();
            };

            showConfirm({
                title: 'Save your changes first?',
                message: `"${sourceTitle}" has unsaved changes.\n\nYou're opening "${destTitle}".`,
                confirmText: 'Save & open',
                cancelText: 'Discard changes',
                confirmColor: 'blue',
                icon: 'warning',
                // The cancel key destroys work here, so opening focus is
                // pinned to the safe Save key — a reflexive Enter must never
                // discard changes.
                defaultFocus: 'confirm',
                hint: `Esc keeps you on "${sourceTitle}" with nothing saved or lost.`,
                onConfirm: () => { attemptSaveAndSwitch(); },
                // Discard means discard: a copy kept on this device while the
                // server was not answering must not come back on the next open.
                // The open is for a different project, so it cannot race this.
                // A project never saved keeps its copy in the new-project slot,
                // which the open releases in this tab, so forget it first.
                onCancel: () => {
                    // The text still in the editor's fields is discarded with the rest, so none of it can reach the project that opens.
                    discardOutgoingVisibleDrafts?.();
                    void clearUnsavedProjectEdits(currentProjectId);
                    discardKeptNewProject();
                    proceedWithLoad();
                },
            });
            return;
        }
        return loadProjectUnchecked(projectSummary, {
            shouldPaint,
            outgoingWorkStamp: visibleWorkStamp?.() ?? null,
            // A clean project being left (not a reopen of the same one): its content now is what was decided on.
            outgoingParent: switchingToDifferentProject ? takeOutgoingParentStamp() : null,
        });
    };

    // Internal loader - assumes caller has already handled unsaved-changes guard.
    //
    // v5.19.0 (DEC-BQ-022) — `batchMode` parameter added to bypass the
    // reconnect-folder modal. The modal exists to recover photos/PDFs the
    // browser dropped from blob: URL cache; a USER CLICK on Confirm/Skip is
    // the only path to `completeProjectLoad`. In batch mode there's no user,
    // so the modal would hang the orchestrator forever (currentProjectIdRef
    // never flips → 5s waitForRefValue timeout in pipeline-runner →
    // "loadProject: currentProjectIdRef did not flip" → every queued job
    // marked failed). Pipeline-architect specialist 2026-05-04 verified.
    //
    // In batch mode we proceed directly with `completeProjectLoad(project)`.
    // AI calls run on whatever Dexie has — degraded photos are acceptable
    // for unattended runs (the queue's contract is "ready for builder
    // review", and the builder reviews the rendered quote anyway).
    //
    // The open still loading (review round 7, 2026-09-30). A load that lands
    // QuoteMaker (completeProjectLoad navigates there as its last statement)
    // records itself in App's pendingProjectOpenRef until it settles, so
    // Menu > QuoteMaker, Back and the Assistant wait for it (useAppRouting's
    // openStillLoading) instead of taking the no-project path. That path
    // reset the form, whose new load generation threw this open away without
    // a word, or reopened the tab's other job. Only the Intake bridge used to
    // record its open, so a Run Sheet row, the Analysis drawer, All Quotes,
    // the workspace tabs and the Assistant's plain open did not. The newest
    // load owns the record: it is taken after the generation claim, so an
    // open a Quote Run refuses records nothing. A reopen that may decline to
    // paint (shouldPaint) and a Quote Run load record nothing, because
    // neither promises to land QuoteMaker, and they clear an older open they
    // have superseded. `paintedBefore` ends the wait as soon as anything
    // paints, this open's own completeProjectLoad included, so its
    // handleNavigate('quotemaker') goes through.
    const recordOpenStillLoading = (projectSummary: LooseRecord, { batchMode = false, shouldPaint }: { batchMode?: boolean } & LoadProjectOptions = {}) => {
        if (!pendingProjectOpenRef) return () => {};
        const open: PendingProjectOpen | null = !batchMode && typeof shouldPaint !== 'function' && projectSummary?.id
            ? { projectId: String(projectSummary.id), paintedBefore: paintedProjectLoadContextRef.current }
            : null;
        pendingProjectOpenRef.current = open;
        return () => {
            if (open && pendingProjectOpenRef.current === open) pendingProjectOpenRef.current = null;
        };
    };
    const loadProjectUnchecked = async (projectSummary: LooseRecord, { batchMode = false, shouldPaint, outgoingWorkStamp = null, outgoingParent = null }: { batchMode?: boolean; outgoingWorkStamp?: string | null; outgoingParent?: OutgoingParentStamp | null } & LoadProjectOptions = {}) => {
        // v5.46.73: claim a load generation for this open. Checked again after
        // the awaited fetches below — if a newer open (or a direct
        // completeProjectLoad) has claimed a later generation meanwhile, this
        // load is stale and must not paint.
        //
        // An open the queue did not start would cancel the job the queue is
        // driving: the generation claim below makes its saves stale and the
        // paint aborts its AI signal. A Quote Run refuses it first (D1). This
        // unchecked path refuses even the running job's own id: its callers
        // (the conflict banner Reload, the Revise and Split reload) replace the
        // job's content, which is exactly what would abort it.
        //
        // The queue's own load names the job the run now drives.
        if (batchMode) {
            if (quoteRunProjectIdRef) quoteRunProjectIdRef.current = projectSummary?.id ?? null;
        } else {
            assertNoQuoteRunForProjectChange('open', projectSummary?.id);
        }
        const loadGeneration = ++projectLoadGenerationRef.current;
        const settleOpenStillLoading = recordOpenStillLoading(projectSummary, { batchMode, shouldPaint });
        const paintedBeforeOpen = paintedProjectLoadContextRef.current;
        try {
            return await fetchAndPaintProject(projectSummary, loadGeneration, { batchMode, shouldPaint, outgoingWorkStamp, outgoingParent });
        } finally {
            settleOpenStillLoading();
            // B: this open ends. If it was the NEWEST claim and nothing painted (held back, not found, declined, failed), the
            // outgoing project is still the one on screen and still the owner of its editor's saves, although the claim moved
            // the live counter ahead of the painted generation. A newer claim in flight owns the counter, so nothing is retained
            // then. The counter is never moved back: opens and runs it invalidated stay invalidated.
            if (!batchMode
                && paintedProjectLoadContextRef.current === paintedBeforeOpen
                && projectLoadGenerationRef.current === loadGeneration) {
                retainOutgoingProject?.();
            }
        }
    };
    // loadProjectUnchecked's fetch and paint, after its generation claim.
    const fetchAndPaintProject = async (projectSummary: LooseRecord, loadGeneration: number, { batchMode = false, shouldPaint, outgoingWorkStamp = null, outgoingParent = null }: { batchMode?: boolean; outgoingWorkStamp?: string | null; outgoingParent?: OutgoingParentStamp | null } & LoadProjectOptions = {}) => {
        // v5.19.6 (autonomous queue test 2026-05-05) — getProject() routes
        // through DataService.projects.getById which is backend-proxied.
        // When a project exists in local Dexie but NOT on the backend (typical
        // for projects synced from a folder + never pushed up), the proxy
        // throws "Not found" on HTTP 404. The pipeline-runner (which already
        // loaded the project from raw Dexie at line 110) passes the full
        // record to handlers.loadProject — re-fetching via the backend-
        // proxied path is unnecessary AND racey when the backend doesn't
        // have it. In batch mode, prefer the pre-loaded record on the
        // projectSummary if it carries enough fields; fall back to raw
        // Dexie via dynamic import (skipping the proxy); only call
        // getProject() if both fail. This keeps non-batch behaviour
        // unchanged (still hits the proxy for cache freshness).
        let project: LooseRecord | null = null;
        if (batchMode && projectSummary && typeof projectSummary === 'object' && (projectSummary.formData || projectSummary.documents || projectSummary.scope)) {
            // Pipeline-runner passed the full record — use it directly.
            project = projectSummary;
        }
        if (!project && batchMode) {
            try {
                const dbMod = await import('../services/db') as any;
                project = await dbMod.default.projects.get(projectSummary.id);
            } catch (rawErr) {
                console.warn('[QM] loadProjectUnchecked: raw Dexie read failed (non-fatal in batch mode):', (rawErr as { message?: string })?.message);
            }
        }
        if (!project) {
            try { project = await getProject(projectSummary.id); } catch (proxyErr) {
                if ((proxyErr as { status?: number })?.status !== 404) throw proxyErr;
                // S1 (2026-08-19): create-read race. A just-imported project
                // lives in Dexie before its first server save lands, so the
                // backend-proxied read 404s (seen live: GET /api/projects/
                // 9a0a3f93… 404 during a one-now import; the id resolved fine
                // minutes later). The express path used to FAIL the load here
                // in non-batch mode. Serve the local record instead — the next
                // save creates the server row. Batch mode already tried Dexie
                // above; retrying is a harmless miss.
                try {
                    const dbMod = await import('../services/db') as any;
                    const dbLocal = dbMod.default || dbMod.db;
                    project = await dbLocal.projects.get(projectSummary.id);
                } catch { /* fall through to the original error below */ }
                if (project) {
                    console.info(`[QM] loadProjectUnchecked: served project ${projectSummary.id} from Dexie (no server copy yet — first save will create it)`);
                } else if (batchMode) {
                    // Backend doesn't have it and Dexie doesn't either —
                    // really gone. Surface a meaningful error.
                    throw new Error(`Project ${projectSummary.id} not found in Dexie or backend (queue cannot proceed)`);
                } else {
                    throw proxyErr;
                }
            }
        }
        if (!project) {
            if (!batchMode) setToast({ message: 'Project not found', type: 'error' });
            else throw new Error(`Project ${projectSummary.id} not found in Dexie or backend (queue cannot proceed)`);
            return;
        }

        // A queued cloud run must paint one coherent Project envelope. Read the
        // backend directly, owner-merge device-local media, reject any changed
        // builder/generated state, and durably cache the exact canonical row
        // before React receives it. A 404, access failure, or outage therefore
        // stops before any paid stage instead of silently reusing stale Dexie.
        if (batchMode
            && project
            && import.meta.env?.VITE_USE_BACKEND !== 'false') {
            const canonicalProject: LooseRecord = await loadCanonicalBackendProjectForBatch(
                String(projectSummary.id),
                {
                    assertCompatible: (localProject: LooseRecord, serverProject: LooseRecord) => (
                        assertProjectCloudSnapshotCompatible(
                            String(projectSummary.id),
                            localProject,
                            serverProject,
                        )
                    ),
                },
            );
            // The service repeats this inside its cache transaction. Keep a
            // hook-side gate as well so a mocked or replaced service cannot
            // paint a different Project or an incompatible envelope.
            assertProjectCloudSnapshotCompatible(
                String(projectSummary.id),
                project,
                canonicalProject,
            );
            project = canonicalProject;
        }

        // v5.46.73 stale-open guard: the fetches above can take seconds on a
        // backend-proxied project. If the user opened ANOTHER project (or a
        // reconnect-modal confirm painted one) while this open was in flight,
        // abandon it — only the latest open wins the paint.
        if (loadGeneration !== projectLoadGenerationRef.current) {
            console.info(`[QM] loadProjectUnchecked: abandoned stale open of ${project.id} (superseded by a newer project open)`);
            return;
        }

        // A docBlobId is a locator into this Chrome profile, not portable
        // Project identity. Historical cloud/cache rows can therefore carry an
        // id that is absent here or now belongs to another Project. Validate
        // those ids BEFORE painting: otherwise every later autosave asks the
        // guarded blob store for the same foreign bytes, gets refused, leaves
        // the locator intact, and repeats forever. Sentinel-owned rows are
        // claimed by the referencing Project; only proven foreign/missing refs
        // are removed. The one repair write is a narrow, version-gated patch.
        if (project.id && Array.isArray(project.documents)
            && project.documents.some((document: any) => (
                document
                && typeof document.docBlobId === 'number'
                && Number.isFinite(document.docBlobId)
            ))) {
            try {
                const repairToken = projectVersionToken(project);
                const ownershipRepair = await quarantineInvalidDocumentBlobReferences(
                    String(project.id),
                    project.documents,
                );
                if (loadGeneration !== projectLoadGenerationRef.current) {
                    console.info(`[QM] loadProjectUnchecked: abandoned stale open of ${project.id} (superseded by a newer project open)`);
                    return;
                }
                if (ownershipRepair.changed) {
                    const foreignCount = ownershipRepair.foreignRefs?.length || 0;
                    const missingCount = ownershipRepair.missingIds?.length || 0;
                    if (foreignCount > 0) {
                        console.warn(`[QM] Quarantined ${foreignCount} cross-project document blob ref(s) on load of ${project.id}`);
                    }
                    if (missingCount > 0) {
                        console.info(`[QM] ${missingCount} device-local document blob ref(s) are not present in this Chrome profile; portable document recovery remains available.`);
                    }
                    project = { ...project, documents: ownershipRepair.documents };
                    if (repairToken) {
                        const repairedAt = await persistLoadRepair(
                            String(project.id),
                            { id: project.id, documents: ownershipRepair.documents },
                            repairToken,
                            'document blob ownership repair',
                        );
                        if (loadGeneration !== projectLoadGenerationRef.current) {
                            console.info(`[QM] loadProjectUnchecked: abandoned stale open of ${project.id} (superseded by a newer project open)`);
                            return;
                        }
                        if (repairedAt) {
                            project = { ...project, updatedAt: repairedAt, updated_at: repairedAt };
                        }
                    }
                }
            } catch (error) {
                // IndexedDB unavailability cannot prove a locator foreign or
                // missing. Keep the Project metadata intact and let the normal
                // preview/recovery paths continue rather than blocking open.
                console.warn('[QM] Document blob ownership check failed (non-fatal):',
                    (error as { message?: string })?.message || error);
            }
        }

        // Durable reference-health refresh belongs at the normal Project-open
        // boundary: one call sees documents, photos, risk, WMS, scope and quote
        // references together. It is intentionally fire-and-forget so storage
        // reconciliation can never delay the operator's paint. The service
        // single-flights duplicate cockpit mounts and enforces a five-minute
        // per-Project cooldown; a full browser reload receives a fresh retry.
        if (!batchMode && project?.id) {
            const referenceHealthProjectId = String(project.id);
            void import('../services/file-upload-service')
                .then(({ reconcileProjectReferenceHealth }) => (
                    reconcileProjectReferenceHealth(referenceHealthProjectId)
                ))
                .catch(() => {
                    // Optional health refresh: direct preview remains the
                    // fallback observer and will surface structured FILE_ORPHAN.
                });
        }

        // Clean up any video File objects that leaked into storage
        if (project.videos?.some((v: any) => v.file)) {
            project.videos = project.videos.map((v: any) => ({ ...v, file: undefined, url: undefined }));
            // AWAITED, not fire-and-forget, and version-carrying. This write
            // lands BEFORE completeProjectLoad stamps the save token from
            // `project`, so leaving it in flight would stamp a version this
            // very write has already superseded — and the operator's first save
            // of a project they only opened would be refused as a foreign edit.
            // Awaiting introduces no new hazard: this path already awaits
            // getProject (and, in batch mode, the server-token probe) above.
            const scrubbedAt = await persistLoadRepair(
                project.id,
                project,
                projectVersionToken(project),
                'video File scrub',
            );
            // The awaited repair is safely keyed to A, but it creates a new
            // interleaving window after the earlier stale-open guard. If B
            // painted while A's repair was in flight, A must not clear B's
            // recovery state or hydrate its own Project over B afterward.
            if (loadGeneration !== projectLoadGenerationRef.current) {
                console.info(`[QM] loadProjectUnchecked: abandoned stale open of ${project.id} (superseded by a newer project open)`);
                return;
            }
            if (scrubbedAt) {
                // Both key shapes, for the same reason the batch probe sets
                // both: completeProjectLoad reads camel-first with a snake
                // fallback, so a lone camel would leave the stale snake value
                // in the spread copy.
                project = { ...(project as LooseRecord), updatedAt: scrubbedAt, updated_at: scrubbedAt };
            }
        }

        // ─── Edits kept on this device (outage lane, 2026-09-29) ────────────
        // A save QuoteMaker's server could not take left its envelope here.
        // Paint it back over the server row and mark the project unsaved so
        // the normal save sends it. When the row has not moved, it saves as
        // is. When it has, the painted record presents the older version the
        // edits came from, so the server refuses the save and the operator
        // gets the conflict choice (or the bookkeeping self-heal saves it).
        // A Quote Run never restores: it drives the server's copy.
        let restoredUnsavedEdits: RestoredUnsavedEdits | null = null;
        if (!batchMode && project?.id) {
            const keptEdits = await readUnsavedProjectEdits(String(project.id));
            if (loadGeneration !== projectLoadGenerationRef.current) {
                console.info(`[QM] loadProjectUnchecked: abandoned stale open of ${project.id} (superseded by a newer project open)`);
                return;
            }
            if (keptEdits) {
                const serverToken = projectVersionToken(project);
                const keptToken = keptEdits.expectedUpdatedAt ? projectVersionToken({ updatedAt: keptEdits.expectedUpdatedAt }) : null;
                const serverMoved = !!(keptToken && serverToken && keptToken !== serverToken);
                const serverRow = project;
                project = overlayKeptProjectEdits(project, keptEdits, {
                    versionToken: serverMoved ? keptToken : serverToken,
                });
                restoredUnsavedEdits = {
                    keptAt: keptEdits.keptAt,
                    serverMoved,
                    baselineSignature: keptEdits.baselineSignature,
                    serverRow,
                };
                console.info(`[QM] Restored unsaved changes to ${project.id} kept on this device at ${keptEdits.keptAt}`
                    + (serverMoved ? ' (the saved version has changed since; saving will ask before overwriting).' : '.'));
            }
        }

        // The caller's last word, after every await (see loadProject),
        // including the kept-edits read above. Merge order (2026-10-04): the
        // overlay is built first because its read is an await, and a "yes"
        // given before it could be stale by the time the project paints. The
        // overlay changes nothing on screen (it builds the record and logs)
        // and the kept edits stay in their store, so declining here still
        // leaves the screen exactly as it is and the edits for the next open.
        // The load that does paint carries restoredUnsavedEdits.
        if (typeof shouldPaint === 'function' && shouldPaint() === false) {
            console.info(`[QM] loadProjectUnchecked: ${project.id} loaded but not painted (the caller no longer wants it on screen)`);
            return;
        }
        // B (C17): the outgoing editor stays mounted and editable while this project is fetched, and painting it resets that
        // editor. If the operator typed or edited since the open was decided on, that work was never saved, discarded or
        // accepted as lost: do not paint over it. Say so, and ask again through the ordinary guard (Save & open, or Discard).
        let workedInWhileLoading = false;
        let closedWithWorkUnsaved = false;
        if (!batchMode && outgoingWorkStamp !== null && visibleWorkStamp) {
            const workStampNow = visibleWorkStamp();
            if (workStampNow !== null) {
                workedInWhileLoading = workStampNow !== outgoingWorkStamp;
            } else {
                // The editor was open when this open was decided and has closed since (Back). A closed editor is not a saved one:
                // Back hands the editor's work to the parent and starts a save of it, and painting this project claims the next
                // load generation, which would make that save stale and drop the work. Let the saves in flight settle first, then
                // paint only if nothing is left unsaved. (A newer claim ends the wait: that open owns the screen now.)
                while (isSavingProjectRef.current || pendingSaveRef.current) {
                    await new Promise((resolve) => setTimeout(resolve, 50));
                    if (loadGeneration !== projectLoadGenerationRef.current) return;
                }
                closedWithWorkUnsaved = hasUnsavedChangesLive?.() === true;
            }
        }
        if (closedWithWorkUnsaved) {
            console.info(`[QM] loadProjectUnchecked: ${project.id} loaded but not painted (the closed editor's work is not saved yet)`);
            setToast({
                message: 'Your changes to the project on screen did not finish saving, so the next project was not opened. They are still here: save them, then open it again.',
                type: 'info',
            });
            return;
        }
        // C29: the same protection for the project on screen at Stages 1 to 4, where the editor is closed, its stamp is null and the
        // check above does not run. Edits published to the parent's state since the decision, and not saved, are not painted over.
        const workedInParentWhileLoading = !batchMode && parentWorkNewerThanDecision(outgoingParent);
        if (workedInWhileLoading || workedInParentWhileLoading) {
            console.info(`[QM] loadProjectUnchecked: ${project.id} loaded but not painted (the project on screen was worked in while it loaded)`);
            setToast({
                message: 'You kept working while the next project was opening, so it was not opened and your changes are kept.',
                type: 'info',
            });
            // Ask again through the ordinary guard. The question is forced: this closure's `hasUnsavedChanges` is from the render
            // that began the open and can be false for a project that was clean then.
            void loadProject(projectSummary, { shouldPaint, forceUnsavedPrompt: true }).catch(ignoreQuoteRunActive);
            return;
        }

        setMissingFiles({ photos: [], documents: [], subfolder: null });
        setMediaRecoveryNeeded(null);
        if (batchMode) {
            console.info('[QM] loadProjectUnchecked: batchMode=true -> using unified sync-folder recovery');
            completeProjectLoad(project);
        } else {
            // 2026-05-09: local file access is resolved through the configured
            // SyncFolderService path. The old per-project reconnect modal
            // forced a second folder model and only really matched legacy
            // QuoteMaker-shaped folders; arbitrary customer structures recover
            // through completeProjectLoad() + SyncFolderService instead.
            completeProjectLoad(project, restoredUnsavedEdits ? { restoredUnsavedEdits } : undefined);
        }
    };

    // Complete project load - restores all state
    const completeProjectLoad = (
        project: LooseRecord,
        { restoredUnsavedEdits = null }: { restoredUnsavedEdits?: RestoredUnsavedEdits | null } = {},
    ) => {
        // v5.46.73: claim a fresh load generation. Any older open still in
        // flight inside loadProjectUnchecked now fails its stale-open check
        // instead of painting over this project. Covers direct callers too
        // (the reconnect-folder modal calls this without going through
        // loadProjectUnchecked).
        projectLoadGenerationRef.current += 1;
        // Opening a project leaves any new project the editor held.
        releaseNewProjectSlot();
        loadedOriginalSnapshotContextRef.current = {
            projectId: project?.id ? String(project.id) : null,
            context: getOriginalSnapshotPersistenceContext(project),
        };
        // v5.65.23 legacy PDF-visual repair: Stage 1 once promoted embedded
        // document views into photos[]. Preserve those audit rows verbatim, but
        // migrate an EXACTLY proven 4/65-style receipt onto the authoritative
        // site-photo set before any readiness/navigation consumer hydrates.
        // Stale/tampered/old-version receipts return null and stay fail-closed.
        const photoCoverageRepairBaseToken = projectVersionToken(project);
        const healedDocumentPhotoCoverage = healDocumentDerivedPhotoCoverage(
            project.photoAnalysis ?? project.photo_analysis,
            project.photos,
        );
        if (healedDocumentPhotoCoverage) {
            project = { ...project, photoAnalysis: healedDocumentPhotoCoverage };
        }
        const completedProjectLoadContext = {
            projectId: String(project.id),
            loadGeneration: projectLoadGenerationRef.current,
        };
        paintedProjectLoadContextRef.current = completedProjectLoadContext;
        if (pendingProjectHydrationFingerprintRef) {
            pendingProjectHydrationFingerprintRef.current = null;
        }
        // Project ids alone cannot identify this load: reopening the same
        // Project claims a newer generation while every older async media read
        // still carries the same id. Every recovery callback must prove both
        // the visible Project and the exact load generation before repainting,
        // persisting a repair, or raising a recovery banner.
        const isCurrentProjectLoad = () => {
            const painted = paintedProjectLoadContextRef.current;
            return currentProjectIdRef.current === project.id
                && painted?.projectId === completedProjectLoadContext.projectId
                && painted?.loadGeneration === completedProjectLoadContext.loadGeneration;
        };
        const markProjectHydrationFingerprint = (
            overrides: LooseRecord,
            liveBeforeOverrides: LooseRecord = {},
        ) => {
            if (!pendingProjectHydrationFingerprintRef || !captureProjectFingerprint) {
                // Compatibility fallback for older embedders/test harnesses.
                // Production always threads the exact receipt ref + capture.
                suppressUnsavedRef.current = true;
                return;
            }
            const previous = pendingProjectHydrationFingerprintRef.current;
            const sameLoad = previous?.projectId === completedProjectLoadContext.projectId
                && previous?.loadGeneration === completedProjectLoadContext.loadGeneration;
            const cumulativeOverrides = sameLoad
                ? { ...(previous?.overrides || {}), ...overrides }
                : { ...overrides };
            const rebasedBaselineFingerprint = rebaseProjectFingerprintWithSaveOverrides(
                savedFingerprintRef?.current ?? null,
                cumulativeOverrides,
            );
            // With no persisted baseline there is nothing safe to rebase.
            // The ordinary null-baseline/load-guard path will adopt the paint;
            // a broad suppression here could hide a concurrent builder edit.
            if (rebasedBaselineFingerprint === null) return;
            const receipt = createProjectHydrationFingerprintReceipt({
                ...completedProjectLoadContext,
                beforeFingerprint: captureProjectFingerprint(liveBeforeOverrides),
                afterFingerprint: captureProjectFingerprint({
                    ...liveBeforeOverrides,
                    ...cumulativeOverrides,
                }),
                rebasedBaselineFingerprint,
                overrides: cumulativeOverrides,
            });
            if (!receipt) {
                return;
            }
            pendingProjectHydrationFingerprintRef.current = receipt;
        };
        setManualSaveRetryLatch(null);
        // ─── Cancel ALL in-flight AI from the previous project ───────────
        // Without this, stale analysis/summary results can land on the new
        // project's state after the load completes. The reason names the
        // opened project (see resetForm for why it is never left bare).
        try { projectAbortRef.current.abort(new DOMException(`Project work aborted: project ${project?.id ?? '(unknown)'} was opened.`, 'AbortError')); } catch {}
        projectAbortRef.current = new AbortController();
        try { projectPersistenceAbortRef.current.abort('project-load'); } catch {}
        projectPersistenceAbortRef.current = new AbortController();
        // Loading a project is a fresh pipeline context: release the
        // STOP-button halt latch (a STOP on the previous project must not
        // silence the loaded project's auto cascade).
        if (aiPipelineHaltedRef) aiPipelineHaltedRef.current = false;
        if (autoAssignAbortRef.current) {
            try { autoAssignAbortRef.current.abort(); } catch {}
            autoAssignAbortRef.current = null;
        }
        // v5.19.24: reset photo-id seen-set + busy mirror on project switch
        // for the same reason resetForm does.
        photoAssignBusyRef.current = false;
        setPhotoAssignBusy(false);
        // QW-PHOTO-REOPEN-FIX (2026-06-17): on a FRESH project resetForm() empties
        // this set so the first photos get auto-assigned. But on LOADING an existing
        // project whose photos are ALREADY assigned to scope, an empty set makes the
        // photo-auto-assign watcher (effect above) treat every loaded photo as "new"
        // and re-run the NON-DETERMINISTIC AI assignment, changing scope[].images +
        // the hero vs what was saved (the "photos are different on re-open" bug).
        // Seed the watcher's "already-assigned" set so re-opening an assigned quote
        // does NOT re-run the non-deterministic photo auto-assign over it (the
        // "photos different on re-open" bug). seenPhotoIdsForLoadedProject seeds the
        // loaded photo ids only when the scope already carries images; an unassigned
        // draft seeds empty so its first real auto-assign can still run. (helper +
        // tests: src/utils/photo-reopen-seed.js, src/__tests__/photo-reopen-seed.test.js)
        lastAssignedPhotoIdsRef.current = seenPhotoIdsForLoadedProject(project);
        // Release concurrency guards so the new project can run AI
        isAnalyzingRef.current = false;
        isSummarizingRef.current = false;
        inspectorNotesAiPausedRef.current = false;
        setInspectorNotesAiPaused(false);
        lastAutoProjectFocusRef.current = String(
            project?.projectSummary?.scope_objective
            || project?.projectSummary?.scopeObjective
            || ''
        ).trim();

        isLoadingProjectRef.current = true; // Block auto-analysis during hydration
        suppressUnsavedRef.current = true;
        // Time-windowed dirty-tracker suppression. Anything that flows through
        // the dirty-effect within 2s of a project load is part of hydration
        // (migrations, async URL rehydrate, normalize calls, modal mount
        // effects, rAF-deferred editor open). Without this, the user
        // sees a "discard changes?" prompt on a project they never edited.
        projectLoadGuardUntilRef.current = Date.now() + 2000;
        const previousProjectId = currentProjectIdRef.current;
        if (previousProjectId && String(previousProjectId) !== String(project.id)) {
            revokeProjectDocumentUrls(previousProjectId);
        }
        setCurrentProjectId(project.id);
        PipelineMonitor.setProjectId(project.id);
        // Restore Stage 1: Data Entry. UNCONDITIONAL, like photos below
        // (spec 2026-10-07 F27): a record without formData must not keep the
        // previous job's client and address on screen or in the Assistant.
        setFormData(project.formData && typeof project.formData === 'object' ? project.formData : {});

        // Load photos immediately for display, then repair persistence in the
        // background. Backend mode prefers S3/file_metadata; local-only Blob
        // migration is only a fallback when the backend is disabled.
        const loadedPhotos = Array.isArray(project.photos) ? project.photos : [];
        // v5.46.72 (cross-project photo-bleed fix): UNCONDITIONAL reset. The old
        // `if (project.photos)` skip meant a record without a photos field
        // silently kept the PREVIOUS project's photos on screen — no race
        // needed, just opening such a project from Project History after a
        // photo-bearing one.
        photosRef.current = loadedPhotos;
        setPhotos(loadedPhotos);
        // v5.46.72 (blob-ownership sanitise): photo_blobs ids are GLOBAL Dexie
        // auto-increments, so after a local-DB reset (fresh browser profile /
        // machine restore) an old record's blobId can point at ANOTHER
        // project's bytes — the display path never passes the store's
        // expectedProjectId guard. Strip refs this project does not own so the
        // photo falls back to s3Key/inline (or the recovery banner) instead of
        // silently rendering the wrong project's image. In-memory only; the
        // record itself is not rewritten by this pass.
        if (project.id && loadedPhotos.some(p => p && p.blobId != null)) {
            import('../services/photo-blob-store')
                .then(async ({ getProjectBlobIds }) => {
                    const owned = new Set((await getProjectBlobIds(project.id)).map(r => r.id));
                    if (!isCurrentProjectLoad()) return;
                    let strippedCount = 0;
                    const cleaned = loadedPhotos.map(p => {
                        if (p && p.blobId != null && !owned.has(p.blobId)) {
                            strippedCount += 1;
                            return { ...p, blobId: undefined, _blobOwnershipMismatch: true };
                        }
                        return p;
                    });
                    if (strippedCount > 0 && isCurrentProjectLoad()) {
                        setPhotos((currentPhotos: any[]) => {
                            if (!isCurrentProjectLoad() || currentPhotos !== loadedPhotos) {
                                console.info('[QM] Photo ownership proof finished after the photo lane changed; skipped stale repaint.');
                                return currentPhotos;
                            }
                            console.warn(`[QM] Stripped ${strippedCount} cross-project photo blob ref(s) on load of ${project.id}`);
                            markProjectHydrationFingerprint(
                                { photos: cleaned },
                                { photos: currentPhotos },
                            );
                            photosRef.current = cleaned;
                            return cleaned;
                        });
                    }
                })
                .catch(() => { /* defensive pass — never block a project load */ });
        }
        const loadPhotoCatalog = buildPhotoCatalog(loadedPhotos);
        const hydrateScopeImageRefs = (items: any[] = []) => {
            if (!Array.isArray(items)) return items;
            return items.map(item => {
                if (!item || typeof item !== 'object' || !Array.isArray(item.images)) return item;
                return {
                    ...item,
                    images: item.images.map((img: any) => mergePortablePhotoFields(img, findCatalogPhoto(img, loadPhotoCatalog))),
                };
            });
        };
        const backendMode = import.meta.env?.VITE_USE_BACKEND !== 'false';
        const hasRetryablePhotoPayloads = loadedPhotos.some(p =>
            p && typeof p === 'object'
            && !p.s3Key
            && !p.fileMetadataId
            && (
                p.blobId != null
                || (typeof p.data === 'string' && p.data.startsWith('data:'))
                || (typeof p.dataUrl === 'string' && p.dataUrl.startsWith('data:'))
            )
        );
        if (backendMode && hasRetryablePhotoPayloads) {
            makePhotoArrayPortableForPersistence(loadedPhotos, project.id)
                .then((portablePhotos) => {
                    if (!isCurrentProjectLoad()) {
                        console.info('[QM] Backend photo repair finished after this Project was reopened; skipped stale repaint and record write.');
                        return;
                    }
                    const repairedCount = portablePhotos.reduce((count: number, photo: any, index: number) => {
                        const before = loadedPhotos[index];
                        const hadCloudBacking = !!(before?.s3Key || before?.fileMetadataId);
                        const hasCloudBacking = !!(photo?.s3Key || photo?.fileMetadataId);
                        return count + (!hadCloudBacking && hasCloudBacking ? 1 : 0);
                    }, 0);
                    if (repairedCount === 0) {
                        console.info('[QM] Backend photo repair made no durable change; leaving the project record untouched for a later retry.');
                        return;
                    }
                    // v5.46.72 CRITICAL stale-load guard (operator report: photos
                    // from ANOTHER project shown after opening from Project
                    // History): this repair runs sequential S3 uploads + backend
                    // validation per photo — seconds-to-minutes on an old
                    // project with local-only or inline payloads. If the user
                    // opened a DIFFERENT project meanwhile, painting these photos (or
                    // touching the global dirty/auto-assign refs) would bleed
                    // this project's photos into the one now on screen. Same
                    // guard the documents rehydrate below has carried since
                    // v5.46.68 — the photo paths never got it.
                    const stillCurrent = isCurrentProjectLoad();
                    const photosUnedited = photosRef.current === loadedPhotos;
                    if (stillCurrent && photosUnedited) {
                        markProjectHydrationFingerprint({ photos: portablePhotos });
                        photosRef.current = portablePhotos;
                        setPhotos(portablePhotos);
                        // QW-PHOTO-REOPEN-FIX: this repair re-keys photos (new s3Key /
                        // blobId), which changes derivePhotoId for any photo lacking a
                        // stable `id`. Re-seed the auto-assign dedup set against the
                        // post-repair photos so the watcher does not treat them as
                        // "new" and re-run the non-deterministic assignment over a
                        // restored, already-assigned quote.
                        lastAssignedPhotoIdsRef.current = seenPhotoIdsForLoadedProject({ ...project, photos: portablePhotos });
                        // Persist only the repaired lane. The data-service merges
                        // this partial patch with the current record, avoiding a
                        // stale full-project write after a slow upload.
                        //
                        // The base token is read from baseUpdatedAtRef, and the
                        // read is safe HERE specifically: this callback is a
                        // .then, so it cannot run until completeProjectLoad's
                        // synchronous body — which stamps that ref from the
                        // record it painted — has finished. Reading it any
                        // earlier would pick up the PREVIOUS project's version.
                        void persistLoadRepair(
                            project.id,
                            { id: project.id, photos: portablePhotos },
                            baseUpdatedAtRef.current,
                            'backend photo repair',
                        );
                    } else {
                        console.info('[QM] Backend photo repair finished after the project or its photos changed; skipped stale repaint and record write.');
                    }
                })
                .catch(err => console.warn('[QM] Backend photo repair failed (inline fallback active):', err?.message));
        }
        // Trigger local migration for photos with inline base64 OR dead blob: URLs.
        // Guards the dirty tracker because the migration setPhotos is
        // infrastructure (base64 → blobId ref swap), not a builder edit -
        // otherwise the "Save" button immediately lights up as dirty on load.
        if (!backendMode && loadedPhotos.some(p => p.data && typeof p.data === 'string' && !p.blobId)) {
            migrateProjectPhotos(project.id, loadedPhotos)
                .then(({ photos: migrated, migrated: count }: any) => {
                    if (!isCurrentProjectLoad()) {
                        console.info('[QM] Local photo migration finished after this Project was reopened; skipped stale repaint and record write.');
                        return;
                    }
                    if (count > 0) {
                        console.log(`[QM] Migrated ${count} photos from base64 → Blob store`);
                        // v5.46.72 stale-load guard (same class as the backend
                        // repair above): never paint a previous project's
                        // migrated photos over the project the user has since
                        // opened. The record save keeps the migration durable
                        // for THIS project's row either way.
                        const stillCurrent = isCurrentProjectLoad();
                        const photosUnedited = photosRef.current === loadedPhotos;
                        if (stillCurrent && photosUnedited) {
                            markProjectHydrationFingerprint({ photos: migrated });
                            photosRef.current = migrated;
                            setPhotos(migrated); // Replaces base64 with blobId refs
                            // QW-PHOTO-REOPEN-FIX: re-seed the auto-assign dedup set
                            // against the post-migration photo ids (base64 -> blobId
                            // changes derivePhotoId) so a restored, already-assigned
                            // quote is not re-assigned by the watcher.
                            lastAssignedPhotoIdsRef.current = seenPhotoIdsForLoadedProject({ ...project, photos: migrated });
                            // Version-carrying and adopted on success, for the
                            // same reason as the backend repair above — and with
                            // the same .then ordering that makes the ref read
                            // safe here.
                            void persistLoadRepair(
                                project.id,
                                { id: project.id, photos: migrated },
                                baseUpdatedAtRef.current,
                                'local photo migration',
                            );
                        } else {
                            console.info('[QM] Local photo migration finished after the project or its photos changed; skipped stale repaint and record write.');
                        }
                    }
                })
                .catch((err: any) => console.warn('[QM] Photo migration failed (base64 fallback active):', err?.message));
        }
        const normalizedDocuments = project.documents ? normalizeDocumentsForProjectLoad(project.documents) : [];
        // v5.46.72: unconditional reset (same class as the photos fix above) —
        // a record without a documents field must not keep the previous
        // project's reference files on screen.
        setDocuments(normalizedDocuments);
        if (project.documents) {
            // Rehydrate reference-file previews from the local document blob store
            // (docBlobId) for any doc whose inline/S3 preview was stripped on the
            // last save — non-blocking, refreshes the Cockpit when the bytes are
            // read back. Guarded so a fast project-switch can't clobber the new
            // project's documents with a late resolve for the previous one.
            if (project.id) {
                rehydrateProjectDocuments(project.id, normalizedDocuments)
                    .then(({ documents: rehydrated, rehydrated: n }: any) => {
                        if (n > 0 && isCurrentProjectLoad()) {
                            setDocuments((currentDocuments: any[]) => {
                                if (!isCurrentProjectLoad() || currentDocuments !== normalizedDocuments) {
                                    console.info('[QM] Document preview hydration finished after the document lane changed; skipped stale repaint.');
                                    return currentDocuments;
                                }
                                markProjectHydrationFingerprint(
                                    { documents: rehydrated },
                                    { documents: currentDocuments },
                                );
                                return rehydrated;
                            });
                        } else if (n > 0 && currentProjectIdRef.current !== project.id) {
                            // The IndexedDB read may finish after the operator
                            // switched projects. getDocumentUrl has already
                            // created/cached the object URL by then, so release
                            // that stale project's late resource explicitly.
                            revokeProjectDocumentUrls(project.id);
                        }
                    })
                    .catch((err: any) => console.warn('[QM] document preview rehydrate failed:', err?.message));
            }
        }
        // Revoke old video blob URLs before replacing
        videos.forEach(v => { if (v.url) revokeVideoObjectUrl(v.url); });
        const loadedVideos = project.videos || [];
        setVideos(loadedVideos);
        // v5.46.73 (video sibling of the v5.46.72 photo sanitise above):
        // video_blobs ids are the same GLOBAL Dexie auto-increment class, so
        // after a local-DB reset an old record's videoBlobId can point at
        // ANOTHER project's footage. Strip refs this project does not own so
        // playback falls back to the sync folder / cloud proxy / item recovery
        // instead of silently playing the wrong project's video. In-memory
        // only; the record itself is not rewritten by this pass.
        if (project.id && loadedVideos.some((v: any) => v && v.videoBlobId != null)) {
            import('../services/video-blob-store')
                .then(async ({ getProjectVideoBlobIds, inspectVideoBlobReferences }) => {
                    const owned = new Set<number>((await getProjectVideoBlobIds(project.id)).map(r => r.id));
                    if (!isCurrentProjectLoad()) return;
                    const unowned: number[] = loadedVideos.flatMap((v: any) => (
                        v
                        && typeof v.videoBlobId === 'number'
                        && Number.isFinite(v.videoBlobId)
                        && !owned.has(v.videoBlobId)
                            ? [v.videoBlobId]
                            : []
                    ));
                    const unresolved: number[] = [...new Set<number>(unowned.filter(id => !owned.has(id)))];
                    const ownership = await inspectVideoBlobReferences(unresolved, project.id);
                    if (!isCurrentProjectLoad()) return;
                    for (const id of ownership.ownedIds || []) owned.add(id);
                    const missingIds = new Set([
                        ...(ownership.missingIds || []),
                        // Sentinel rows are unproven and use the same portable
                        // fallback as a browser-local miss.
                        ...(ownership.sentinelIds || []),
                    ]);
                    const foreignIds = new Set((ownership.foreignRefs || []).map((ref: any) => ref.id));
                    let foreignCount = 0;
                    let missingCount = 0;
                    const cleaned = loadedVideos.map((v: any) => {
                        if (v && v.videoBlobId != null && !owned.has(v.videoBlobId)) {
                            if (missingIds.has(v.videoBlobId) || !foreignIds.has(v.videoBlobId)) {
                                missingCount += 1;
                                return { ...v, videoBlobId: undefined, _localBlobUnavailable: true };
                            }
                            foreignCount += 1;
                            return { ...v, videoBlobId: undefined, _blobOwnershipMismatch: true };
                        }
                        return v;
                    });
                    if ((foreignCount > 0 || missingCount > 0) && isCurrentProjectLoad()) {
                        setVideos((currentVideos: any[]) => {
                            if (!isCurrentProjectLoad() || currentVideos !== loadedVideos) {
                                console.info('[QM] Video ownership proof finished after the video lane changed; skipped stale repaint.');
                                return currentVideos;
                            }
                            if (foreignCount > 0) {
                                console.warn(`[QM] Stripped ${foreignCount} cross-project video blob ref(s) on load of ${project.id}`);
                            }
                            if (missingCount > 0) {
                                console.info(`[QM] ${missingCount} device-local video blob ref(s) are not present in this Chrome profile; portable video recovery remains available.`);
                            }
                            markProjectHydrationFingerprint(
                                { videos: cleaned },
                                { videos: currentVideos },
                            );
                            videosRef.current = cleaned;
                            return cleaned;
                        });
                        // Video access is deliberately item-scoped. If this
                        // ownership proof removes the last browser-local blob,
                        // the video row quietly indicates that it plays from
                        // its original folder. Recovery is offered only after
                        // the builder opens that exact video.
                    }
                })
                .catch(() => { /* defensive pass — never block a project load */ });
        }
        setVideoTranscripts(project.videoTranscripts || []);
        setSelectedHeroImage(project.selectedHeroImage || null);

        // 2026-05-31 (Peter): use the shared sync-recovery predicates so the
        // trigger gate and the displayed counts can never disagree, and so a
        // backend-backed PDF (pdfS3Key / fileMetadataId, with pdfDataUrl
        // stripped on save and re-fetched on demand) is correctly treated as
        // previewable, NOT missing. Previously this keyed off pdfDataUrl alone
        // and flagged every reloaded backend PDF, surfacing the false-positive
        // "Files missing from project folder" banner. See
        // src/utils/sync-recovery-detection.js.
        const needsSyncPdfRecovery = normalizedDocuments.some(pdfNeedsLocalRecovery);
        const needsSyncPhotoRecovery = loadedPhotos.some(photoNeedsLocalRecovery);
        if (needsSyncPdfRecovery || needsSyncPhotoRecovery) {
            // Customer-Zero issue D (2026-05-15): when recovery IS needed but
            // the folder handle is missing, SyncFolderService.recoverMissingFiles
            // silently returns empty (sync-folder.js:1570-1572 — gates on
            // ensurePermission/getHandle). Pre-2026-05-15 the user had no path
            // to re-grant the handle from the project view; they had to bounce
            // to Settings → File sync. Now: probe the handle and, if absent,
            // raise a banner-style flag the cockpit reads to render an
            // in-flow "Reconnect folder" CTA. The actual recovery path stays
            // wired below — the moment a handle is granted (via the banner
            // button or any other path), the next recovery attempt succeeds.
            (async () => {
                try {
                    const configured = await SyncFolderService.isConfigured();
                    // v5.46.72 stale-load guard: this whole recovery flow reads
                    // the sync folder (slow) and then patches photos/documents
                    // BY ARRAY INDEX — grafting another project's files
                    // onto whatever is on screen if the user has switched
                    // projects meanwhile. Same guard as the photo repair above.
                    if (!isCurrentProjectLoad()) return;
                    if (!configured) {
                        setMediaRecoveryNeeded({
                            projectId: project.id,
                            folderName: project?.projectFolder?.parentName
                                || project?.projectFolder?.name
                                || null,
                            pdfCount: normalizedDocuments.filter(pdfNeedsLocalRecovery).length,
                            photoCount: loadedPhotos.filter(photoNeedsLocalRecovery).length,
                            videoCount: 0,
                        });
                        return;
                    }
                } catch (handleCheckErr) {
                    console.warn('[QM] Sync-folder handle probe failed:', (handleCheckErr as { message?: string })?.message);
                }
                if (!isCurrentProjectLoad()) return;
                SyncFolderService.recoverMissingFiles(project, loadedPhotos, normalizedDocuments, [])
                    .then(async (recovered: any) => {
                    // v5.46.72 stale-load guard (see the handle-probe guard above).
                    if (!isCurrentProjectLoad()) return;
                    if (!recovered?.documents?.length && !recovered?.photos?.length) {
                        // CZ-B (2026-05-15): handle WAS configured, but recovery
                        // returned nothing — files have been renamed, moved, or
                        // deleted from the connected folder. Sharper messaging
                        // than the silent no-op pre-CZ-B: surface a banner
                        // saying so, with the folder name + missing counts.
                        const folderName = await (async () => {
                            try { return await SyncFolderService.getFolderName(); }
                            catch { return null; }
                        })();
                        if (!isCurrentProjectLoad()) return;
                        setMediaRecoveryNeeded({
                            projectId: project.id,
                            folderName: folderName || project?.projectFolder?.parentName || null,
                            handleConfigured: true,   // distinguishes from the no-handle case (D)
                            pdfCount: normalizedDocuments.filter(pdfNeedsLocalRecovery).length,
                            photoCount: loadedPhotos.filter(photoNeedsLocalRecovery).length,
                            videoCount: 0,
                        });
                        return;
                    }
                    if (recovered.documents?.length) {
                        setDocuments(prev => {
                            if (!isCurrentProjectLoad() || prev !== normalizedDocuments) {
                                console.info('[QM] Sync-folder document recovery finished after the document lane changed; skipped stale index patch.');
                                return prev;
                            }
                            const next = prev.map((doc: any, index: any) => {
                                const match = recovered.documents.find((d: any) => d.index === index);
                                return match ? {
                                    ...doc,
                                    pdfDataUrl: match.pdfDataUrl,
                                    pdfBlob: match.pdfBlob,
                                    syncFileName: match.syncFileName,
                                    pdfPreviewUnavailableReason: undefined,
                                } : doc;
                            });
                            markProjectHydrationFingerprint(
                                { documents: next },
                                { documents: prev },
                            );
                            return next;
                        });
                    }

                    if (recovered.photos?.length) {
                        const photoUpdates: any[] = [];
                        for (const item of recovered.photos) {
                            if (!isCurrentProjectLoad()) return;
                            try {
                                const blobId = await storePhotoBlob(item.blob, project.id, {
                                    width: item.meta?.width,
                                    height: item.meta?.height,
                                    recoveredFrom: 'local-sync-folder',
                                });
                                if (!isCurrentProjectLoad()) return;
                                photoUpdates.push({
                                    index: item.index,
                                    blobId,
                                    sizeKB: Math.round((item.blob?.size || 0) / 1024),
                                });
                            } catch (photoRecoverErr) {
                                console.warn('[QM] Sync-folder photo recovery could not persist blob:', (photoRecoverErr as { message?: string })?.message);
                            }
                        }
                        // Re-check after the per-photo await loop above — the
                        // storePhotoBlob writes are keyed to project.id (safe),
                        // but the index-patch below must only touch the arrays
                        // while THIS project is still the one on screen.
                        if (photoUpdates.length && isCurrentProjectLoad()) {
                            setPhotos(prev => {
                                if (!isCurrentProjectLoad() || prev !== loadedPhotos) {
                                    console.info('[QM] Sync-folder photo recovery finished after the photo lane changed; skipped stale index patch.');
                                    return prev;
                                }
                                const next = prev.map((photo: any, index: any) => {
                                const match = photoUpdates.find(p => p.index === index);
                                return match ? {
                                    ...photo,
                                    blobId: match.blobId,
                                    sizeKB: match.sizeKB || photo.sizeKB,
                                    data: undefined,
                                    dataUrl: undefined,
                                    _dataLost: false,
                                } : photo;
                                });
                                markProjectHydrationFingerprint(
                                    { photos: next },
                                    { photos: prev },
                                );
                                photosRef.current = next;
                                return next;
                            });
                        }

                        // Cloud repair (2026-08-14, D8 durability): the blobs
                        // recovered above exist on THIS device only. Push each
                        // back to its EXISTING key via /direct-upload (row-
                        // exists + size verified server-side). Fire-and-forget,
                        // never a load blocker; failures retry on next load.
                        // No project save happens, so the optimistic-
                        // concurrency guard is untouched.
                        void (async () => {
                            try {
                                const { rescueUploadBlob } = await import('../services/file-upload-service');
                                if (!isCurrentProjectLoad()) return;
                                let repaired = 0;
                                for (const item of recovered.photos) {
                                    if (!isCurrentProjectLoad()) return;
                                    const key = item.meta?.s3Key || item.meta?.s3_key;
                                    if (!key || !(item.blob instanceof Blob)) continue;
                                    if (await rescueUploadBlob(key, item.blob, item.meta?.cloudMedia)) repaired += 1;
                                    if (!isCurrentProjectLoad()) return;
                                }
                                if (repaired > 0) {
                                    console.log(`[QM] Cloud repair: ${repaired} recovered photo(s) uploaded back to storage — visible on every device again.`);
                                }
                            } catch (repairErr) {
                                console.warn('[QM] Cloud repair pass failed (non-fatal):', (repairErr as { message?: string })?.message);
                            }
                        })();
                    }

                })
                .catch((err: any) => {
                    if (!isCurrentProjectLoad()) return;
                    console.warn('[QM] Sync-folder media recovery failed (non-fatal):', err?.message);
                    setMediaRecoveryNeeded({
                        projectId: project.id,
                        folderName: project?.projectFolder?.parentName
                            || project?.projectFolder?.name
                            || null,
                        handleConfigured: true,
                        pdfCount: normalizedDocuments.filter(pdfNeedsLocalRecovery).length,
                        photoCount: loadedPhotos.filter(photoNeedsLocalRecovery).length,
                        videoCount: 0,
                        recoveryError: 'QuoteMaker could not read the configured folder during file recovery. Reconnect the folder and try again.',
                    });
                });
            })();
        }

        // Restore Stage 1.5: Project Summary (best-guess pre-analysis pass)
        //
        // ─── `normalizeToSnake` heal-on-load (Peter 2026-04-20) ───────────
        // Legacy projects saved before the `OPAQUE_JSONB_KEYS` allowlist
        // landed in data-service-api.js carry camelCased internal keys
        // (`detectedRisks` vs the intended `detected_risks`, `chosenApproach`
        // vs `chosen_approach`, etc.) because the old `convertKeysToCamel`
        // recursed into AI-authored blobs. Every downstream consumer expects
        // snake_case as authored by the AI prompts - Stage 2 reads
        // `analysis.detected_risks`, Stage 3 reads
        // `projectStrategy.chosen_rationale`, costing reads
        // `costingSheet.structured.procurement`, etc. We heal those fields
        // here on read. `normalizeToSnake` is idempotent for pure-AI blobs:
        // already-snake keys have no uppercase letters, so the walk is a
        // no-op on fresh records saved under the post-allowlist path.
        //
        // riskAnalysis, photoAnalysis, projectSummary and projectStrategy go
        // through `healLegacyCamelCasedBlob` instead: it heals only a blob the
        // old converter damaged and otherwise keeps every key as stored. Those
        // four now carry code-authored camelCase keys (riskEvidenceFingerprint,
        // summaryEvidenceFingerprint, batchCount, fallbackReason, ...) and are
        // hashed key-for-key by the Stage 2/3 receipts, so the hydrated state
        // must equal the stored row or the next save "changes" the inputs
        // (2026-10-04 UAT: a Quote Run resumed at Stage 3 failed with
        // WMS_SOURCE_INPUT_CHANGED).
        //
        // IMPORTANT - the following fields are NOT normalised because they
        // contain code-authored camelCase keys that consumers read verbatim:
        //   • `project.wms`              → activities carry `costBreakdown`,
        //                                  `costBreakdownLocked`, materials
        //                                  carry `unitRate`; 224+ reads in src.
        //   • `project.quoteData`        → `header.heroImage`, `meta.quoteNumber`,
        //                                  `customSignature`, `isGstInclusive`,
        //                                  scope item camelCase fields.
        //   • `project.appliedLearnings` → entries are canonically camelCase
        //                                  (`appliedAt`, `appliedBy`,
        //                                  `contradictReason` per
        //                                  utils/applied-learnings.js).
        //   • `project.aiGeneratedItems` → scope-item snapshots (camelCase).
        //   • `project.originalAiScope`  → scope-item snapshots (camelCase).
        //   • `project.aiContext` / `project.kpiReport` → not restored to
        //                                  state in this function at all;
        //                                  constructed fresh at save time.
        // Those blobs either pre-date the convertKeysToCamel damage path
        // (stored by our own code) or were never recursively rewritten.
        if (project.projectSummary) {
            setProjectSummary(healLegacyCamelCasedBlob(project.projectSummary));
            setSummaryNeedsRefresh(false);
        } else {
            setProjectSummary(null);
        }
        setProjectWorkflowMode(normalizeProjectWorkflowMode(project.workflowMode ?? project.workflow_mode));
        setSummaryError(null);
        setSummaryRerunHold?.(null);

        // Restore Stage 2: Risk Scan
        setAnalysis(project.riskAnalysis ? healLegacyCamelCasedBlob(project.riskAnalysis) : null);
        setPhotoAnalysis(project.photoAnalysis ? healLegacyCamelCasedBlob(project.photoAnalysis) : null);
        // `diagnosticResult` mixes code-authored camelCase envelope keys with
        // prompt-authored snake_case payloads, so it must round-trip verbatim.
        // Normalising the whole object would rename decisionPath,
        // projectDiagnosis, photoForensicResult, etc. and make the saved receipt
        // look empty to downstream consumers.
        setDiagnosticResult(
            project.aiContext?.diagnosticResult
            ?? project.ai_context?.diagnosticResult
            ?? project.diagnosticResult
            ?? null,
        );
        // H1: the on-demand Risk Report, verbatim like diagnosticResult (its
        // fields are prompt-authored snake_case inside a camelCase envelope).
        setRiskReport?.(
            project.aiContext?.riskReport
            ?? project.ai_context?.riskReport
            ?? null,
        );

        // Restore Stage 3 & WMS state. Order matters: build a local scope+wms
        // reference first so we can run the activity-mirror sync inline before
        // committing to state. Without the inline sync, scope items whose
        // activity costBreakdowns have drifted on disk (WMS regenerated out
        // of band, or from a prior session) would render stale amounts for a
        // render tick until a downstream effect catches up.
        // ensureItemIds migrates legacy scope items that pre-date stable UUIDs.
        // NOTE: `project.wms` is intentionally NOT normalised - see comment
        // above at the top of the restore block.
        let restoredWms = project.wms || null;
        let restoredScope = hydrateScopeImageRefs(ensureItemIds(project.scope || []));
        const allowDraftPricingRestoreRepair = isUnissuedPreStage5Draft(project);
        if (allowDraftPricingRestoreRepair) {
            try {
                restoredScope = syncScopeFromActivities(restoredScope, restoredWms);
                // The activity mirror above is a deterministic restore migration:
                // it can backfill method fields that older saved scope rows did not
                // carry. Keep the WMS projection bound to the exact scope we are
                // committing into React, otherwise a reopen makes a completed
                // Stage 3 look stale and blocks the ordinary Stage 5 author even
                // though no operator or AI changed the method.
                if (restoredWms?.structured && restoredScope.length > 0) {
                    restoredWms = bindWmsToScopeProjection(restoredWms, restoredScope, {
                        source: 'load-activity-mirror',
                    });
                }
            } catch (syncErr) {
                console.warn('[load] syncScopeFromActivities failed (non-fatal):', (syncErr as { message?: string })?.message);
            }
        }
        // Commit the synced scope immediately so the UI restores fast…
        // The Stage 5 read-only opener runs synchronously later in this same
        // load transaction. Mirror the restored topology now so it cannot seed
        // its drag/order baseline from the previously-open project while React
        // is still batching setScope.
        scopeRef.current = restoredScope;
        setScope(restoredScope);
        // …then DURABLE FIX (2026-06-18): re-stamp catalogue provenance.
        // This restore repair is an integrity migration, not an optional
        // generation strategy: persisted pricing must reopen identically even
        // when a legacy catalogue-first developer switch is disabled.
        // syncScopeFromActivities (above) re-mirrors each non-locked item's
        // costBreakdown from its WMS activities, whose RAW cost lines carry no
        // pricingItemId / _rateSource / rateIsFinalIncGst — so on re-open the
        // catalogue provenance the original costing stamped is stripped (and the
        // strip then gets saved). That broke the Stage-4 Price Working rate-book
        // LINKS, and the MARGIN: cost-math-adapter's mapScopeToInputs partitions on
        // rateIsFinalIncGst, so once the flag is gone the whole final-inc-GST
        // charge-out total is mis-counted as raw cost (a profitable job reads as a
        // loss). The catalogue match is PROVENANCE, not price — re-derive it from
        // the rate book for EVERY item (incl. editedBy:'user', which
        // recomputeWmsScopeCost skips — see its line ~37), reconciling
        // directCostTotal. A generated committed amount is then raised only
        // when that refreshed ledger proves it is below the Stage 4 floor;
        // reviewable builder/operator locks remain byte-exact. Own async IIFE because
        // the matcher is async + this restore scope is sync (same pattern as the
        // media-recovery block above); re-commits scope when done, guarded so a
        // project switch mid-flight can't clobber the newly-opened project.
        const restampForProjectId = project.id;
        if (allowDraftPricingRestoreRepair) (async () => {
            try {
                const [{ applyCatalogueMatchToBreakdown }, { BusinessPricingService }] = await Promise.all([
                    import('../services/pricing-match-applier') as any,
                    import('../services/pricing'),
                ]);
                const bizData = await BusinessPricingService.getDataReadOnly({ failIfMissing: true });
                const hasDetailLines = (cb: any) => cb && ['materials', 'labour', 'equipment', 'subcontractors', 'otherCosts']
                    .some(k => Array.isArray(cb[k]) && cb[k].length > 0);
                let restamped = 0;
                const next = await Promise.all(restoredScope.map(async (item) => {
                    const cb = item?.costBreakdown;
                    // Only reviewable builder/operator authority stays byte-exact.
                    // A generated amountLocked carrier is still system output and
                    // must re-link before the deterministic floor is evaluated.
                    if (hasReviewableManualPriceAuthority(item) || !hasDetailLines(cb)) return item;
                    try {
                        const enriched = await applyCatalogueMatchToBreakdown(cb, bizData, {
                            authorityOnly: true,
                            allowDiscoveryWrites: false,
                        });
                        // Never let an empty, partial, or unresolved authority
                        // match repaint a stored builder ledger during reopen.
                        // Reopen is a provenance repair, not a repricing action.
                        const unresolvedLines = Array.isArray(enriched.unresolvedLines)
                            ? enriched.unresolvedLines
                            : [];
                        if (Number(enriched.lineSumDirectCost) > 0 && unresolvedLines.length === 0) {
                            restamped++;
                            return { ...item, costBreakdown: {
                                ...cb,
                                materials: enriched.materials,
                                labour: enriched.labour,
                                equipment: enriched.equipment,
                                subcontractors: enriched.subcontractors,
                                otherCosts: enriched.otherCosts,
                                directCostTotal: enriched.lineSumDirectCost,
                                pricingFingerprint: enriched.pricingFingerprint ?? cb.pricingFingerprint,
                            } };
                        }
                    } catch (itemErr) {
                        console.error('[load] Catalogue re-stamp failed for one item:', (itemErr as { message?: string })?.message);
                    }
                    return item;
                }));
                // Only commit if the user is still on the project we re-stamped.
                if (restamped > 0 && currentProjectIdRef.current === restampForProjectId) {
                    const reconciled = reconcileGeneratedAmountsToPricingFloor(
                        next,
                        project.scopeGstInclusive ?? true,
                    );
                    const candidateAdjusted = reconciled.reduce((count, item, index) => (
                        count + (item !== next[index] ? 1 : 0)
                    ), 0);
                    setScope((currentScope: LooseRecord[]) => {
                        // The matcher is asynchronous. Same-project operator edits
                        // and other enrichments replace the array immutably; only
                        // the exact baseline painted by this load is ours to repair.
                        if (currentScope !== restoredScope) {
                            console.info('[load] Catalogue re-stamp finished after scope changed; skipped stale pricing repaint.');
                            return currentScope;
                        }
                        return reconciled;
                    });
                    console.log(`[load] Post-restore catalogue re-stamp prepared: ${restamped}/${next.length} item(s) re-linked to the rate book; ${candidateAdjusted} generated amount(s) eligible for baseline-owned Stage 4 floor repair`);
                }
            } catch (recErr) {
                console.error('[load] Post-restore catalogue re-stamp FAILED — rate-book links may be stale until the next edit:', (recErr as { message?: string })?.message);
            }
        })();
        // M3: restore the project-level WMS programme narrative. Defaults to null
        // for projects saved before Stage 2.5 existed.
        setWorkMethodOverallProgramme(project.workMethodOverallProgramme || null);
        // The label repair below changes a stored strategy only when it still
        // carries the retired v5.65.35 label 'Evidence-backed causal
        // rectification'. Any current label is kept exactly as stored.
        setProjectStrategy(project.projectStrategy
            ? normaliseDiagnosisAnchoredStrategyLabel(healLegacyCamelCasedBlob(project.projectStrategy))
            : null);
        setProjectWMS(restoredWms);
        setProjectCostingSheet(project.costingSheet ? normalizeToSnake(project.costingSheet) : null);
        // M2 polish: restore attribution trail + retrieval mode. Legacy projects
        // have neither - default to empty array / 'none' so the UI doesn't try
        // to render undefined.
        // NOTE: appliedLearnings entries are canonically camelCase
        // (`appliedAt`/`appliedBy`/`contradictReason` per the spec in
        // utils/applied-learnings.js); do NOT pipe through normalizeToSnake.
        setAppliedLearnings(Array.isArray(project.appliedLearnings) ? project.appliedLearnings : []);
        setLearningsRetrievalMode(project.learningsRetrievalMode || 'none');
        // Restore the current project's GST display mode. New projects default
        // to inc GST; Stage 3/WMS owns new scope pricing.
        setScopeGstInclusive(project.scopeGstInclusive ?? true);
        // Margin migration: projects saved BEFORE defaultMarginPercent existed
        // are treated as 0% (their stored amounts already reflect the user's
        // intended price). New projects and projects that already carry the
        // field use whatever was persisted.
        const savedDefaultMargin = project.defaultMarginPercent ?? project.default_margin_percent;
        const parsedDefaultMargin = savedDefaultMargin == null || savedDefaultMargin === ''
            ? null
            : Number(savedDefaultMargin);
        if (Number.isFinite(parsedDefaultMargin)) {
            setProjectDefaultMargin(parsedDefaultMargin);
        } else {
            setProjectDefaultMargin(0);
        }
        // A markup the operator chose earlier belongs to the project it was
        // chosen for; the loaded record now carries the project's markup.
        if (operatorMarginRef) operatorMarginRef.current = null;
        setQuoteNote(project.quoteNote || '');
        // aiGeneratedItems / originalAiScope hold scope-item snapshots which
        // carry code-authored camelCase fields - do NOT normalise.
        setAiGeneratedItems(project.aiGeneratedItems || {});
        setOriginalAiScope(project.originalAiScope || null);

        // Restore Stage 5: Final Quote - quoteData is a code-authored
        // document with camelCase internals (header.heroImage,
        // meta.quoteNumber, customSignature, isGstInclusive, …). Do NOT
        // normalise.
        let restoredQuoteData = project.quoteData ?? project.quote_data;
        if (restoredQuoteData?.scope) {
            restoredQuoteData = {
                ...restoredQuoteData,
                scope: hydrateScopeImageRefs(restoredQuoteData.scope),
            };
        }
        // Migration 070 / v5.20: hydrate the QM-issued Quote Number from the
        // top-level `qm_quote_no` column into `quoteData.meta.qmQuoteNo` so
        // FullQuoteEditorModal can render it in the quote header. The column
        // is camelCased by apiFetch (snake_case → camelCase top-level
        // translation) so we read both forms for safety. The number is
        // intentionally stored as a column rather than inside the JSONB blob
        // so the (tenant_id, qm_quote_no) partial unique index can enforce
        // per-tenant uniqueness at the DB layer.
        const qmQuoteNoFromColumn = project.qmQuoteNo ?? project.qm_quote_no ?? null;
        if (qmQuoteNoFromColumn) {
            restoredQuoteData = {
                ...(restoredQuoteData || {}),
                meta: {
                    ...((restoredQuoteData || {}).meta || {}),
                    qmQuoteNo: qmQuoteNoFromColumn,
                },
            };
        }
        // Migration 120 (Jobs): the revision and an imported builder's own
        // number are server-owned columns. Mirror them into meta the same way,
        // so the header, the PDF file name and the email subject print
        // "RBB260605 Rev 1" (or the builder's number) from what the server
        // holds, never from a stale copy inside the blob.
        // A column is mirrored only when the row carries it: an offline row
        // without these fields must not have its revision or number erased.
        const hasQuoteRevColumn = project && ('quoteRev' in project || 'quote_rev' in project);
        const hasSourceRefColumn = project && ('sourceQuoteRef' in project || 'source_quote_ref' in project);
        const quoteRevFromColumn = Number(project.quoteRev ?? project.quote_rev ?? 0);
        const sourceQuoteRefFromColumn = project.sourceQuoteRef ?? project.source_quote_ref ?? null;
        // Only a real difference rewrites meta, so a quote that never had a
        // revision or an imported number loads byte-for-byte as stored.
        const storedMeta = restoredQuoteData?.meta || {};
        const columnRev = Number.isInteger(quoteRevFromColumn) && quoteRevFromColumn > 0 ? quoteRevFromColumn : 0;
        const revDiffers = hasQuoteRevColumn && (columnRev > 0 || storedMeta.quoteRev !== undefined)
            && storedMeta.quoteRev !== columnRev;
        const refDiffers = hasSourceRefColumn && (sourceQuoteRefFromColumn
            ? storedMeta.sourceQuoteRef !== sourceQuoteRefFromColumn
            : storedMeta.sourceQuoteRef !== undefined);
        if (restoredQuoteData && typeof restoredQuoteData === 'object' && (revDiffers || refDiffers)) {
            const meta = { ...storedMeta };
            if (revDiffers) meta.quoteRev = columnRev;
            if (refDiffers) {
                if (sourceQuoteRefFromColumn) meta.sourceQuoteRef = sourceQuoteRefFromColumn;
                else delete meta.sourceQuoteRef;
            }
            restoredQuoteData = { ...restoredQuoteData, meta };
        }
        quoteDataRef.current = restoredQuoteData || null;
        setQuoteData(restoredQuoteData || null);

        // Restore navigation state. Keep persisted completions, then infer any
        // stages that are plainly complete from the restored artifacts so a
        // project remains truthful even if the user did not arrive via NEXT.
        const restoredCompletedSeed = normalizeCompletedStages(project.completedStages ?? project.completed_stages);
        // Soft-migration for pre-WMS-first projects. Before the 5-stage refactor
        // the flow was: 1 Data Entry, 2 Risk, 3 Scope Builder, 4 Final Quote.
        // Now it's: 1 Data Entry, 2 Risk, 3 WMS Builder, 4 Scope Builder,
        // 5 Final Quote. A legacy project that was saved mid-Scope (stage 3)
        // or mid-Quote (stage 4) would otherwise open on the new WMS Builder
        // / Scope Builder \u2014 confusing. Detect legacy by "has scope but no
        // WMS" and bump stored stage by +1 so the user lands where they left
        // off. This is an in-memory display shim; the bumped value persists
        // naturally on next save.
        const rawStoredStage = project.currentStage ?? project.current_stage ?? 1;
        const parsedStoredStage = Number(rawStoredStage);
        const storedStage = Number.isInteger(parsedStoredStage) && parsedStoredStage >= 1
            ? parsedStoredStage
            : 1;
        // Only bump pre-refactor projects (no `pipelineVersion` marker). Any
        // project saved after the WMS-first refactor carries the marker, so
        // `currentStage` already uses the new 5-stage numbering and we trust
        // it as-is. Detection keys on absence of the marker rather than on
        // WMS presence, because new projects can legitimately have no WMS
        // yet (between risk-analysis completion and auto-fire landing).
        // Legacy detection: a project is genuinely "pre-WMS-first" ONLY if
        // none of the post-refactor artifacts exist. Previously this keyed
        // on just `!project.pipelineVersion`, which produced FALSE POSITIVES
        // for projects saved before the backend persisted pipelineVersion
        // (pre-migration-018) or for round-trips that dropped the field.
        // Peter reported Stage 3 flagging fresh projects as legacy - same
        // cause. 2026-04-20 rigorous-audit fix.
        const hasPostRefactorArtifact = !!(
            project.pipelineVersion
            || project.wms
            || project.projectStrategy
            || project.projectSummary
        );
        // v5.50.6: an imported historical record is an ARCHIVE, not a stranded
        // legacy job. The legacy shim below walks old live projects into the
        // 5-stage flow; applied to the materialized corpus rows (which carry
        // no pipelineVersion/wms/strategy/summary BY CONSTRUCTION) it demoted
        // every one from stage 5 to the Scope Builder — closing the quote
        // stranded the operator on Stage 4 of an archive with a legacy
        // "generate a WMS" banner, and the autosave wrote current_stage=4
        // back over the record. An archive with a produced quote always
        // loads AS its quote (stage 5 + view-only peek), which also keeps
        // the 4 odd-stage history rows out of the mid-pipeline AI triggers.
        const isHistoryArchive = (project.projectType || project.project_type) === 'history';
        const isLegacy = !isHistoryArchive && !hasPostRefactorArtifact
            && Array.isArray(project.scope) && project.scope.length > 0;

        // The browser holds the loaded record's pipelineVersion, so every
        // old writer can tell a Quote Brain project (src/utils/quote-brain-project.ts).
        noteLoadedProjectPipeline(project.id, project.pipelineVersion ?? project.pipeline_version ?? null);

        // Self-heal: if the project is clearly post-refactor (has any
        // downstream artifact) but is missing the pipelineVersion marker,
        // stamp it on disk so future loads don't misclassify. Fire-and-forget
        // - the in-memory `project.pipelineVersion` also gets set so the
        // isLegacy check above already read the fixed value on this load.
        if (hasPostRefactorArtifact && !project.pipelineVersion) {
            project.pipelineVersion = 'wms-first-v1';
            // Gated on the version this record was loaded at, and adopted when
            // it lands. The base token comes from the RECORD, not from
            // baseUpdatedAtRef: this line runs synchronously inside
            // completeProjectLoad, BEFORE the ref is stamped further down, so
            // the ref still holds the PREVIOUS project's version right here. By
            // the time the write resolves the ref holds this record's version,
            // which is what persistLoadRepair compares against before adopting.
            void persistLoadRepair(
                project.id,
                { ...project, pipelineVersion: 'wms-first-v1' },
                projectVersionToken(project),
                'pipelineVersion back-stamp',
            );
        }

        // Cap bump at Stage 4 \u2014 a user who left a legacy project on the old
        // Quote (stored stage 4) lands on the new Scope Builder (stage 4) so
        // they see their scope rather than an empty page 5. One click forward
        // reopens the quote editor.
        let effectiveStage = isLegacy && storedStage >= 3 ? Math.min(storedStage + 1, 4) : storedStage;
        // History archives: quote data present ⇒ the completed-quote view is
        // the load target, even where `status: 'archived'` un-passes the
        // status-sensitive completion gate (projectHasCompletedQuote) that the
        // import itself used before forcing the archived status.
        const loadedQuoteComplete = projectHasCompletedQuote(project)
            || (isHistoryArchive && !!project.quoteData);
        const loadedStage5Presentation = projectHasStage5Presentation(project);
        if (isHistoryArchive && project.quoteData) {
            effectiveStage = 5;
        }
        if (loadedStage5Presentation) {
            effectiveStage = 5;
        }
        if (Number(effectiveStage) === 5 && !loadedQuoteComplete && !loadedStage5Presentation) {
            effectiveStage = Array.isArray(project.scope) && project.scope.length > 0 ? 4 : 1;
        }
        // A job saved at the Risk report or the Work Method, stages the old pipeline had and the app no longer shows
        // (docs/old-pipeline-removal/PLAN.md), opens at Scope when it has items and at Stage 1 when it has none.
        if (RETIRED_STAGE_IDS.includes(Number(effectiveStage))) {
            effectiveStage = Array.isArray(project.scope) && project.scope.length > 0 ? 4 : 1;
        }
        // Opening a project is not, on its own, a request to RUN it. (A Full AI
        // run parked at Stage 3 used to resume here; the Work Method went with
        // the old pipeline, docs/old-pipeline-removal/PLAN.md.)
        if (openedForReviewRef) {
            openedForReviewRef.current = true;
        }
        // Clear the PREVIOUS project's editor state before deciding whether to
        // open this one. This call used to live ~140 lines below, in the
        // transient-reset block, where it unconditionally undid the peek() below
        // in the same synchronous function — see the note at its old site.
        // Ordering contract: editor.reset() must precede the peek(), never
        // follow it.
        editor.reset();
        const shouldOpenQuoteAfterLoad = !!pendingOpenQuoteRef.current
            || (Number(effectiveStage) === 5 && (loadedQuoteComplete || loadedStage5Presentation));
        if (shouldOpenQuoteAfterLoad) {
            if (loadedQuoteComplete || loadedStage5Presentation) {
                // v5.39.1 (Peter 2026-05-21): "Stage 5 loads blank when navigating
                // from project history; only a Stage 4 → Next nudge renders the
                // content." Root cause: handleNavigate('quotemaker') (called at
                // the end of this function) defers setCurrentPage via a 50ms
                // setTimeout, so the auto-open watcher useEffect (which gates
                // on currentPage === 'quotemaker') sees the stale page on its
                // first run and early-returns. By the time setCurrentPage fires
                // and the watcher re-runs, transient state races have eaten the
                // intent (observed intermittently, more often under load).
                //
                // Fix: open the editor SYNCHRONOUSLY here, in the same React
                // batch as setCurrentProjectId / setQuoteData / setStage etc.
                // The FullQuoteEditorModal is gated by `currentPage ===
                // 'quotemaker' && quoteEditorOpen`, so it stays unmounted until
                // setCurrentPage flips ~50ms later — at which point ALL the
                // state it needs (quoteData, scope, formData) is already
                // present. The watcher ref is still set as a safety net in
                // case some path skips this branch.
                autoOpenLoadedQuoteProjectRef.current = project.id;
                editor.peek();
            } else {
                setToast({ message: 'No completed quote to view yet - generate one first.', type: 'info' });
            }
            pendingOpenQuoteRef.current = false;
        }
        setCompletedStages(inferCompletedStages({
            ...project,
            completedStages: restoredCompletedSeed,
            currentStage: effectiveStage,
            formData: project.formData || {},
            photos: loadedPhotos,
            documents: project.documents || [],
            videos: project.videos || [],
            projectSummary: project.projectSummary,
            riskAnalysis: project.riskAnalysis,
            photoAnalysis: project.photoAnalysis,
            wms: restoredWms,
            projectStrategy: project.projectStrategy,
            scope: restoredScope,
            quoteData: restoredQuoteData,
        }));
        setStage(effectiveStage);
        // Phase 8: surface a banner on Stage 3 inviting legacy projects to
        // generate a WMS and switch to method-first pricing. Dismissible
        // per-project via `legacyBannerDismissed` on the envelope.
        setLegacyBannerDismissed(project.legacyBannerDismissed === true);
        setImportPhase('form');
        // 2026-05-17 (Peter request): rehydrate the Stage 1 Sorting Floor for
        // projects opened from Project History. procState only exists in-session
        // (populated by INIT_FILES during a fresh import); without this dispatch
        // a re-opened project showed an empty intake area because totalFiles=0
        // gated the Sorting Floor render in Stage1DataEntry.jsx (~line 6261).
        // The reducer builds synthetic file entries (all DONE) from the persisted
        // documents/photos/videos arrays. Extracted facts are NOT persisted today
        // so the facts strip will be empty on re-open; the file lanes are the
        // primary signal.
        //
        // ORDERING CONTRACT (2026-07-16): CLEAR must precede REHYDRATE_FROM_LOADED.
        // CLEAR wipes the PREVIOUS project's in-session procState (it long predates
        // this feature — it lived in the transient-reset block below, AFTER the
        // rehydrate, which wiped the rehydrated lanes in the same load and left the
        // Sorting Floor empty on every re-open; the feature never worked until the
        // dispatch order was fixed). Do NOT add a later CLEAR between here and the
        // end of this load.
        procDispatch({ type: PROC_ACTIONS.CLEAR });
        procDispatch({
            type: PROC_ACTIONS.REHYDRATE_FROM_LOADED,
            payload: {
                documents: project.documents || [],
                photos: loadedPhotos || [],
                videos: project.videos || [],
                // A clip its transcript rows hold is transcribed, whatever its video row says.
                videoTranscripts: project.videoTranscripts || [],
            },
        });
        // Preserve the stored title on load. Sync the title-source snapshot
        // FIRST so the title effect treats the load-driven formData swap as
        // already-titled and does NOT regenerate the quote code — the (MMYY)
        // suffix stamps the CURRENT month, so regenerating on open meant any
        // later save rewrote e.g. "QUOTE RBB/BSS (0526)" → "(0726)" in
        // projects.title (observed live 2026-07-03). Only a project with NO
        // stored title gets a fresh code, generated from the LOADED formData
        // — not this closure's formData, which still holds the previous
        // project's values at this point.
        const loadedTitleSource = project.formData || {};
        titleSourceRef.current = titleSourceKey(loadedTitleSource);
        const needsImportedTitle = isImportedFolderTitle(project.title, project)
            && String(loadedTitleSource.address || '').trim();
        // Repair a project saved with the strata manager's name as its title
        // (isContactNameQuoteTitle). A title the operator typed is theirs. The
        // code is dated from the quote, not today: a fresh month on open is the
        // drift described above.
        // A project saved with its job description as the title while it had no
        // address is repaired the same way once it has one (isJobDescriptionQuoteTitle).
        const needsContactNameRepair = !needsImportedTitle
            && !project.titleEditedByUser
            && (isContactNameQuoteTitle(project.title, loadedTitleSource)
                || (!!String(loadedTitleSource.address || '').trim()
                    && isJobDescriptionQuoteTitle(project.title, loadedTitleSource)));
        setQuoteTitle(needsImportedTitle
            ? generateQuoteTitle(loadedTitleSource)
            : needsContactNameRepair
                ? generateQuoteTitle({
                    ...loadedTitleSource,
                    quoteDate: loadedTitleSource.quoteDate
                        || project.quoteData?.meta?.date
                        || project.createdAt
                        || project.created_at,
                })
                : project.title || generateQuoteTitle(loadedTitleSource));
        setTitleEditedByUser(!!project.titleEditedByUser);
        setHasViewedRationale(false);

        // Restore file system info (handles need reconnection)
        if (project.projectFolder) {
            setQuoteSubfolder(project.projectFolder.subfolderName
                ? {
                    name: project.projectFolder.subfolderName,
                    folderName: project.projectFolder.folderName || project.projectFolder.subfolderName,
                    relativePath: project.projectFolder.relativePath ?? project.projectFolder.path ?? project.projectFolder.subfolderName,
                    path: project.projectFolder.path ?? project.projectFolder.relativePath ?? project.projectFolder.subfolderName,
                    sourcePath: project.projectFolder.sourcePath ?? project.projectFolder.relativePath ?? project.projectFolder.subfolderName,
                    handle: null,
                }
                : null);
            setFolderFiles(project.projectFolder.files || { photos: [], documents: [], outputs: [] });
        } else {
            setQuoteSubfolder(null);
            setFolderFiles({ photos: [], documents: [], outputs: [] });
        }

        // ─── Reset remaining transient state that isn't persisted in the project record ──
        // These would otherwise leak from the previous project.
        setPreprocessedFiles([]);
        setPostProcessContext(null);
        setIsStage1Processing(false);
        setCostingRegenerating(false);
        setIsAnalyzing(false);
        // procState is NOT cleared here: CLEAR already ran immediately before the
        // REHYDRATE_FROM_LOADED dispatch above. A CLEAR at this point would return
        // the reducer to initialState and wipe the just-rehydrated Sorting Floor
        // lanes (the P3 latent bug — the 2026-05-17 feature never worked while the
        // CLEAR sat in this block).
        setExtractedFacts([]);
        setOcrScanning(false);
        setOcrResults(null);
        setOcrApplying(false);
        // editor.reset() USED TO SIT HERE and it sat AFTER the
        // shouldOpenQuoteAfterLoad block above, so it unconditionally undid that
        // block's editor.peek() — in the same synchronous function, so the peek
        // never survived to a commit. The v5.39.1 fix that deliberately added
        // that synchronous open ("open the editor SYNCHRONOUSLY here, in the
        // same React batch") has therefore been dead since it landed, leaving
        // the auto-open watcher in App.jsx as the ONLY thing that opened the
        // modal — and that watcher is a race, which is why opening a quoted job
        // sometimes landed on a blank Stage 5 (stage 5 with no modal, the
        // documented bad pair). The reset now runs BEFORE the open decision, so
        // it still clears the PREVIOUS project's editor state and the new
        // project's peek survives. Moved v5.47.37; see the block above.
        setRibbonNotification(null);
        setRibbonDismissing(false);
        setParentFolder(null);             // X01-005: prevent file writes to wrong project folder
        prevFilesProcessedRef.current = false;  // X01-006: reset auto-summary one-shot guard
        // NOTE: Do NOT reset scopeGstInclusive here - it was already restored
        // from the project above. Keep the user's current display preference.

        // Edits restored from this device are unsaved by definition: the
        // autosave sends them through the normal save token rules.
        setHasUnsavedChanges(!!restoredUnsavedEdits);
        // Behavioural round 2 (P4 fix): a record without updated_at (or with
        // an unparseable one) used to paint an Invalid Date here — which is
        // truthy, so the nav "SAVED" chip showed for a project with no known
        // save time. null is the state's documented empty (App seeds
        // useState(null); resetForm paints null; NavActions gates on truthiness).
        // Camel FIRST, per the apiFetch HARD RULE — and here it is not a style
        // preference, it is the whole behaviour. A server-backed open arrives
        // via projects.getById, whose `_withProjectLifecycleAliases` returns
        // `{ ...project }`; an object spread copies only ENUMERABLE own
        // properties, and apiFetch exposes the original snake_case key as a
        // NON-ENUMERABLE alias. So `project.updated_at` was undefined on every
        // healthy project open (verified in node: the alias survives a direct
        // read and vanishes through the spread).
        //
        // Two things were silently wrong because of it. The optimistic-
        // concurrency token was left NULL after a load, so the cross-device
        // guard only armed itself after the first successful save — which is
        // precisely the window the 2026-08-18 outage lived in. And
        // setLastSavedAt below painted null, so the nav SAVED chip was blank on
        // a project that had been saved for months.
        const loadedUpdatedAtRaw = project.updatedAt || project.updated_at || null;
        const loadedUpdatedAt = loadedUpdatedAtRaw ? new Date(loadedUpdatedAtRaw) : null;
        const loadedUpdatedAtIsValid = !!loadedUpdatedAt && !Number.isNaN(loadedUpdatedAt.getTime());
        setLastSavedAt(loadedUpdatedAtIsValid ? loadedUpdatedAt : null);
        // Stamp the optimistic-concurrency token from the record we just
        // painted. This is the ONLY place a load establishes it, so the token
        // always describes the version actually on screen — including a record
        // served from the offline Dexie fallback, whose whole danger is that it
        // looks identical to fresh server truth. A record with no readable
        // updated_at yields null (do not enforce) rather than a guessed value.
        baseUpdatedAtRef.current = loadedUpdatedAtIsValid
            ? (loadedUpdatedAt as Date).toISOString()
            : null;
        // Same record, same moment — see rememberServerProjectBaseline.
        rememberServerProjectBaseline(project?.id, project);
        if (restoredUnsavedEdits && project?.id) {
            // The painted record carries the operator's kept edits, so it is
            // not what the server holds. The stale-write self-heal compares the
            // server's content with the content these edits were based on: the
            // row as loaded when it has not moved, or the kept signature when
            // it has. With no kept signature the comparison always fails, so a
            // refusal goes to the operator, never to a silent overwrite.
            if (restoredUnsavedEdits.serverMoved) {
                if (staleWriteBaselineRef) {
                    staleWriteBaselineRef.current = restoredUnsavedEdits.baselineSignature
                        ? { projectId: String(project.id), signature: restoredUnsavedEdits.baselineSignature }
                        : null;
                }
            } else {
                rememberServerProjectBaseline(project.id, restoredUnsavedEdits.serverRow);
            }
            noteRestoredUnsavedEdits(project.id);
            const keptAt = new Date(restoredUnsavedEdits.keptAt);
            const keptAtText = Number.isNaN(keptAt.getTime())
                ? 'earlier'
                : keptAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
            setToast({
                type: 'info',
                message: restoredUnsavedEdits.serverMoved
                    ? `Your unsaved changes from ${keptAtText} were kept on this device and are back on screen. This project was also changed since, so saving will ask before replacing that version.`
                    : `Your unsaved changes from ${keptAtText} were kept on this device and are back on screen. They are being saved now.`,
            });
        }

        // Persist only the migrated receipt; never delete the historical photo
        // rows, assignments or hero selection. The existing optimistic token,
        // project-id and generation guards make this a bounded load repair.
        // Records without a trustworthy version are healed in memory only.
        if (healedDocumentPhotoCoverage && project?.id && photoCoverageRepairBaseToken) {
            const repairProjectId = String(project.id);
            const repairGeneration = projectLoadGenerationRef.current;
            const repairToken = photoCoverageRepairBaseToken;
            afterNextPaintOrTimeout(() => {
                if (currentProjectIdRef.current !== repairProjectId) return;
                if (projectLoadGenerationRef.current !== repairGeneration) return;
                if (baseUpdatedAtRef.current !== repairToken) return;
                void persistLoadRepair(
                    repairProjectId,
                    { id: repairProjectId, photoAnalysis: healedDocumentPhotoCoverage },
                    repairToken,
                    'document-derived photo coverage migration',
                );
            });
        }

        // ── Freshness probe (2026-08-19, the tab-switch 409 loop) ──────────
        // A paint is allowed to come from a LOCAL copy (Dexie fallback, an
        // HMR/session restore, a caller-supplied record). When the server row
        // is NEWER — typically the preparation queue finished this project
        // after the copy was taken — every save from this tab is then
        // correctly refused (409 PROJECT_STALE_WRITE) until someone reloads.
        // The operator experienced that as an unfixable error loop. So: after
        // painting, ask the server for the row; if it is ahead of what we
        // painted, repaint the newer version — the local mirror is a cache,
        // never truth. Guards make the repaint strictly a no-op otherwise:
        // same project still open, no newer load claimed, token untouched
        // (a completed save in the window moves it, and the repaint yields).
        // Residual: an operator keystroke inside the ~200ms probe window is
        // repainted over; the window is a single round trip at load time,
        // before the page is realistically interactable. Offline or probe
        // failure keeps the local paint (fail-open, same as the Dexie
        // fallback itself). The repaint re-enters completeProjectLoad, whose
        // own probe then finds token == server and stops — no loop.
        {
            const paintedProjectId = project?.id ? String(project.id) : null;
            const paintedGeneration = projectLoadGenerationRef.current;
            const paintedToken = baseUpdatedAtRef.current;
            // Not for restored edits: a newer server row would be repainted
            // straight over the operator's kept changes. Their save decides.
            if (paintedProjectId && !restoredUnsavedEdits) {
                void (async () => {
                    try {
                        const server = await getProject(paintedProjectId) as LooseRecord | null;
                        const serverRaw = server?.updatedAt || server?.updated_at || null;
                        if (!serverRaw || typeof serverRaw !== 'string') return;
                        const serverAt = new Date(serverRaw);
                        if (Number.isNaN(serverAt.getTime())) return;
                        const paintedAt = paintedToken ? new Date(paintedToken) : null;
                        const serverIsAhead = !paintedAt || serverAt.getTime() > paintedAt.getTime();
                        if (!serverIsAhead) return;
                        if (currentProjectIdRef.current !== paintedProjectId) return;
                        if (projectLoadGenerationRef.current !== paintedGeneration) return;
                        if (baseUpdatedAtRef.current !== paintedToken) return;
                        console.info(`[QM] The loaded copy of ${paintedProjectId} is older than the saved version (${paintedToken || 'no token'} < ${serverAt.toISOString()}) — showing the newer saved version.`);
                        completeProjectLoad(server as LooseRecord);
                    } catch { /* offline or transient — keep the local paint */ }
                })();
            }
        }
        handleNavigate('quotemaker');

        // Clear the loading guard after React has committed all state.
        // The paint callback ensures we're past the next paint, so the
        // auto-analysis useEffect won't see partially-hydrated state. The
        // timer fallback matters for Quote Run: requestAnimationFrame never
        // fires in a hidden tab, and a guard that never clears wedges every
        // later stage of a run the operator left in the background.
        afterNextPaintOrTimeout(() => {
            isLoadingProjectRef.current = false;
        });
    };

    // ─── A new project kept on this device (review of the outage lane) ─────
    // A project that was never saved has no id, so a save the server could not
    // take kept its edits in the new-project slot. The QuoteMaker page calls
    // this when it opens on a blank new project (a refresh during the outage
    // comes back that way): the edits are painted back, still as a new
    // project, and marked unsaved so the autosave creates the project when the
    // server answers. Its first successful save clears the copy.
    //
    // Review 7, item 2 (2026-10-04): a recovered quote opened from Quick edit
    // is not a project load (no generation, no id), so it is checked
    // separately once the read lands: whichever of the two runs first, the
    // kept project never paints over it.
    const recoveredQuoteOnScreen = () => paintedProjectLoadContextRef.current?.recoveredQuote === true;
    const restoreKeptNewProject = async (): Promise<boolean> => {
        if (batchActiveRef.current) return false;
        if (currentProjectIdRef.current) return false;
        const generation = projectLoadGenerationRef.current;
        const kept = await readUnsavedProjectEdits(NEW_PROJECT_SLOT);
        if (!kept) return false;
        // The operator opened a project, a recovered quote, or started another
        // one while this was being read: that screen is theirs now.
        if (generation !== projectLoadGenerationRef.current || currentProjectIdRef.current || recoveredQuoteOnScreen()) return false;
        const envelope = (kept.envelope || {}) as LooseRecord;
        const listOf = (value: unknown) => (Array.isArray(value) ? value : []);
        // The same test as isProjectWorthSaving, on the kept copy. An empty
        // copy (older builds kept a blank import owner) is dropped, not
        // painted back with a notice about nothing.
        const keptForm = (envelope.formData && typeof envelope.formData === 'object' ? envelope.formData : {}) as LooseRecord;
        const hasText = (value: unknown) => typeof value === 'string' && value.trim().length > 0;
        const worthRestoring = ['address', 'notes', 'clientName', 'clientCompany', 'jobDescription'].some(key => hasText(keptForm[key]))
            || ['photos', 'documents', 'videos', 'scope'].some(key => listOf(envelope[key]).length > 0)
            || !!(envelope.quoteData || envelope.riskAnalysis || envelope.projectSummary);
        if (!worthRestoring) {
            await clearUnsavedProjectEdits(NEW_PROJECT_SLOT);
            return false;
        }
        // The paint is hydration, not an edit: the dirty tracker adopts it as
        // its baseline, and the flag below is what marks it unsaved.
        suppressUnsavedRef.current = true;
        projectLoadGuardUntilRef.current = Date.now() + 2000;
        if (envelope.formData && typeof envelope.formData === 'object') setFormData(envelope.formData);
        setPhotos(listOf(envelope.photos));
        setDocuments(listOf(envelope.documents));
        setVideos(listOf(envelope.videos));
        setVideoTranscripts(listOf(envelope.videoTranscripts));
        setSelectedHeroImage(envelope.selectedHeroImage ?? null);
        setScope(listOf(envelope.scope));
        if (typeof envelope.scopeGstInclusive === 'boolean') setScopeGstInclusive(envelope.scopeGstInclusive);
        setQuoteNote(typeof envelope.quoteNote === 'string' ? envelope.quoteNote : '');
        setQuoteData(envelope.quoteData ?? null);
        setAnalysis(envelope.riskAnalysis ?? null);
        setPhotoAnalysis(envelope.photoAnalysis ?? null);
        setProjectSummary(envelope.projectSummary ?? null);
        setProjectWMS(envelope.wms ?? null);
        setProjectStrategy(envelope.projectStrategy ?? null);
        setOriginalAiScope(envelope.originalAiScope ?? null);
        if (envelope.aiGeneratedItems && typeof envelope.aiGeneratedItems === 'object') setAiGeneratedItems(envelope.aiGeneratedItems);
        if (Array.isArray(envelope.appliedLearnings)) setAppliedLearnings(envelope.appliedLearnings);
        if (typeof envelope.learningsRetrievalMode === 'string') setLearningsRetrievalMode(envelope.learningsRetrievalMode);
        if (typeof envelope.defaultMarginPercent === 'number') setProjectDefaultMargin(envelope.defaultMarginPercent);
        const keptTitle = typeof envelope.quoteTitle === 'string' && envelope.quoteTitle.trim()
            ? envelope.quoteTitle
            : (typeof envelope.title === 'string' ? envelope.title : '');
        if (keptTitle) setQuoteTitle(keptTitle);
        setTitleEditedByUser(envelope.titleEditedByUser === true);
        setProjectWorkflowMode(normalizeProjectWorkflowMode(envelope.workflowMode));
        setCompletedStages(new Set(normalizeCompletedStages(envelope.completedStages)));
        // Never Stage 5: without the quote editor open that is the blank
        // Stage 5 pair. A project that reached Stage 5 was saved long before.
        const keptStage = Number(envelope.currentStage);
        setStage(Number.isInteger(keptStage) && keptStage >= 1 && !RETIRED_STAGE_IDS.includes(keptStage) ? Math.min(keptStage, 4) : 1);
        setImportPhase('form');
        setHasUnsavedChanges(true);
        noteRestoredUnsavedEdits(NEW_PROJECT_SLOT);
        const keptAt = new Date(kept.keptAt);
        const keptAtText = Number.isNaN(keptAt.getTime())
            ? 'earlier'
            : keptAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        setToast({
            type: 'info',
            message: `Your new quote from ${keptAtText} was kept on this device and is back on screen. It is being saved now.`,
        });
        console.info(`[QM] Restored a new project kept on this device at ${kept.keptAt}.`);
        return true;
    };

    // App calls this just before editor.openRecovered puts a recovered quote
    // on the new-project screen. It names the screen without claiming a load
    // generation (nothing in flight is abandoned and no save is made stale),
    // and lasts until the next reset or paint replaces the painted load.
    const markRecoveredQuoteOnScreen = () => {
        paintedProjectLoadContextRef.current = {
            projectId: null,
            loadGeneration: projectLoadGenerationRef.current,
            recoveredQuote: true,
        };
    };

    return {
        resetForm,
        saveCurrentProjectImpl,
        loadProject,
        loadProjectUnchecked,
        completeProjectLoad,
        refuseProjectChangeDuringQuoteRun,
        showQuoteRunJobInsteadOfOpening,
        quoteRunJobInProgress,
        restoreKeptNewProject,
        markRecoveredQuoteOnScreen,
        discardKeptNewProject,
    };
}
